// Global
import React, { useRef, useState, useEffect } from "react";
import { NavLink as Link } from 'react-router-dom';
import { animateScroll as scroll } from 'react-scroll';
import { useSelector, useDispatch } from 'react-redux';
import { useCookies } from 'react-cookie';
import Loadable from 'react-loadable';
import moment from 'moment';

// Params and data
import { API, UPLOADS, SETTINGS_APP } from "../../params";
import { upload_config_one_image, upload_config_one_png_image, upload_config_multi_image, upload_config_multi_pdf } from "../../API/upload";
import users from '../header/users';

// Editor
import { EditorState, convertToRaw, ContentState } from 'draft-js';
import draftToHtml from 'draftjs-to-html';

// Components
import { Skeleton, Tabs, Form, Input, Select, List, Modal, Empty, Badge, Tooltip, Grid, Popconfirm, Space, Typography, Tag, Progress, Image, Upload, Button, message, InputNumber, Rate } from 'antd';
import { YMaps, Map, Placemark } from 'react-yandex-maps';
import ReactCodeInput from "react-code-input";
import ImgCrop from 'antd-img-crop';

// Mask
import MaskedInput, {conformToMask} from 'react-text-mask';
import InputMask from 'react-input-mask';
import emailMask from 'text-mask-addons/dist/emailMask';

// Icons
import {
    SearchOutlined,
    PlusOutlined,
    StarFilled
} from '@ant-design/icons';
import { VscFilePdf } from "react-icons/vsc";
import { FiEdit, FiTrash2 } from "react-icons/fi";

// Redux
import { updPanel } from '../../redux/app_global/reducer';
import { setToken, setAuth, deleteData } from '../../redux/profile/reducer';
import { setOrganization_id } from '../../redux/organization/reducer';
import { setSpecialist_id } from '../../redux/specialist/reducer';
import { setReviews_id } from '../../redux/reviews/reducer';
import { setLocations_search } from "../../redux/locations/reducer";
import { setPosts_id, setCategory_id } from "../../redux/posts/reducer";
import { setSurveys_id, setQuestions_id } from "../../redux/surveys/reducer";
import { setAdvertisement_id } from "../../redux/advertisement/reducer";

// API
import { searchLocations } from "../../API/locations";
import { 
    getProfile, getUsers,
    updProfile, updProfilePhone, updProfileEmail, updProfilePassword, updUser,
    confirmProfileEmail, confirmProfileEmailCode, confirmProfilePhone, confirmProfilePhoneCode
} from "../../API/profile";
import { 
    getOrganizationList, getOrganizationMy, getOrganizationId, getOrganizationSpecialistsSpecializationList, getOrganizationCategoriesList,
    addOrganization, addOrganizationPhone, addOrganizationWorktime, addOrganizationWorktime_admin, addOrganizationServices, addOrganizationSpecialists, addOrganizationCategories, addReview,
    updOrganizationData, updOrganizationSoc, updOrganizationContent, updOrganizationSeo, updOrganizationLocation, updOrganizationPhone, updOrganizationWorktime, updOrganizationWorktime_admin, updOrganizationServices, updOrganizationModeration,updOrganizationSpecialists, updOrganizationPrice, updOrganizationCategories,
    delOrganizationPhone, delOrganizationWorktime, delOrganizationWorktime_admin, delOrganizationServices, delOrganizationHeaderPc, delOrganizationHeaderMob, delOrganizationPhotos, delOrganizationCertificates, delOrganizationSpecialists, delOrganizationPrice, delOrganizationLogo
} from "../../API/organization";
import { 
    getSpecialistList, getSpecialistMy, getSpecialistId, getSpecialistSpecializationList, getSpecialistOrganizationsList,
    addSpecialist, addSpecialistEducation, addSpecialistExperience, addSpecialistPhone,
    updSpecialistData, updSpecialistSeo, updSpecialistModeration, updSpecialistDraft, updSpecialistLocation, updSpecialistEducation, updSpecialistExperience, updSpecialistPhone,
    delSpecialistEducation, delSpecialistExperience, delSpecialistPhotos, delSpecialistCertificates, delSpecialistPhoto, delSpecialistPhone
} from "../../API/specialist";
import { 
    getAdvertisementList, getAdvertisementMy, getAdvertisementId,
    addAdvertisement,
    updAdvertisementData, updAdvertisementContent, updAdvertisementModeration, updAdvertisementTempDraft, updAdvertisementPublish, updAdvertisementUp, updAdvertisementLocation
} from "../../API/advertisement";
import { 
    getReviewsList, getReviewsMy, getReviewsId,
    updReviewsData,
    delReviews
} from "../../API/reviews";
import { 
    getPostsList, getPostsId,
    addPosts,
    updPostsData, updPostsContent, updPostsSeo,
    delPostsPhoto, delPostsPhotoBig
} from "../../API/posts";
import { 
    getPostsCategoryList, getPostsCategoryId, getPostsCategoryCategoryList,
    addPostsCategory,
    updPostsCategoryData, updPostsCategoryContent, updPostsCategorySeo,
    delPostsCategoryPhoto, delPostsCategoryPhotoBig
} from "../../API/posts";
import { 
    getSurveysList, getSurveysId, getSurveysQuestionsId, getSurveysQuestionsAnswersId, getSurveysEmailsId, getSurveysCriteriaId,
    addSurveys, addSurveysQuestions, addSurveysQuestionsAnswers, addSurveysEmails, addSurveysCriteria,
    updSurveysData, updSurveysSeo, updSurveysContent, updSurveysQuestions, updSurveysQuestionsAnswers, updSurveysEmails, updSurveysCriteria,
    delSurveysPhoto, delSurveysPhotoBig, delSurveysQuestions, delSurveysQuestionsAnswers, delSurveysEmails, delSurveysCriteria
} from "../../API/surveys";

// Localization of components
moment.locale('ru');

// Loading the library for SSR
let htmlToDraft = null;
if (typeof window === 'object') {
    htmlToDraft = require('html-to-draftjs').default;
}

// Other components
const { useBreakpoint } = Grid;
const { TextArea } = Input;
const { Text} = Typography;
const { Dragger } = Upload;

// Page Component
export const Profile = () => {
    // Hooks
    const screens = useBreakpoint();
    const dispatch = useDispatch();

    // Cookies
    const [cookies, setCookie, removeCookie] = useCookies(['token']);

    // Redux
    const app_global = useSelector(state => state.app_global);
    const locations = useSelector(state => state.locations);
    const profile = useSelector(state => state.profile);
    const organization = useSelector(state => state.organization);
    const specialist = useSelector(state => state.specialist);
    const reviews = useSelector(state => state.reviews);
    const posts = useSelector(state => state.posts);
    const surveys = useSelector(state => state.surveys);
    const advertisement = useSelector(state => state.advertisement);

    // Pages
    const [pageMyReviews, setPageMyReviews] = useState(1);
    const [pageListReviews, setPageListReviews] = useState(1);
    const [pageCategories, setPageCategories] = useState(1);
    const [pageMyOrganizations, setPageMyOrganizations] = useState(1);
    const [pageListOrganizations, setPageListOrganizations] = useState(1);
    const [pageMySpecialists, setPageMySpecialists] = useState(1);
    const [pageListSpecialists, setPageListSpecialists] = useState(1);
    const [pagePostsCategories, setPagePostsCategories] = useState(1);
    const [pagePosts, setPagePosts] = useState(1);
    const [pageSurveys, setPageSurveys] = useState(1);
    const [pageUsers, setPageUsers] = useState(1);
    const [pageMyAdvertisements, setPageMyAdvertisements] = useState(1);
    const [pageListAdvertisements, setPageListAdvertisements] = useState(1);

    // States
    // Global
    const [activeTab, setActiveTab] = useState("profile");

    // Profile
    const [password_complexity, setPassword_complexity] = useState(0);

    // Editor
    const [editorState, setEditorState] = useState(EditorState.createEmpty());
    const editorCoreOrganization = React.useRef(null);
    const editorCorePosts = React.useRef(null);
    const editorCorePostsCategory = React.useRef(null);
    const editorCoreSurveys = React.useRef(null);
    const editorCoreSurveysCriteria = React.useRef(null);
    const editorCoreAdvertisement = React.useRef(null);

    // Reviews
    const [reviews_list_search, setReviews_list_search] = useState("");
    const [reviews_list_status, setReviews_list_status] = useState("");

    // Organization
    const [activeTabOrgEdit, setActiveTabOrgEdit] = useState("data");
    const [organization_my_search, setOrganization_my_search] = useState("");
    const [organization_list_search, setOrganization_list_search] = useState("");
    const [organization_list_status, setOrganization_list_status] = useState("");

    // Organization Content Editor
    const handleInitialize = React.useCallback((instance) => {
        editorCoreOrganization.current = instance
    }, []);

    // Specialist
    const [activeTabSpecEdit, setActiveTabSpecEdit] = useState("data");
    const [specialist_my_search, setSpecialist_my_search] = useState("");
    const [specialist_list_search, setSpecialist_list_search] = useState("");
    const [specialist_list_status, setSpecialist_list_status] = useState("");

    // Advertisement
    const [activeTabAdvertisementEdit, setActiveTabAdvertisementEdit] = useState("data");
    const [advertisement_my_search, setAdvertisement_my_search] = useState("");
    const [advertisement_list_search, setAdvertisement_list_search] = useState("");
    const [advertisement_list_status, setAdvertisement_list_status] = useState("");

    // Advertisement Content Editor
    const handleInitializeAdvertisement = React.useCallback((instance) => {
        editorCoreAdvertisement.current = instance
    }, []);
    
    // Categories
    const [categories_list_search, setCategories_list_search] = useState("");

    // Posts
    const [activeTabPostsEdit, setActiveTabPostsEdit] = useState("data");
    const [posts_list_search, setPosts_list_search] = useState("");
    const [posts_list_status, setPosts_list_status] = useState("");
    const [posts_list_category, setPosts_list_category] = useState("");

    // Post Content Editor
    const handleInitializePosts = React.useCallback((instance) => {
        editorCorePosts.current = instance
    }, []);

    // Posts Category
    const [activeTabPostsCategoryEdit, setActiveTabPostsCategoryEdit] = useState("data");
    const [posts_category_list_search, setPosts_category_list_search] = useState("");
    const [posts_category_list_status, setPosts_category_list_status] = useState("");
    const [posts_category_list_category, setPosts_category_list_category] = useState("");

    // Post Category Content Editor
    const handleInitializePostsCategory = React.useCallback((instance) => {
        editorCorePostsCategory.current = instance
    }, []);

    // Users
    const [users_list_search, setUsers_list_search] = useState("");

    // Surveys
    const [activeTabSurveysEdit, setActiveTabSurveysEdit] = useState("data");
    const [surveys_list_search, setSurveys_list_search] = useState("");
    const [surveys_list_status, setSurveys_list_status] = useState("");
    const [surveys_criteria_min_count, setSurveys_criteria_min_count] = useState(1);

    // Surveys Content Editor
    const handleInitializeSurveys = React.useCallback((instance) => {
        editorCoreSurveys.current = instance
    }, []);

    // Surveys Criteria Content Editor
    const handleInitializeSurveysCriteria = React.useCallback((instance) => {
        editorCoreSurveysCriteria.current = instance
    }, []);

    // Forms
    // Profile
    const [form_profile] = Form.useForm();
    const [form_phone] = Form.useForm();
    const [form_email] = Form.useForm();
    const [form_password] = Form.useForm();

    // Reviews
    const [form_reviews_data] = Form.useForm();
    const [form_reviews_list_search] = Form.useForm();
    const [form_review_add] = Form.useForm();

    // Organization
    const [form_organization_add] = Form.useForm();
    const [form_organization_data] = Form.useForm();
    const [form_organization_soc] = Form.useForm();
    const [form_organization_content] = Form.useForm();
    const [form_organization_seo] = Form.useForm();
    const [form_organization_location] = Form.useForm();
    const [form_organization_phone] = Form.useForm();
    const [form_organization_worktime] = Form.useForm();
    const [form_organization_worktime_admin] = Form.useForm();
    const [form_organization_specialists] = Form.useForm();
    const [form_organization_price] = Form.useForm();
    const [form_organization_services] = Form.useForm();

    // Organization [search]
    const [form_organization_my_search] = Form.useForm();
    const [form_organization_list_search] = Form.useForm();
    const [form_locations_search_search] = Form.useForm();

    // Specialist
    const [form_specialist_add] = Form.useForm();
    const [form_specialist_data] = Form.useForm();
    const [form_specialist_seo] = Form.useForm();
    const [form_specialist_location] = Form.useForm();
    const [form_specialist_education] = Form.useForm();
    const [form_specialist_experience] = Form.useForm();
    const [form_specialist_phone] = Form.useForm();

    // Specialist [search]
    const [form_specialist_my_search] = Form.useForm();
    const [form_specialist_list_search] = Form.useForm();

    // Advertisement
    const [form_advertisement_add] = Form.useForm();
    const [form_advertisement_data] = Form.useForm();
    const [form_advertisement_location] = Form.useForm();
    const [form_advertisement_content] = Form.useForm();

    // Advertisement [search]
    const [form_advertisement_my_search] = Form.useForm();
    const [form_advertisement_list_search] = Form.useForm();

    // Categories
    const [form_categories_list_search] = Form.useForm();
    const [form_categories_add] = Form.useForm();
    const [form_categories_data] = Form.useForm();

    // Posts
    const [form_posts_list_search] = Form.useForm();
    const [form_posts_add] = Form.useForm();
    const [form_posts_data] = Form.useForm();
    const [form_posts_content] = Form.useForm();
    const [form_posts_seo] = Form.useForm();

    // Posts Category
    const [form_posts_category_list_search] = Form.useForm();
    const [form_posts_category_add] = Form.useForm();
    const [form_posts_category_data] = Form.useForm();
    const [form_posts_category_content] = Form.useForm();
    const [form_posts_category_seo] = Form.useForm();

    // Users
    const [form_users_list_search] = Form.useForm();
    const [form_users_data] = Form.useForm();
    
    // Surveys
    const [form_surveys_list_search] = Form.useForm();
    const [form_surveys_add] = Form.useForm();
    const [form_surveys_data] = Form.useForm();
    const [form_surveys_content] = Form.useForm();
    const [form_surveys_seo] = Form.useForm();
    const [form_surveys_questions] = Form.useForm();
    const [form_surveys_questions_answers] = Form.useForm();
    const [form_surveys_emails] = Form.useForm();
    const [form_surveys_criteria] = Form.useForm();

    // Filling out search forms

    // Reviews
    form_reviews_list_search.setFieldsValue({ search: reviews_list_search });

    // Organizatios
    form_organization_my_search.setFieldsValue({ search: organization_my_search });
    form_organization_list_search.setFieldsValue({ search: organization_list_search });

    // Categories
    form_categories_list_search.setFieldsValue({ search: categories_list_search });

    // Posts
    form_posts_list_search.setFieldsValue({ search: posts_list_search });

    // Posts Category
    form_posts_category_list_search.setFieldsValue({ search: posts_list_search });

    // Page component loading status
    const isInitialMount = useRef(true);

    // Page Component Rendering event
    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;

        }
    }, []);

    // The SSR Editor Loader Component
    function Loading(props) {
        if (props.error) {
            return <div>Error! <button onClick={ props.retry }>Retry</button></div>;
        } else if (props.pastDelay) {
            return <div>Loading...</div>;
        } else {
            return null;
        }
    }

    // SSR Editor Loader
    const ReactEditorJS = Loadable({
        loader: () => import('../editors/organization'),
        render(loaded, props) {
            let Component = loaded.CustomEditor;
            return <Component {...props} />;
        },
        loading: Loading,
    });

    // Функция заполнения формы профиля
    show_tab_profile();

    // Выход
    async function logout() {
        message.error('Сессия закрыта');
        removeCookie('token', { path: '/' });
        dispatch(setToken(''));
        dispatch(setAuth(''));
        dispatch(deleteData());
    }

    // Вывод номера в нужном формате
    function customPhoneMask(phone = "") {
        var end = "";
        if (phone !== null) {
            var length = phone.length;
            
            switch(SETTINGS_APP.project_format) {
                case 'UZ':
                    if(length > 0) { if (phone[0] === '9') { end += '+'; } end += phone[0]; }
                    if(length > 1) { end += phone[1]; }
                    if(length > 2) { end += phone[2]; }
                    if(length > 3) { end += " "+phone[3]; }
                    if(length > 4) { end += phone[4]; }
                    if(length > 5) { end += " "+phone[5]; }
                    if(length > 6) { end += phone[6]; }
                    if(length > 7) { end += phone[7]; }
                    if(length > 8) { end += " "+phone[8]; }
                    if(length > 9) { end += phone[9]; }
                    if(length > 10) { end += " "+phone[10]; }
                    if(length > 11) { end += phone[11]; }
                    break;
                default:
                    if(length > 0) { if (phone[0] === '7') { end += '+'; } end += phone[0]; }
                    if(length > 1) { end += "("+phone[1]; }
                    if(length > 2) { end += phone[2]; }
                    if(length > 3) { end += phone[3]+")"; }
                    if(length > 4) { end += phone[4]; }
                    if(length > 5) { end += phone[5]; }
                    if(length > 6) { end += phone[6]; }
                    if(length > 7) { end += "-"+phone[7]; }
                    if(length > 8) { end += phone[8]; }
                    if(length > 9) { end += "-"+phone[9]; }
                    if(length > 10) { end += phone[10]; }
            }
        } else {
            end = "Номер не заполнен";
        }

        return end;
    }

    /* Переключатели разделов Start */
        // Глобальный
        const tabChange = (key) => {
            setActiveTab(key);
            switch(key) {
                case 'profile':
                    show_tab_profile(key);
                    break;
                case 'safety':
                    show_tab_safety(key);
                    break;
                case 'organization_my':
                    show_tab_organization_my();
                    break;
                case 'organization_list':
                    show_tab_organization_list();
                    break;
                case 'specialist_my':
                    show_tab_specialist_my();
                    break;
                case 'specialist_list':
                    show_tab_specialist_list();
                    break;
                case 'reviews_my':
                    show_tab_reviews_my();
                    break;
                case 'reviews_list':
                    show_tab_reviews_list();
                    break;
                case 'publications':
                    show_tab_posts_list();
                    break;
                case 'publications_category':
                    show_tab_posts_category_list();
                    break;
                case 'categories':
                    show_tab_categories_list();
                    break;
                case 'surveys':
                    show_tab_surveys_list();
                    break;
                case 'users':
                    show_tab_users_list();
                    break;
                case 'advertisement_my':
                    show_tab_advertisement_my();
                    break;
                case 'advertisement_list':
                    show_tab_advertisement_list();
                    break;
            }
        };

        // Организация
        const tabOrgEditChange = (key) => {
            setActiveTabOrgEdit(key);
        };
    /* Переключатели разделов Stop */

    /* Профиль Start */
        // Открытие вкладки профиля
        async function show_tab_profile(key) {
            form_profile.resetFields();
            form_profile.setFieldsValue({
                firstname: profile.data.firstname,
                lastname: profile.data.lastname,
                patronymic: profile.data.patronymic,
                gender: profile.data.gender,
                location: profile.data.location
            });
            if (+profile.data.birthdate > 0) {
                form_profile.setFieldsValue({
                    birthdate:moment.unix(profile.data.birthdate).format("DD.MM.YYYY"),
                });
            }
        }

        // Отправка формы обновления профиля
        async function form_finish_profile(v) {
            if(v['birthdate']) { v['birthdate'] = Date.parse(moment(v['birthdate'],'DD.MM.YYYY').format())/1000; }
            await updProfile(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [updProfile]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Профиль обновлён');
                    await getProfile({},dispatch,profile)
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Отправка формы подтверждения email
        async function form_finish_profile_email_confirm(e) {
            if (e.length === 6) {
                await confirmProfileEmailCode({code:e},dispatch,profile)
                .then(async json => {
                    console.log ( `%c [API]: [confirmProfileEmailCode]`, `color: #A366FF`, json );
                    if (json?.code) {
                        dispatch( updPanel('confirmProfileEmail') );
                        message.success('Почта подтверждена');
                        await getProfile({},dispatch,profile)
                    } else {
                        let error = [];
                        if (json.error[0]) {
                            error.push(json.error[0]);
                        }
                        if (json.error[1]) {
                            error.push(json.error[1]);
                        }
                        if (json.error[3]) {
                            error.push((json.error[3]).join(', '));
                        }
                        message.error(error.join('. '));
                    }
                });
            }
        }

        // Отправка формы подтверждения phone
        async function form_finish_profile_phone_confirm(e) {
            if (e.length === 6) {
                await confirmProfilePhoneCode({code:e},dispatch,profile)
                .then(async json => {
                    console.log ( `%c [API]: [confirmProfilePhoneCode]`, `color: #A366FF`, json );
                    if (json?.code) {
                        dispatch( updPanel('confirmProfilePhone') );
                        message.success('Телефон подтвержден');
                        await getProfile({},dispatch,profile)
                    } else {
                        let error = [];
                        if (json.error[0]) {
                            error.push(json.error[0]);
                        }
                        if (json.error[1]) {
                            error.push(json.error[1]);
                        }
                        if (json.error[3]) {
                            error.push((json.error[3]).join(', '));
                        }
                        message.error(error.join('. '));
                    }
                });
            }
        }
    /* Профиль End */

    /* Безопасность Start */
        // Открытие вкладки безопасность
        async function show_tab_safety(key) {
            form_email.resetFields();
            form_email.setFieldsValue({
                email: profile.data.email
            });

            form_phone.resetFields();
            form_phone.setFieldsValue({
                phone: `+${(profile.data.phone)}`
            });

            setPassword_complexity(0);
            form_password.resetFields();
        }

        // Открытие модального окна подтверждения email
        async function clickProfileConfirmEmail() {
            await confirmProfileEmail({},dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [confirmProfileEmail]`, `color: #A366FF`, json );
                if (json?.code) {
                    dispatch( updPanel('confirmProfileEmail') );
                    message.success('На Вашу почту отправлено письмо с кодом');
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Открытие модального окна подтверждения phone
        async function clickProfileConfirmPhone() {
            await confirmProfilePhone({},dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [confirmProfilePhone]`, `color: #A366FF`, json );
                if (json?.code) {
                    dispatch( updPanel('confirmProfilePhone') );
                    message.success('На Ваш телефон отправлено SMS с кодом');
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Закрытие модального окна подтверждения email
        async function hide_modal_confirm_profile_email() {
            dispatch( updPanel('confirmProfileEmail') );
        }

        // Отправка формы сохранения email
        async function form_finish_profile_email(v) {
            if(v['birthdate']) { v['birthdate'] = Date.parse(moment(v['birthdate']).format())/1000; }
            await updProfileEmail(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [updProfileEmail]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Почта изменена');
                    await getProfile({},dispatch,profile)
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Закрытие модального окна подтверждения phone
        async function hide_modal_confirm_profile_phone() {
            dispatch( updPanel('confirmProfilePhone') );
        }
        
        // Отправка формы сохранения номера телефона
        async function form_finish_profile_phone(v) {
            var conformedPhoneNumber = conformToMask(
                v['phone'],
                SETTINGS_APP.mask[SETTINGS_APP.project_format]['account'],
                {guide: false}
            )
            v['phone'] = Number(conformedPhoneNumber.conformedValue.replace(/\D+/g,""));

            await updProfilePhone(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [updProfilePhone]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Номер изменён');
                    await getProfile({},dispatch,profile)
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Отправка формы сохранения пароля
        async function form_finish_profile_password(v) {
            if (v['password'] === v['password_new']) {
                message.error('Вы написали одинаковые пароли');
                return false;
            }
            if (checkPassword(v['password_new']) < 100) {
                message.error('Пароль недостаточно сложный');
                return false;
            }

            await updProfilePassword(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [updProfilePassword]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Пароль изменен');
                    await getProfile({},dispatch,profile)
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }
    /* Безопасность End */

    /* Мои отзывы Start */
        // Получение информации о отзыве (с разделением)
        async function reviews_load(id) {
            switch(activeTab) {
                case 'reviews_my':
                    get_reviews_item(id);
                    break;
                case 'reviews_list':
                    get_reviews_item(id);
                    break;
            }
        }

        // Открытие вкладки моих отзывов
        async function show_tab_reviews_my() {
            await getReviewsMy({},dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [getReviewsMy]`, `color: #A366FF`, json );
                if (json?.code) {
                    //Done
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

    /* Мои отзывы End */

    /* Все отзывы Start */
        // Открытие вкладки всех отзывов
        async function show_tab_reviews_list() {
            await getReviewsList({},dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [getReviewsList]`, `color: #A366FF`, json );
                if (json?.code) {
                    //Done
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Изменение текста поисковой строки в списке отзывов
        function reviews_list_search_change (e) { 
            setReviews_list_search(e.target.value.replace(/\s+/g,' '));
        }

        // Изменение статуса поисковой формы в списке отзывов
        function reviews_list_search_status_change (e) {
            setReviews_list_status(e);
        }

        // Открытие модального окна моего отзыва
        async function show_tab_reviews_item(id) {
            form_reviews_data.resetFields();
            dispatch( setReviews_id({}) );
            dispatch( updPanel('reviews') );

            await get_reviews_item(id);
        }
        
        // Закрытие модального окна моего отзыва
        async function hide_tab_reviews_item() {
            dispatch( updPanel('reviews') );
            tabChange(activeTab);
        }

        // Получение информации о отзыве
        async function get_reviews_item(id) {
            await getReviewsId({
                mode: 'edit',
                id: id
            },dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [getReviewsId]`, `color: #A366FF`, json );
                if (json?.code) {
                    if (json?.result) {
                        form_reviews_data.setFieldsValue({
                            status: json?.result?.status,
                            text_answer: json?.result?.text_answer
                        });
                    }
                } else {
                    dispatch( updPanel('reviews') );
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Отправка формы сохранения отзыва
        async function form_finish_reviews_data(v) {
            v['id'] = reviews.reviews_id.id;
            
            await updReviewsData(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [updReviewsData]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Данные обновлены');
                    reviews_load(v['id']);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Открытие модального окна
        async function show_tab_reviews_add() {
            await getSpecialistOrganizationsList({},dispatch,profile)
            form_review_add.resetFields();
            form_review_add.setFieldsValue({
                date_create:moment().format("DD.MM.YYYY"),
                time_create:moment().format("HH:mm")
            });
            dispatch( updPanel('reviewAdd') );
        }

        // Закрытие модального окна
        async function hide_tab_reviews_add() {
            dispatch( updPanel('reviewAdd') );
        }

        // Отправка формы сохранения отзыва
        async function form_finish_review_add(v) {
            if(v['date_create'] && v['time_create']) { v['date_create'] = Date.parse(moment(`${v['date_create']} ${v['time_create']}`,'DD.MM.YYYY HH:mm').format())/1000; }
            
            await addReview(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [addReview]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Отзыв добавлен');
                    show_tab_reviews_list();
                    hide_tab_reviews_add();
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }
    /* Все отзывы End */

    /* Мои организации Start */
        // Открытие вкладки списка моих организаций
        async function show_tab_organization_my() {
            await getOrganizationMy({},dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [getOrganizationMy]`, `color: #A366FF`, json );
                if (json?.code) {
                    //Done
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Изменение текста поисковой строки в списке моих организаций
        function organization_my_search_change (e) { 
            setOrganization_my_search(e.target.value.replace(/\s+/g,' '));
        }

        // Открытие модального окна создания моей организации
        async function show_tab_organization_my_add() {
            form_organization_add.resetFields();
            dispatch( updPanel('organizationAdd') );
        }

        // Закрытие модального окна создания моей организации
        async function hide_tab_organization_my_add() {
            dispatch( updPanel('organizationAdd') );
        }

        // Открытие модального окна моей организации
        async function show_tab_organization_my_item(id) {
            form_organization_data.resetFields();
            form_organization_soc.resetFields();
            form_organization_content.resetFields();
            var contentBlock = htmlToDraft('');
            if (contentBlock) {
                var contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
                setEditorState(EditorState.createWithContent(contentState));
            }
            form_organization_location.resetFields();
            form_organization_seo.resetFields();
            dispatch( setOrganization_id({}) );
            dispatch( setLocations_search([]) );
            dispatch( updPanel('organization') );
            
            await get_organization_my_item(id);
        }

        // Получение информации о моей организации
        async function get_organization_my_item(id) {
            await getOrganizationId({
                mode: 'edit',
                id: id
            },dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [getOrganizationId]`, `color: #A366FF`, json );
                if (json?.code) {
                    if (json?.result) {
                        form_organization_data.setFieldsValue({
                            status: json?.result?.status,
                            orgname: json?.result?.orgname,
                            orgname_last: json?.result?.orgname_last,
                            type: json?.result?.type,
                            category: json?.result?.category,
                            email: json?.result?.email,
                            location: json?.result?.location
                        });
                        form_organization_soc.setFieldsValue({
                            website: json?.result?.website,
                            soc_vk: json?.result?.soc_vk,
                            soc_facebook: json?.result?.soc_facebook,
                            soc_instagram: json?.result?.soc_instagram,
                            soc_telegram: json?.result?.soc_telegram
                        });
                        var contentBlock = htmlToDraft(json?.result?.content);
                        if (contentBlock) {
                            var contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
                            setEditorState(EditorState.createWithContent(contentState));
                        }
                        form_organization_content.setFieldsValue({
                            content: draftToHtml(convertToRaw(editorState.getCurrentContent()))
                        });
                        form_organization_location.setFieldsValue({
                            latitude: json?.result?.latitude,
                            longitude: json?.result?.longitude,
                            address: json?.result?.address,
                        });
                        form_organization_seo.setFieldsValue({
                            seo_title: json?.result?.seo_title,
                            seo_description: json?.result?.seo_description,
                            seo_keywords: json?.result?.seo_keywords,
                        });
                    }
                } else {
                    dispatch( updPanel('organization') );
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Отправка формы добавления организации
        async function form_finish_organization_add(v) {
            await addOrganization(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [addOrganization]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Организация добавлена');
                    hide_tab_organization_my_add();
                    getOrganizationMy({},dispatch,profile);
                    show_tab_organization_my_item(json?.result);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }
    /* Мои организации End */

    /* Все организации Start */
        // Открытие вкладки списка всех организаций
        async function show_tab_organization_list() {
            await getOrganizationList({
                mode: 'edit'
            },dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [getOrganizationList]`, `color: #A366FF`, json );
                if (json?.code) {
                    //Done
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
            getOrganizationSpecialistsSpecializationList({},dispatch,profile);
        }

        // Изменение текста поисковой строки в списке всех организаций
        function organization_list_search_change (e) { 
            setOrganization_list_search(e.target.value.replace(/\s+/g,' '));
        }

        // Изменение статуса поисковой формы в списке всех организаций
        function organization_list_search_status_change (e) {
            setOrganization_list_status(e);
        }
    /* Все организации End */

    /* Организация Start */
        // Получение информации о организации (с разделением)
        async function organization_load(id) {
            switch(activeTab) {
                case 'organization_my':
                    get_organization_my_item(id);
                    break;
                case 'organization_list':
                    get_organization_list_item(id);
                    break;
            }
            getOrganizationSpecialistsSpecializationList({},dispatch,profile);
        }

        // Открытие модального окна организации
        async function show_tab_organization_list_item(id) {
            form_organization_data.resetFields();
            form_organization_soc.resetFields();
            form_organization_content.resetFields();
            var contentBlock = htmlToDraft('');
            if (contentBlock) {
                var contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
                setEditorState(EditorState.createWithContent(contentState));
            }
            form_organization_location.resetFields();
            form_organization_seo.resetFields();
            dispatch( setOrganization_id({}) );
            dispatch( setLocations_search([]) );
            dispatch( updPanel('organization') );
            
            await get_organization_list_item(id);
        }
        
        // Закрытие модального окна организации
        async function hide_tab_organization_item() {
            dispatch( updPanel('organization') );
            tabChange(activeTab);
        }

        // Получение информации о организации
        async function get_organization_list_item(id) {
            await getOrganizationId({
                mode: 'edit',
                id: id
            },dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [getOrganizationId]`, `color: #A366FF`, json );
                if (json?.code) {
                    if (json?.result) {
                        form_organization_data.setFieldsValue({
                            status: json?.result?.status,
                            orgname: json?.result?.orgname,
                            orgname_last: json?.result?.orgname_last,
                            type: json?.result?.type,
                            category: json?.result?.category,
                            location: json?.result?.location,
                            email: json?.result?.email
                        });
                        form_organization_soc.setFieldsValue({
                            website: json?.result?.website,
                            soc_vk: json?.result?.soc_vk,
                            soc_facebook: json?.result?.soc_facebook,
                            soc_instagram: json?.result?.soc_instagram,
                            soc_telegram: json?.result?.soc_telegram
                        });
                        var contentBlock = htmlToDraft(json?.result?.content);
                        if (contentBlock) {
                            var contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
                            setEditorState(EditorState.createWithContent(contentState));
                        }
                        form_organization_content.setFieldsValue({
                            content: draftToHtml(convertToRaw(editorState.getCurrentContent()))
                        });
                        form_organization_location.setFieldsValue({
                            latitude: json?.result?.latitude,
                            longitude: json?.result?.longitude,
                            address: json?.result?.address,
                        });
                        form_organization_seo.setFieldsValue({
                            seo_title: json?.result?.seo_title,
                            seo_description: json?.result?.seo_description,
                            seo_keywords: json?.result?.seo_keywords,
                        });
                    }
                } else {
                    dispatch( updPanel('organization') );
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Отправка формы сохранения организации (основная информация)
        async function form_finish_organization_data(v) {
            v['id'] = organization.organization_id.id;
            
            await updOrganizationData(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [updOrganizationData]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Данные обновлены');
                    organization_load(v['id']);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Отправка формы сохранения организации (социальные сети)
        async function form_finish_organization_soc(v) {
            v['id'] = organization.organization_id.id;
            
            await updOrganizationSoc(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [updOrganizationSoc]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Данные обновлены');
                    organization_load(v['id']);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Отправка формы сохранения организации (SEO)
        async function form_finish_organization_seo(v) {
            v['id'] = organization.organization_id.id;
            
            await updOrganizationSeo(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [updOrganizationSeo]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Данные обновлены');
                    organization_load(v['id']);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Отправка формы сохранения организации (контент)
        async function form_finish_organization_content(v) {
            v['id'] = organization.organization_id.id;

            await editorCoreOrganization.current.save()
            .then(async data => {
                v['content'] = JSON.stringify(data);
            });
            
            await updOrganizationContent(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [updOrganizationContent]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Данные обновлены');
                    organization_load(v['id']);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Удаление логотип организации
        async function organization_logo_delete() {
            let v = {};
            v['id'] = organization.organization_id.id;
            
            await delOrganizationLogo(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [delOrganizationLogo]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Логотип удалён');
                    organization_load(v['id']);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Изменение статуса организации
        async function clickUpdOrganizationModeration() {
            await updOrganizationModeration({
                id: organization.organization_id.id
            },dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [updOrganizationModeration]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Заявка отправлена');
                    organization_load(organization.organization_id.id);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        /* Номера телефона Start */
            // Открытие вкладки номеров телефона организации
            async function show_tab_organization_phone(v) {
                form_organization_phone.resetFields();
                form_organization_phone.setFieldsValue({
                    id:v.id,
                    phone:v.phone !== null ? `+${v.phone}` : null,
                    more:v.more
                });
                dispatch( updPanel('organizationPhone') );
            }

            // Закрытие вкладки номеров телефона организации
            async function hide_tab_organization_phone() {
                dispatch( updPanel('organizationPhone') );
            }

            // Добавление нового номера телефона организации
            async function clickAddOrganizationPhone() {
                await addOrganizationPhone({
                    id: organization.organization_id.id
                },dispatch,profile)
                .then(async json => {
                    console.log ( `%c [API]: [addOrganizationPhone]`, `color: #A366FF`, json );
                    if (json?.code) {
                        message.success('Телефон добавлен');
                        organization_load(organization.organization_id.id);
                    } else {
                        let error = [];
                        if (json.error[0]) {
                            error.push(json.error[0]);
                        }
                        if (json.error[1]) {
                            error.push(json.error[1]);
                        }
                        if (json.error[3]) {
                            error.push((json.error[3]).join(', '));
                        }
                        message.error(error.join('. '));
                    }
                });
            }

            // Удаление номера телефона организации
            async function clickDelOrganizationPhone(v) {
                v['organization_id'] = organization.organization_id.id;

                await delOrganizationPhone(v,dispatch,profile)
                .then(async json => {
                    console.log ( `%c [API]: [addDelOrganizationPhone]`, `color: #A366FF`, json );
                    if (json?.code) {
                        message.success('Телефон удалён');
                        organization_load(v['organization_id']);
                    } else {
                        let error = [];
                        if (json.error[0]) {
                            error.push(json.error[0]);
                        }
                        if (json.error[1]) {
                            error.push(json.error[1]);
                        }
                        if (json.error[3]) {
                            error.push((json.error[3]).join(', '));
                        }
                        message.error(error.join('. '));
                    }
                });
            }

            // Отправка формы сохранения номера телефона организации
            async function form_finish_organization_phone(v) {
                var conformedPhoneNumber = conformToMask(
                    v['phone'],
                    SETTINGS_APP.mask[SETTINGS_APP.project_format]['default'],
                    {guide: false}
                )
                v['phone'] = Number(conformedPhoneNumber.conformedValue.replace(/\D+/g,""));

                v['organization_id'] = organization.organization_id.id;
                
                await updOrganizationPhone(v,dispatch,profile)
                .then(async json => {
                    console.log ( `%c [API]: [updOrganizationPhone]`, `color: #A366FF`, json );
                    if (json?.code) {
                        message.success('Номер изменён');
                        organization_load(v['organization_id']);
                    } else {
                        let error = [];
                        if (json.error[0]) {
                            error.push(json.error[0]);
                        }
                        if (json.error[1]) {
                            error.push(json.error[1]);
                        }
                        if (json.error[3]) {
                            error.push((json.error[3]).join(', '));
                        }
                        message.error(error.join('. '));
                    }
                });
            }
        /* Номера телефона End */

        /* Режим работы Start */
            // Открытие вкладки режима работы организации
            async function show_tab_organization_worktime(v) {
                form_organization_worktime.resetFields();
                form_organization_worktime.setFieldsValue({
                    id:v.id,
                    day:v.day,
                    text:v.text,
                    type:v.type
                });
                dispatch( updPanel('organizationWorktime') );
            }

            // Закрытие вкладки режима работы организации
            async function hide_tab_organization_worktime() {
                dispatch( updPanel('organizationWorktime') );
            }

            // Добавление нового пункта в режиме работы организации
            async function clickAddOrganizationWorktime() {
                
                await addOrganizationWorktime({
                    id: organization.organization_id.id
                },dispatch,profile)
                .then(async json => {
                    console.log ( `%c [API]: [addOrganizationWorktime]`, `color: #A366FF`, json );
                    if (json?.code) {
                        message.success('Пункт добавлен');
                        organization_load(organization.organization_id.id);
                    } else {
                        let error = [];
                        if (json.error[0]) {
                            error.push(json.error[0]);
                        }
                        if (json.error[1]) {
                            error.push(json.error[1]);
                        }
                        if (json.error[3]) {
                            error.push((json.error[3]).join(', '));
                        }
                        message.error(error.join('. '));
                    }
                });
            }

            // Удаление пункта в режиме работы организации
            async function clickDelOrganizationWorktime(v) {
                v['organization_id'] = organization.organization_id.id;

                await delOrganizationWorktime(v,dispatch,profile)
                .then(async json => {
                    console.log ( `%c [API]: [delOrganizationWorktime]`, `color: #A366FF`, json );
                    if (json?.code) {
                        message.success('Пункт удалён');
                        organization_load(v['organization_id']);
                    } else {
                        let error = [];
                        if (json.error[0]) {
                            error.push(json.error[0]);
                        }
                        if (json.error[1]) {
                            error.push(json.error[1]);
                        }
                        if (json.error[3]) {
                            error.push((json.error[3]).join(', '));
                        }
                        message.error(error.join('. '));
                    }
                });
            }

            // Отправка формы сохранения режима работы организации
            async function form_finish_organization_worktime(v) {
                v['organization_id'] = organization.organization_id.id;
                
                await updOrganizationWorktime(v,dispatch,profile)
                .then(async json => {
                    console.log ( `%c [API]: [updOrganizationWorktime]`, `color: #A366FF`, json );
                    if (json?.code) {
                        message.success('Пункт изменён');
                        organization_load(v['organization_id']);
                    } else {
                        let error = [];
                        if (json.error[0]) {
                            error.push(json.error[0]);
                        }
                        if (json.error[1]) {
                            error.push(json.error[1]);
                        }
                        if (json.error[3]) {
                            error.push((json.error[3]).join(', '));
                        }
                        message.error(error.join('. '));
                    }
                });
            }
        /* Режим работы End */

        /* Режим работы администрации Start */
            // Добавление нового пункта в режиме работы администрации организации
            async function clickAddOrganizationWorktime_admin() { 
                await addOrganizationWorktime_admin({
                    id: organization.organization_id.id
                },dispatch,profile)
                .then(async json => {
                    console.log ( `%c [API]: [addOrganizationWorktime_admin]`, `color: #A366FF`, json );
                    if (json?.code) {
                        message.success('Пункт добавлен');
                        organization_load(organization.organization_id.id);
                    } else {
                        let error = [];
                        if (json.error[0]) {
                            error.push(json.error[0]);
                        }
                        if (json.error[1]) {
                            error.push(json.error[1]);
                        }
                        if (json.error[3]) {
                            error.push((json.error[3]).join(', '));
                        }
                        message.error(error.join('. '));
                    }
                });
            }

            // Удаление пункта в режиме работы администрации организации
            async function clickDelOrganizationWorktime_admin(v) {
                v['organization_id'] = organization.organization_id.id;

                await delOrganizationWorktime_admin(v,dispatch,profile)
                .then(async json => {
                    console.log ( `%c [API]: [delOrganizationWorktime_admin]`, `color: #A366FF`, json );
                    if (json?.code) {
                        message.success('Пункт удалён');
                        organization_load(v['organization_id']);
                    } else {
                        let error = [];
                        if (json.error[0]) {
                            error.push(json.error[0]);
                        }
                        if (json.error[1]) {
                            error.push(json.error[1]);
                        }
                        if (json.error[3]) {
                            error.push((json.error[3]).join(', '));
                        }
                        message.error(error.join('. '));
                    }
                });
            }

            // Открытие модального окна пункта в режиме работы администрации организации
            async function show_tab_organization_worktime_admin(v) {
                form_organization_worktime_admin.resetFields();
                form_organization_worktime_admin.setFieldsValue({
                    id:v.id,
                    day:v.day,
                    text:v.text,
                    type:v.type
                });
                dispatch( updPanel('organizationWorktime_admin') );
            }
    
            // Закрытие модального окна пункта в режиме работы администрации организации
            async function hide_tab_organization_worktime_admin() {
                dispatch( updPanel('organizationWorktime_admin') );
            }
    
            // Отправка формы сохранения пункта в режиме работы администрации организации
            async function form_finish_organization_worktime_admin(v) {
                v['organization_id'] = organization.organization_id.id;
                
                await updOrganizationWorktime_admin(v,dispatch,profile)
                .then(async json => {
                    console.log ( `%c [API]: [updOrganizationWorktime_admin]`, `color: #A366FF`, json );
                    if (json?.code) {
                        message.success('Пункт изменён');
                        organization_load(v['organization_id']);
                    } else {
                        let error = [];
                        if (json.error[0]) {
                            error.push(json.error[0]);
                        }
                        if (json.error[1]) {
                            error.push(json.error[1]);
                        }
                        if (json.error[3]) {
                            error.push((json.error[3]).join(', '));
                        }
                        message.error(error.join('. '));
                    }
                });
            }
        /* Режим работы администрации End */

        /* ТОП-10 услуг Start */
            // Открытие вкладки режима работы организации
            async function show_tab_organization_services(v) {
                form_organization_services.resetFields();
                form_organization_services.setFieldsValue({
                    id:v.id,
                    name:v.name,
                    amount:v.amount
                });
                dispatch( updPanel('organizationServices') );
            }

            // Закрытие вкладки ТОП-10 услуг организации
            async function hide_tab_organization_services() {
                dispatch( updPanel('organizationServices') );
            }

            // Добавление нового пункта в ТОП-10 услуг организации
            async function clickAddOrganizationServices() {
                
                await addOrganizationServices({
                    id: organization.organization_id.id
                },dispatch,profile)
                .then(async json => {
                    console.log ( `%c [API]: [addOrganizationServices]`, `color: #A366FF`, json );
                    if (json?.code) {
                        message.success('Пункт добавлен');
                        organization_load(organization.organization_id.id);
                    } else {
                        let error = [];
                        if (json.error[0]) {
                            error.push(json.error[0]);
                        }
                        if (json.error[1]) {
                            error.push(json.error[1]);
                        }
                        if (json.error[3]) {
                            error.push((json.error[3]).join(', '));
                        }
                        message.error(error.join('. '));
                    }
                });
            }

            // Удаление пункта в ТОП-10 услуг организации
            async function clickDelOrganizationServices(v) {
                v['organization_id'] = organization.organization_id.id;

                await delOrganizationServices(v,dispatch,profile)
                .then(async json => {
                    console.log ( `%c [API]: [delOrganizationServices]`, `color: #A366FF`, json );
                    if (json?.code) {
                        message.success('Пункт удалён');
                        organization_load(v['organization_id']);
                    } else {
                        let error = [];
                        if (json.error[0]) {
                            error.push(json.error[0]);
                        }
                        if (json.error[1]) {
                            error.push(json.error[1]);
                        }
                        if (json.error[3]) {
                            error.push((json.error[3]).join(', '));
                        }
                        message.error(error.join('. '));
                    }
                });
            }

            // Отправка формы сохранения ТОП-10 услуг организации
            async function form_finish_organization_services(v) {
                v['organization_id'] = organization.organization_id.id;
                
                await updOrganizationServices(v,dispatch,profile)
                .then(async json => {
                    console.log ( `%c [API]: [updOrganizationServices]`, `color: #A366FF`, json );
                    if (json?.code) {
                        message.success('Пункт изменён');
                        organization_load(v['organization_id']);
                    } else {
                        let error = [];
                        if (json.error[0]) {
                            error.push(json.error[0]);
                        }
                        if (json.error[1]) {
                            error.push(json.error[1]);
                        }
                        if (json.error[3]) {
                            error.push((json.error[3]).join(', '));
                        }
                        message.error(error.join('. '));
                    }
                });
            }
        /* ТОП-10 услуг End */

        /* Фотографии в шапке Start */
            // Удаление фотографии в шапке
            async function organizations_image_header_mob_delete() {
                let v = {};
                v['organization_id'] = organization.organization_id.id;

                await delOrganizationHeaderMob(v,dispatch,profile)
                .then(async json => {
                    console.log ( `%c [API]: [delOrganizationHeaderMob]`, `color: #A366FF`, json );
                    if (json?.code) {
                        message.success('Изображение удалено');
                        organization_load(v['organization_id']);
                    } else {
                        let error = [];
                        if (json.error[0]) {
                            error.push(json.error[0]);
                        }
                        if (json.error[1]) {
                            error.push(json.error[1]);
                        }
                        if (json.error[3]) {
                            error.push((json.error[3]).join(', '));
                        }
                        message.error(error.join('. '));
                    }
                });
            }

            // Удаление фотографии в шапке
            async function organizations_image_header_pc_delete() {
                let v = {};
                v['organization_id'] = organization.organization_id.id;

                await delOrganizationHeaderPc(v,dispatch,profile)
                .then(async json => {
                    console.log ( `%c [API]: [delOrganizationHeaderPc]`, `color: #A366FF`, json );
                    if (json?.code) {
                        message.success('Изображение удалено');
                        organization_load(v['organization_id']);
                    } else {
                        let error = [];
                        if (json.error[0]) {
                            error.push(json.error[0]);
                        }
                        if (json.error[1]) {
                            error.push(json.error[1]);
                        }
                        if (json.error[3]) {
                            error.push((json.error[3]).join(', '));
                        }
                        message.error(error.join('. '));
                    }
                });
            }
        /* Фотографии в шапке End */

        /* Фотографии Start */
            // Удаление фотографии организации
            async function clickDelOrganizationPhotos(v) {
                v['organization_id'] = organization.organization_id.id;

                await delOrganizationPhotos(v,dispatch,profile)
                .then(async json => {
                    console.log ( `%c [API]: [delOrganizationPhotos]`, `color: #A366FF`, json );
                    if (json?.code) {
                        message.success('Фото удалено');
                        organization_load(v['organization_id']);
                    } else {
                        let error = [];
                        if (json.error[0]) {
                            error.push(json.error[0]);
                        }
                        if (json.error[1]) {
                            error.push(json.error[1]);
                        }
                        if (json.error[3]) {
                            error.push((json.error[3]).join(', '));
                        }
                        message.error(error.join('. '));
                    }
                });
            }
        /* Фотографии End */

        /* Сертификаты Start */
            // Удаление сертификата организации
            async function clickDelOrganizationCertificates(v) {
                v['organization_id'] = organization.organization_id.id;

                await delOrganizationCertificates(v,dispatch,profile)
                .then(async json => {
                    console.log ( `%c [API]: [delOrganizationCertificates]`, `color: #A366FF`, json );
                    if (json?.code) {
                        message.success('Сертификат удалён');
                        organization_load(v['organization_id']);
                    } else {
                        let error = [];
                        if (json.error[0]) {
                            error.push(json.error[0]);
                        }
                        if (json.error[1]) {
                            error.push(json.error[1]);
                        }
                        if (json.error[3]) {
                            error.push((json.error[3]).join(', '));
                        }
                        message.error(error.join('. '));
                    }
                });
            }
        /* Сертификаты End */

        /* Специалисты Start */
            // Добавление нового специалиста организации
            async function clickAddOrganizationSpecialists() {
                await addOrganizationSpecialists({
                    id: organization.organization_id.id
                },dispatch,profile)
                .then(async json => {
                    console.log ( `%c [API]: [addOrganizationSpecialists]`, `color: #A366FF`, json );
                    if (json?.code) {
                        message.success('Специалист добавлен');
                        organization_load(organization.organization_id.id);
                    } else {
                        let error = [];
                        if (json.error[0]) {
                            error.push(json.error[0]);
                        }
                        if (json.error[1]) {
                            error.push(json.error[1]);
                        }
                        if (json.error[3]) {
                            error.push((json.error[3]).join(', '));
                        }
                        message.error(error.join('. '));
                    }
                });
            }

            // Удаление специалиста организации
            async function clickDelOrganizationSpecialists(v) {
                v['organization_id'] = organization.organization_id.id;

                await delOrganizationSpecialists(v,dispatch,profile)
                .then(async json => {
                    console.log ( `%c [API]: [delOrganizationSpecialists]`, `color: #A366FF`, json );
                    if (json?.code) {
                        message.success('Специалист удалён');
                        organization_load(v['organization_id']);
                    } else {
                        let error = [];
                        if (json.error[0]) {
                            error.push(json.error[0]);
                        }
                        if (json.error[1]) {
                            error.push(json.error[1]);
                        }
                        if (json.error[3]) {
                            error.push((json.error[3]).join(', '));
                        }
                        message.error(error.join('. '));
                    }
                });
            }

            // Открытие модального окна специалиста
            async function show_tab_organization_specialists(v) {
                form_organization_specialists.resetFields();
                form_organization_specialists.setFieldsValue({
                    id:v.id,
                    name:v.name,
                    post:v.post,
                    department:v.department,
                    specializations:v.specializations
                });
                dispatch( updPanel('organizationSpecialists') );
            }

            // Закрытие модального окна специалиста
            async function hide_tab_organization_specialists() {
                dispatch( updPanel('organizationSpecialists') );
            }

            // Отправка формы сохранения специалиста
            async function form_finish_organization_specialists(v) {
                v['organization_id'] = organization.organization_id.id;
                
                await updOrganizationSpecialists(v,dispatch,profile)
                .then(async json => {
                    console.log ( `%c [API]: [updOrganizationSpecialists]`, `color: #A366FF`, json );
                    if (json?.code) {
                        message.success('Специалист изменён');
                        organization_load(v['organization_id']);
                    } else {
                        let error = [];
                        if (json.error[0]) {
                            error.push(json.error[0]);
                        }
                        if (json.error[1]) {
                            error.push(json.error[1]);
                        }
                        if (json.error[3]) {
                            error.push((json.error[3]).join(', '));
                        }
                        message.error(error.join('. '));
                    }
                });
            }
        /* Специалисты End */

        /* Цены Start */
            // Удаление файла прайса организации
            async function clickDelOrganizationPrice(v) {
                v['organization_id'] = organization.organization_id.id;

                await delOrganizationPrice(v,dispatch,profile)
                .then(async json => {
                    console.log ( `%c [API]: [delOrganizationPrice]`, `color: #A366FF`, json );
                    if (json?.code) {
                        message.success('Файл удален');
                        organization_load(v['organization_id']);
                    } else {
                        let error = [];
                        if (json.error[0]) {
                            error.push(json.error[0]);
                        }
                        if (json.error[1]) {
                            error.push(json.error[1]);
                        }
                        if (json.error[3]) {
                            error.push((json.error[3]).join(', '));
                        }
                        message.error(error.join('. '));
                    }
                });
            }

            // Открытие модального окна файла прайса организации
            async function show_tab_organization_price(v) {
                form_organization_price.resetFields();
                form_organization_price.setFieldsValue({
                    id:v.id,
                    text:v.text
                });
                dispatch( updPanel('organizationPrice') );
            }

            // Закрытие модального окна файла прайса организации
            async function hide_tab_organization_price() {
                dispatch( updPanel('organizationPrice') );
            }

            // Отправка формы сохранения файла прайса организации
            async function form_finish_organization_price(v) {
                v['organization_id'] = organization.organization_id.id;
                
                await updOrganizationPrice(v,dispatch,profile)
                .then(async json => {
                    console.log ( `%c [API]: [updOrganizationPrice]`, `color: #A366FF`, json );
                    if (json?.code) {
                        message.success('Файл изменён');
                        organization_load(v['organization_id']);
                    } else {
                        let error = [];
                        if (json.error[0]) {
                            error.push(json.error[0]);
                        }
                        if (json.error[1]) {
                            error.push(json.error[1]);
                        }
                        if (json.error[3]) {
                            error.push((json.error[3]).join(', '));
                        }
                        message.error(error.join('. '));
                    }
                });
            }
        /* Цены End */
    /* Организация End */

    /* Мои специалисты Start */
        // Открытие вкладки списка моих специалистов
        async function show_tab_specialist_my() {
            await getSpecialistOrganizationsList({},dispatch,profile)
            await getSpecialistMy({},dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [getSpecialistMy]`, `color: #A366FF`, json );
                if (json?.code) {
                    //Done
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Изменение текста поисковой строки в списке моих специалистов
        function specialist_my_search_change (e) { 
            setSpecialist_my_search(e.target.value.replace(/\s+/g,' '));
        }

        // Открытие модального окна создания моего специалиста
        async function show_tab_specialist_my_add() {
            form_specialist_add.resetFields();
            dispatch( updPanel('specialistAdd') );
        }

        // Закрытие модального окна создания моего специалиста
        async function hide_tab_specialist_my_add() {
            dispatch( updPanel('specialistAdd') );
        }

        // Открытие модального окна моего специалиста
        async function show_tab_specialist_my_item(id) {
            form_specialist_data.resetFields();
            form_specialist_seo.resetFields();
            dispatch( setSpecialist_id({}) );
            dispatch( updPanel('specialist') );
            
            await get_specialist_my_item(id);
        }

        // Получение информации о моём специалисте
        async function get_specialist_my_item(id) {
            await getSpecialistId({
                mode: 'edit',
                id: id
            },dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [getSpecialistId]`, `color: #A366FF`, json );
                if (json?.code) {
                    if (json?.result) {
                        form_specialist_data.setFieldsValue({
                            status: json?.result?.status,
                            organization_id: json?.result?.organization_id,
                            name: json?.result?.name,
                            gender: json?.result?.gender,
                            experience: json?.result?.experience,
                            specialization:json?.result?.specialization,
                            specializations:json?.result?.specializations,
                            organizations:json?.result?.organizations,
                            personal_location: json?.result?.personal_location
                        });
                        form_specialist_seo.setFieldsValue({
                            seo_title: json?.result?.seo_title,
                            seo_description: json?.result?.seo_description,
                            seo_keywords: json?.result?.seo_keywords,
                        });
                        form_specialist_location.setFieldsValue({
                            latitude: json?.result?.personal_latitude,
                            longitude: json?.result?.personal_longitude,
                            address: json?.result?.personal_address,
                        });
                    }
                } else {
                    dispatch( updPanel('specialist') );
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Отправка формы добавления специалиста
        async function form_finish_specialist_add(v) {
            await addSpecialist(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [addSpecialist]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Специалист добавлен');
                    hide_tab_specialist_my_add();
                    getSpecialistMy({},dispatch,profile);
                    show_tab_specialist_my_item(json?.result);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Добавление нового пункта в образовании специалиста
        async function clickAddSpecialistEducation() {
                
            await addSpecialistEducation({
                id: specialist.specialist_id.id
            },dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [addSpecialistEducation]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Пункт добавлен');
                    specialist_load(specialist.specialist_id.id);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Удаление пункта в образовании специалиста
        async function clickDelSpecialistEducation(v) {
            v['specialist_id'] = specialist.specialist_id.id;

            await delSpecialistEducation(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [delSpecialistEducation]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Пункт удалён');
                    specialist_load(v['specialist_id']);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Отправка формы сохранения образования специалиста
        async function form_finish_specialist_education(v) {
            v['specialist_id'] = specialist.specialist_id.id;
            
            await updSpecialistEducation(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [updSpecialistEducation]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Пункт изменён');
                    specialist_load(v['specialist_id']);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Открытие вкладки образования специалиста
        async function show_tab_specialist_education(v) {
            form_specialist_education.resetFields();
            form_specialist_education.setFieldsValue({
                id:v.id,
                date:v.date,
                text:v.text,
            });
            dispatch( updPanel('specialistEducation') );
        }

        // Закрытие вкладки образования специалиста
        async function hide_tab_specialist_education() {
            dispatch( updPanel('specialistEducation') );
        }

        // Добавление нового пункта в достижениях специалиста
        async function clickAddSpecialistExperience() {
                
            await addSpecialistExperience({
                id: specialist.specialist_id.id
            },dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [addSpecialistExperience]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Пункт добавлен');
                    specialist_load(specialist.specialist_id.id);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Удаление пункта в достижениях специалиста
        async function clickDelSpecialistExperience(v) {
            v['specialist_id'] = specialist.specialist_id.id;

            await delSpecialistExperience(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [delSpecialistExperience]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Пункт удалён');
                    specialist_load(v['specialist_id']);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Отправка формы сохранения достижений специалиста
        async function form_finish_specialist_experience(v) {
            v['specialist_id'] = specialist.specialist_id.id;
            
            await updSpecialistExperience(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [updSpecialistExperience]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Пункт изменён');
                    specialist_load(v['specialist_id']);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Открытие вкладки достижений специалиста
        async function show_tab_specialist_experience(v) {
            form_specialist_experience.resetFields();
            form_specialist_experience.setFieldsValue({
                id:v.id,
                text:v.text,
            });
            dispatch( updPanel('specialistExperience') );
        }

        // Закрытие вкладки достижений специалиста
        async function hide_tab_specialist_experience() {
            dispatch( updPanel('specialistExperience') );
        }

        /* Номера телефона Start */
            // Открытие вкладки номеров телефона специалиста
            async function show_tab_specialist_phone(v) {
                form_specialist_phone.resetFields();
                form_specialist_phone.setFieldsValue({
                    id:v.id,
                    phone:v.phone !== null ? `+${v.phone}` : null,
                    more:v.more
                });
                dispatch( updPanel('specialistPhone') );
            }

            // Закрытие вкладки номеров телефона специалиста
            async function hide_tab_specialist_phone() {
                dispatch( updPanel('specialistPhone') );
            }

            // Добавление нового номера телефона специалиста
            async function clickAddSpecialistPhone() {
                await addSpecialistPhone({
                    id: specialist.specialist_id.id
                },dispatch,profile)
                .then(async json => {
                    console.log ( `%c [API]: [addSpecialistPhone]`, `color: #A366FF`, json );
                    if (json?.code) {
                        message.success('Телефон добавлен');
                        specialist_load(specialist.specialist_id.id);
                    } else {
                        let error = [];
                        if (json.error[0]) {
                            error.push(json.error[0]);
                        }
                        if (json.error[1]) {
                            error.push(json.error[1]);
                        }
                        if (json.error[3]) {
                            error.push((json.error[3]).join(', '));
                        }
                        message.error(error.join('. '));
                    }
                });
            }

            // Удаление номера телефона специалиста
            async function clickDelSpecialistPhone(v) {
                v['specialist_id'] = specialist.specialist_id.id;

                await delSpecialistPhone(v,dispatch,profile)
                .then(async json => {
                    console.log ( `%c [API]: [delSpecialistPhone]`, `color: #A366FF`, json );
                    if (json?.code) {
                        message.success('Телефон удалён');
                        specialist_load(v['specialist_id']);
                    } else {
                        let error = [];
                        if (json.error[0]) {
                            error.push(json.error[0]);
                        }
                        if (json.error[1]) {
                            error.push(json.error[1]);
                        }
                        if (json.error[3]) {
                            error.push((json.error[3]).join(', '));
                        }
                        message.error(error.join('. '));
                    }
                });
            }

            // Отправка формы сохранения номера телефона специалиста
            async function form_finish_specialist_phone(v) {
                var conformedPhoneNumber = conformToMask(
                    v['phone'],
                    SETTINGS_APP.mask[SETTINGS_APP.project_format]['default'],
                    {guide: false}
                )
                v['phone'] = Number(conformedPhoneNumber.conformedValue.replace(/\D+/g,""));

                v['specialist_id'] = specialist.specialist_id.id;
                
                await updSpecialistPhone(v,dispatch,profile)
                .then(async json => {
                    console.log ( `%c [API]: [updSpecialistPhone]`, `color: #A366FF`, json );
                    if (json?.code) {
                        message.success('Номер изменён');
                        specialist_load(v['specialist_id']);
                    } else {
                        let error = [];
                        if (json.error[0]) {
                            error.push(json.error[0]);
                        }
                        if (json.error[1]) {
                            error.push(json.error[1]);
                        }
                        if (json.error[3]) {
                            error.push((json.error[3]).join(', '));
                        }
                        message.error(error.join('. '));
                    }
                });
            }
        /* Номера телефона End */
    /* Мои специалисты End */

    /* Все специалисты Start */
        const tabSpecEditChange = (key) => {
            setActiveTabSpecEdit(key);
        };
        // Открытие вкладки списка всех специалистов
        async function show_tab_specialist_list() {
            await getSpecialistOrganizationsList({},dispatch,profile)
            await getSpecialistList({
                mode: 'edit'
            },dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [getSpecialistList]`, `color: #A366FF`, json );
                if (json?.code) {
                    //Done
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
            getSpecialistSpecializationList({},dispatch,profile);
        }

        // Изменение текста поисковой строки в списке всех специалистов
        function specialist_list_search_change (e) { 
            setSpecialist_list_search(e.target.value.replace(/\s+/g,' '));
        }

        // Изменение статуса поисковой формы в списке всех специалистов
        function specialist_list_search_status_change (e) {
            setSpecialist_list_status(e);
        }
    /* Все специалисты End */

    /* Специалист Start */
        // Получение информации о специалисте (с разделением)
        async function specialist_load(id) {
            switch(activeTab) {
                case 'specialist_my':
                    get_specialist_my_item(id);
                    break;
                case 'specialist_list':
                    get_specialist_list_item(id);
                    break;
            }
            getSpecialistSpecializationList({},dispatch,profile);
            getSpecialistOrganizationsList({},dispatch,profile)
        }

        // Открытие модального окна специалиста
        async function show_tab_specialist_list_item(id) {
            form_specialist_data.resetFields();
            form_specialist_seo.resetFields();
            dispatch( setSpecialist_id({}) );
            dispatch( updPanel('specialist') );
            
            await get_specialist_list_item(id);
        }
        
        // Закрытие модального окна организации
        async function hide_tab_specialist_item() {
            dispatch( updPanel('specialist') );
            tabChange(activeTab);
        }

        // Получение информации о специалисте
        async function get_specialist_list_item(id) {
            await getSpecialistId({
                mode: 'edit',
                id: id
            },dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [getSpecialistId]`, `color: #A366FF`, json );
                if (json?.code) {
                    if (json?.result) {
                        form_specialist_data.setFieldsValue({
                            status: json?.result?.status,
                            organization_id: json?.result?.organization_id,
                            name: json?.result?.name,
                            gender: json?.result?.gender,
                            experience: json?.result?.experience,
                            specialization:json?.result?.specialization,
                            specializations:json?.result?.specializations,
                            organizations:json?.result?.organizations,
                            personal_location: json?.result?.personal_location
                        });
                        form_specialist_seo.setFieldsValue({
                            seo_title: json?.result?.seo_title,
                            seo_description: json?.result?.seo_description,
                            seo_keywords: json?.result?.seo_keywords,
                        });
                        form_specialist_location.setFieldsValue({
                            latitude: json?.result?.personal_latitude,
                            longitude: json?.result?.personal_longitude,
                            address: json?.result?.personal_address,
                        });
                    }
                } else {
                    dispatch( updPanel('specialist') );
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Отправка формы сохранения специалиста (основная информация)
        async function form_finish_specialist_data(v) {
            v['id'] = specialist.specialist_id.id;
            
            await updSpecialistData(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [updSpecialistData]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Данные обновлены');
                    specialist_load(v['id']);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Отправка формы сохранения специалиста (SEO)
        async function form_finish_specialist_seo(v) {
            v['id'] = specialist.specialist_id.id;
            
            await updSpecialistSeo(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [updSpecialistSeo]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Данные обновлены');
                    specialist_load(v['id']);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Смена геолокации
        async function specialist_location_select(v) {
            v['id'] = specialist.specialist_id.id;

            await updSpecialistLocation(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [updSpecialistLocation]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Геолокация изменена');
                    specialist_load(v['id']);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Удаление фотографии специалиста
        async function specialist_photo_delete() {
            let v = {};
            v['id'] = specialist.specialist_id.id;
            
            await delSpecialistPhoto(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [delSpecialistPhoto]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Фотография удалена');
                    specialist_load(v['id']);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Изменение статуса специалиста
        async function clickUpdSpecialistModeration() {
            await updSpecialistModeration({
                id: specialist.specialist_id.id
            },dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [updSpecialistModeration]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Заявка отправлена');
                    specialist_load(specialist.specialist_id.id);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Изменение статуса специалиста
        async function clickUpdSpecialistDraft() {
            await updSpecialistDraft({
                id: specialist.specialist_id.id
            },dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [updSpecialistDraft]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Специалист скрыт');
                    specialist_load(specialist.specialist_id.id);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        /* Фотографии Start */
            // Удаление фотографии специалиста
            async function clickDelSpecialistPhotos(v) {
                v['specialist_id'] = specialist.specialist_id.id;

                await delSpecialistPhotos(v,dispatch,profile)
                .then(async json => {
                    console.log ( `%c [API]: [delSpecialistPhotos]`, `color: #A366FF`, json );
                    if (json?.code) {
                        message.success('Фото удалено');
                        specialist_load(v['specialist_id']);
                    } else {
                        let error = [];
                        if (json.error[0]) {
                            error.push(json.error[0]);
                        }
                        if (json.error[1]) {
                            error.push(json.error[1]);
                        }
                        if (json.error[3]) {
                            error.push((json.error[3]).join(', '));
                        }
                        message.error(error.join('. '));
                    }
                });
            }
        /* Фотографии End */

        /* Сертификаты Start */
            // Удаление сертификата специалиста
            async function clickDelSpecialistCertificates(v) {
                v['specialist_id'] = specialist.specialist_id.id;

                await delSpecialistCertificates(v,dispatch,profile)
                .then(async json => {
                    console.log ( `%c [API]: [delSpecialistCertificates]`, `color: #A366FF`, json );
                    if (json?.code) {
                        message.success('Сертификат удалён');
                        specialist_load(v['specialist_id']);
                    } else {
                        let error = [];
                        if (json.error[0]) {
                            error.push(json.error[0]);
                        }
                        if (json.error[1]) {
                            error.push(json.error[1]);
                        }
                        if (json.error[3]) {
                            error.push((json.error[3]).join(', '));
                        }
                        message.error(error.join('. '));
                    }
                });
            }
        /* Сертификаты End */

    /* Специалист End */

    /* Мои вакансии Start */
        // Открытие вкладки списка моих вакансий
        async function show_tab_advertisement_my() {
            if (["3","4"].indexOf( profile?.data?.type ) !== -1) {
                await getOrganizationList({},dispatch,profile)
                await getSpecialistList({},dispatch,profile)
            } else {
                await getOrganizationMy({},dispatch,profile)
                await getSpecialistMy({},dispatch,profile)
            }
            await getAdvertisementMy({},dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [getAdvertisementMy]`, `color: #A366FF`, json );
                if (json?.code) {
                    //Done
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
            getSpecialistSpecializationList({},dispatch,profile);
        }

        // Изменение текста поисковой строки в списке моих вакансий
        function advertisement_my_search_change (e) { 
            setAdvertisement_my_search(e.target.value.replace(/\s+/g,' '));
        }

        // Открытие модального окна создания моей вакансии
        async function show_tab_advertisement_my_add() {
            form_advertisement_add.resetFields();
            dispatch( updPanel('advertisementAdd') );
        }

        // Закрытие модального окна создания моей вакансии
        async function hide_tab_advertisement_my_add() {
            dispatch( updPanel('advertisementAdd') );
        }

        // Открытие модального окна моей вакансии
        async function show_tab_advertisement_my_item(id) {
            form_advertisement_data.resetFields();
            dispatch( setAdvertisement_id({}) );
            dispatch( updPanel('advertisement') );
            
            await get_advertisement_my_item(id);
        }

        // Получение информации о моей вакансии
        async function get_advertisement_my_item(id) {
            await getAdvertisementId({
                mode: 'edit',
                id: id
            },dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [getAdvertisementId]`, `color: #A366FF`, json );
                if (json?.code) {
                    if (json?.result) {
                        form_advertisement_data.setFieldsValue({
                            status: json?.result?.status,
                            fixed: json?.result?.fixed,
                            title: json?.result?.title,
                            type: json?.result?.type,
                            connect_id: json?.result?.connect_id,
                            specializations:json?.result?.specializations,
                            location:json?.result?.location,
                            phone:json?.result?.phone !== null ? `+${json?.result?.phone}` : null,
                            email:json?.result?.email,
                        });
                        form_advertisement_location.setFieldsValue({
                            latitude: json?.result?.latitude,
                            longitude: json?.result?.longitude,
                            address: json?.result?.address,
                        });
                    }
                } else {
                    dispatch( updPanel('advertisement') );
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Отправка формы добавления вакансии
        async function form_finish_advertisement_add(v) {
            await addAdvertisement(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [addAdvertisement]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Специалист добавлен');
                    hide_tab_advertisement_my_add();
                    getAdvertisementMy({},dispatch,profile);
                    show_tab_advertisement_my_item(json?.result);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }
    /* Мои вакансии End */

    /* Все вакансии Start */
        const tabAdvertisementEditChange = (key) => {
            setActiveTabAdvertisementEdit(key);
        };
        // Открытие вкладки списка всех вакансий
        async function show_tab_advertisement_list() {
            if (["3","4"].indexOf( profile?.data?.type ) !== -1) {
                await getOrganizationList({},dispatch,profile)
                await getSpecialistList({},dispatch,profile)
            } else {
                await getOrganizationMy({},dispatch,profile)
                await getSpecialistMy({},dispatch,profile)
            }
            await getAdvertisementList({
                mode: 'edit'
            },dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [getAdvertisementList]`, `color: #A366FF`, json );
                if (json?.code) {
                    //Done
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
            getSpecialistSpecializationList({},dispatch,profile);
        }

        // Изменение текста поисковой строки в списке всех вакансий
        function advertisement_list_search_change (e) { 
            setAdvertisement_list_search(e.target.value.replace(/\s+/g,' '));
        }

        // Изменение статуса поисковой формы в списке всех вакансий
        function advertisement_list_search_status_change (e) {
            setAdvertisement_list_status(e);
        }
    /* Все вакансии End */

    /* Вакансия Start */
        // Получение информации о вакансии (с разделением)
        async function advertisement_load(id) {
            switch(activeTab) {
                case 'advertisement_my':
                    get_advertisement_my_item(id);
                    break;
                case 'advertisement_list':
                    get_advertisement_list_item(id);
                    break;
            }
            getSpecialistSpecializationList({},dispatch,profile);
        }

        // Открытие модального окна вакансии
        async function show_tab_advertisement_list_item(id) {
            form_advertisement_data.resetFields();
            dispatch( setAdvertisement_id({}) );
            dispatch( updPanel('advertisement') );
            
            await get_advertisement_list_item(id);
        }
        
        // Закрытие модального окна вакансии
        async function hide_tab_advertisement_item() {
            dispatch( updPanel('advertisement') );
            tabChange(activeTab);
        }

        // Получение информации о вакансии
        async function get_advertisement_list_item(id) {
            await getAdvertisementId({
                mode: 'edit',
                id: id
            },dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [getAdvertisementId]`, `color: #A366FF`, json );
                if (json?.code) {
                    if (json?.result) {
                        form_advertisement_data.setFieldsValue({
                            status: json?.result?.status,
                            title: json?.result?.title,
                            type: json?.result?.type,
                            connect_id: json?.result?.connect_id,
                            specializations:json?.result?.specializations,
                            location:json?.result?.location,
                            phone:json?.result?.phone !== null ? `+${json?.result?.phone}` : null,
                            email:json?.result?.email,
                        });
                        form_advertisement_location.setFieldsValue({
                            latitude: json?.result?.latitude,
                            longitude: json?.result?.longitude,
                            address: json?.result?.address,
                        });
                    }
                } else {
                    dispatch( updPanel('advertisement') );
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Отправка формы сохранения вакансии (основная информация)
        async function form_finish_advertisement_data(v) {
            v['id'] = advertisement.advertisement_id.id;

            if (v['phone']) {
                var conformedPhoneNumber = conformToMask(
                    v['phone'],
                    SETTINGS_APP.mask[SETTINGS_APP.project_format]['default'],
                    {guide: false}
                )
                v['phone'] = Number(conformedPhoneNumber.conformedValue.replace(/\D+/g,""));
            }
            
            await updAdvertisementData(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [updAdvertisementData]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Данные обновлены');
                    advertisement_load(v['id']);
                    tabChange(activeTab);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Отправка формы сохранения организации (контент)
        async function form_finish_advertisement_content(v) {
            v['id'] = advertisement.advertisement_id.id;

            await editorCoreAdvertisement.current.save()
            .then(async data => {
                v['content'] = JSON.stringify(data);
            });
            
            await updAdvertisementContent(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [updAdvertisementContent]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Данные обновлены');
                    advertisement_load(v['id']);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Смена геолокации
        async function advertisement_location_select(v) {
            v['id'] = advertisement.advertisement_id.id;

            await updAdvertisementLocation(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [updAdvertisementLocation]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Геолокация изменена');
                    advertisement_load(v['id']);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Изменение статуса вакансии
        async function clickUpdAdvertisementModeration() {
            await updAdvertisementModeration({
                id: advertisement.advertisement_id.id
            },dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [updAdvertisementModeration]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Заявка отправлена');
                    advertisement_load(advertisement.advertisement_id.id);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Изменение статуса вакансии
        async function clickUpdAdvertisementTempDraft() {
            await updAdvertisementTempDraft({
                id: advertisement.advertisement_id.id
            },dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [updAdvertisementTempDraft]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Вакансия снята с публикации');
                    advertisement_load(advertisement.advertisement_id.id);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Изменение статуса вакансии
        async function clickUpdAdvertisementPublish() {
            await updAdvertisementPublish({
                id: advertisement.advertisement_id.id
            },dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [updAdvertisementPublish]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Вакансия опубликована');
                    advertisement_load(advertisement.advertisement_id.id);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Поднятие вакансии
        async function clickUpdAdvertisementUp() {
            await updAdvertisementUp({
                id: advertisement.advertisement_id.id
            },dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [updAdvertisementUp]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Вакансия поднята выше');
                    advertisement_load(advertisement.advertisement_id.id);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

    /* Вакансия End */

    /* Поиск и выбор локации Start */
        // Отправка формы поиска локации
        async function form_finish_locations_search(v) {
            await searchLocations({
                search:v['search'].replace(/\s+/g,' ') 
            },dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [searchLocations]`, `color: #A366FF`, json );
                if (json?.code) {
                    //Done
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }
        
        async function organization_location_select(v) {
            v['id'] = organization.organization_id.id;

            await updOrganizationLocation(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [updOrganizationLocation]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Геолокация изменена');
                    get_organization_list_item(v['id']);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }
    /* Поиск локации End */

    /* Объект списков Start */
    var obj = {
        get users () {
            if ( profile.users.length > 0 ) {
                let search = users_list_search.toLowerCase();
                return profile.users.filter( function( item ) {
                    return(
                        [item?.lastname,item?.firstname].join(' ').replace(/ +/g, ' ').trim().toLowerCase().indexOf( search ) > -1
                    );
                } );
            } else {
                return profile.users;
            }
        },
        get advertisement_my () {
            if ( advertisement.advertisement_my.length > 0 ) {
                let search = advertisement_my_search.toLowerCase();
                return advertisement.advertisement_my.filter( function( item ) {
                    return(
                        item['title'].toLowerCase().indexOf( search ) > -1
                    );
                } );
            } else {
                return advertisement.advertisement_my;
            }
        },
        get advertisement_list () {
            if ( advertisement.advertisement_list.length > 0 ) {
                let search = advertisement_list_search.toLowerCase();
                return advertisement.advertisement_list.filter( function( item ) {
                    return(
                        item['title'].toLowerCase().indexOf( search ) > -1 &&
                        (
                            advertisement_list_status !== "" ?
                                item['status'].toLowerCase() === advertisement_list_status
                            :
                                true
                        )
                    );
                } );
            } else {
                return advertisement.advertisement_list;
            }
        },
        get organization_my () {
            if ( organization.organization_my.length > 0 ) {
                let search = organization_my_search.toLowerCase();
                return organization.organization_my.filter( function( item ) {
                    return(
                        item['orgname'].toLowerCase().indexOf( search ) > -1
                    );
                } );
            } else {
                return organization.organization_my;
            }
        },
        get organization_list () {
            if ( organization.organization_list.length > 0 ) {
                let search = organization_list_search.toLowerCase();
                return organization.organization_list.filter( function( item ) {
                    return(
                        item['orgname'].toLowerCase().indexOf( search ) > -1 &&
                        (
                            organization_list_status !== "" ?
                                item['status'].toLowerCase() === organization_list_status
                            :
                                true
                        )
                    );
                } );
            } else {
                return organization.organization_list;
            }
        },
        get specialist_my () {
            if ( specialist.specialist_my.length > 0 ) {
                let search = specialist_my_search.toLowerCase();
                return specialist.specialist_my.filter( function( item ) {
                    return(
                        item['name'].toLowerCase().indexOf( search ) > -1
                    );
                } );
            } else {
                return specialist.specialist_my;
            }
        },
        get specialist_list () {
            if ( specialist.specialist_list.length > 0 ) {
                let search = specialist_list_search.toLowerCase();
                return specialist.specialist_list.filter( function( item ) {
                    return(
                        item['name'].toLowerCase().indexOf( search ) > -1 &&
                        (
                            specialist_list_status !== "" ?
                                item['status'].toLowerCase() === specialist_list_status
                            :
                                true
                        )
                    );
                } );
            } else {
                return specialist.specialist_list;
            }
        },
        get locations_search () {
            return locations.locations_search;
        },
        get reviews_my () {
            return reviews.reviews_my;
        },
        get reviews_list () {
            if ( reviews.reviews_list.length > 0 ) {
                let search = reviews_list_search.toLowerCase();
                return reviews.reviews_list.filter( function( item ) {
                    return(
                        item['orgname'].toLowerCase().indexOf( search ) > -1 &&
                        (
                            reviews_list_status !== "" ?
                                item['status'].toLowerCase() === reviews_list_status
                            :
                                true
                        )
                    );
                } );
            } else {
                return reviews.reviews_list;
            }
        },
        get posts_list () {
            if ( posts.posts_list.length > 0 ) {
                let search = posts_list_search.toLowerCase();
                return posts.posts_list.filter( function( item ) {
                    return(
                        item['title'].toLowerCase().indexOf( search ) > -1 &&
                        (
                            posts_list_status !== "" ?
                                item['status'].toLowerCase() === posts_list_status
                            :
                                true
                        ) &&
                        (
                            posts_list_category !== "" ?
                                item['category'].toLowerCase() === posts_list_category
                            :
                                true
                        )
                    );
                } );
            } else {
                return posts.posts_list;
            }
        },
        get posts_category_list() {
            if ( posts.category_list.length > 0 ) {
                let search = posts_category_list_search.toLowerCase();
                return posts.category_list.filter( function( item ) {
                    return(
                        item['title'].toLowerCase().indexOf( search ) > -1 &&
                        (
                            posts_category_list_status !== "" ?
                                item['status'].toLowerCase() === posts_category_list_status
                            :
                                true
                        ) &&
                        (
                            posts_category_list_category !== "" ?
                                item['category'].toLowerCase() === posts_category_list_category
                            :
                                true
                        )
                    );
                } );
            } else {
                return posts.category_list;
            }
        },
        get posts_category_category_list() {
            if ( posts.category_category_list.length > 0 ) {
                var end = posts.category_category_list.map((item, indexedDB) => ({value:item.value,label:item.label}));
                end.unshift({
                    value:'0',
                    label:'Не выбрано'
                });
                return end;
            } else {
                return [{
                    value:'0',
                    label:'Не выбрано'
                }];
            }
        },
        get categories_list () {
            if ( organization.organization_categories.length > 0 ) {
                let search = categories_list_search.toLowerCase();
                return organization.organization_categories.filter( function( item ) {
                    return(
                        item['label'].toLowerCase().indexOf( search ) > -1
                    );
                } );
            } else {
                return organization.organization_categories;
            }
        },
        get surveys_list () {
            if ( surveys.surveys_list.length > 0 ) {
                let search = surveys_list_search.toLowerCase();
                return surveys.surveys_list.filter( function( item ) {
                    return(
                        item['title'].toLowerCase().indexOf( search ) > -1 &&
                        (
                            surveys_list_status !== "" ?
                                item['status'].toLowerCase() === surveys_list_status
                            :
                                true
                        )
                    );
                } );
            } else {
                return surveys.surveys_list;
            }
        },
    };
    /* Объект списков End */

    /* Записи Start */
        // Изменение вкладки в открытой записи
        const tabPostsEditChange = (key) => {
            setActiveTabPostsEdit(key);
        };

        // Открытие вкладки списка записей
        async function show_tab_posts_list() {
            await getPostsList({
                mode: 'edit'
            },dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [getPostsList]`, `color: #A366FF`, json );
                if (json?.code) {
                    //Done
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Закрытие вкладки списка записей
        async function hide_tab_posts_item() {
            dispatch( updPanel('posts') );
            tabChange(activeTab);
        }

        // Получение информации о записи
        async function posts_load(id) {
            get_posts_item(id);
        }

        // Изменение текста поисковой строки в списке записей
        function posts_list_search_change (e) { 
            setPosts_list_search(e.target.value.replace(/\s+/g,' '));
        }

        // Изменение статуса поисковой формы в списке записей
        function posts_list_search_status_change (e) {
            setPosts_list_status(e);
        }

        // Изменение категории поисковой формы в списке записей
        function posts_list_search_category_change (e) {
            setPosts_list_category(e);
        }

        // Открытие модального окна добавления записи
        async function show_tab_posts_add() {
            form_posts_add.resetFields();
            dispatch( updPanel('postsAdd') );
        }

        // Закрытие модального окна добавления записи
        async function hide_tab_posts_add() {
            dispatch( updPanel('postsAdd') );
        }

        // Отправка формы добавления записи
        async function form_finish_posts_add(v) {

            await addPosts(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [addPosts]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Запись добавлена');
                    hide_tab_posts_add();
                    getPostsList({
                        mode: 'edit'
                    },dispatch,profile);
                    show_tab_posts_item(json?.result);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Открытие модального окна записи
        async function show_tab_posts_item(id) {
            form_posts_data.resetFields();
            form_posts_content.resetFields();
            var contentBlock = htmlToDraft('');
            if (contentBlock) {
                var contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
                setEditorState(EditorState.createWithContent(contentState));
            }
            form_posts_seo.resetFields();
            dispatch( setPosts_id({}) );
            dispatch( updPanel('posts') );
            
            await get_posts_item(id);
        }

        // Получение информации записи
        async function get_posts_item(id) {
            await getPostsId({
                mode: 'edit',
                id: id
            },dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [getPostsId]`, `color: #A366FF`, json );
                if (json?.code) {
                    if (json?.result) {
                        console.log("Date: ",moment.unix(json?.result?.date_create).format("DD.MM.YYYY"));
                        form_posts_data.setFieldsValue({
                            status: json?.result?.status,
                            title: json?.result?.title,
                            category: json?.result?.category,
                            excerpt: json?.result?.excerpt,
                            date_create:moment.unix(json?.result?.date_create).format("DD.MM.YYYY"),
                        });
                        var contentBlock = htmlToDraft(json?.result?.content);
                        if (contentBlock) {
                            var contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
                            setEditorState(EditorState.createWithContent(contentState));
                        }
                        form_posts_content.setFieldsValue({
                            content: draftToHtml(convertToRaw(editorState.getCurrentContent()))
                        });
                        form_posts_seo.setFieldsValue({
                            seo_title: json?.result?.seo_title,
                            seo_description: json?.result?.seo_description,
                            seo_keywords: json?.result?.seo_keywords,
                        });
                    }
                } else {
                    dispatch( updPanel('posts') );
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Отправка формы сохранения записи (основная информация)
        async function form_finish_posts_data(v) {
            if(v['date_create']) { v['date_create'] = Date.parse(moment(v['date_create'],'DD.MM.YYYY').format())/1000; }
            v['id'] = posts.posts_id.id;
            
            await updPostsData(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [updPostsData]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Данные обновлены');
                    posts_load(v['id']);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Отправка формы сохранения записи (SEO)
        async function form_finish_posts_seo(v) {
            v['id'] = posts.posts_id.id;
            
            await updPostsSeo(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [updPostsSeo]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Данные обновлены');
                    posts_load(v['id']);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Отправка формы сохранения записи (контент)
        async function form_finish_posts_content(v) {
            v['id'] = posts.posts_id.id;

            await editorCorePosts.current.save()
            .then(async data => {
                v['content'] = JSON.stringify(data);
            });
            
            await updPostsContent(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [updPostsContent]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Данные обновлены');
                    posts_load(v['id']);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Загрузка изображения/большого изображения записи
        const upload_posts_image = {
            onChange(info) {
                var error_text = '';
                if (info.file.status === 'removed') {
                    console.log('Файл удалён из списка',info.file);
                }
                if (info.file.status === 'uploading') {
                    console.log('Загрузка',info.file);
                }
                if (info.file.status === 'done') {
                    message.success(`${info.file.name} файл загружен`);
                    posts_load(posts.posts_id?.id);
                } else if (info.file.status === 'error') {
                    console.log('Файл не загружен',info.file);
                    message.error(`${info.file.name} файл не загружен`);
                }
            }
        };

        // Удаление изображения записи
        async function posts_image_delete() {
            let v = {};
            v['id'] = posts.posts_id.id;
            
            await delPostsPhoto(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [delPostsPhoto]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Изображение удалено');
                    posts_load(v['id']);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Удаление большого изображения записи
        async function posts_image_big_delete() {
            let v = {};
            v['id'] = posts.posts_id.id;
            
            await delPostsPhotoBig(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [delPostsPhotoBig]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Изображение удалено');
                    posts_load(v['id']);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }
    /* Записи End */

    /* Категории записей Start */
        // Изменение вкладки в открытой категории записей
        const tabPostsCategoryEditChange = (key) => {
            setActiveTabPostsCategoryEdit(key);
        };

        // Открытие вкладки списка категорий записей
        async function show_tab_posts_category_list() {
            await getPostsCategoryList({
                mode: 'edit'
            },dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [getPostsCategoryList]`, `color: #A366FF`, json );
                if (json?.code) {
                    //Done
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });

            await getPostsCategoryCategoryList({},dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [getPostsCategoryCategoryList]`, `color: #A366FF`, json );
                if (json?.code) {
                    //Done
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Закрытие вкладки списка категорий записей
        async function hide_tab_posts_category_item() {
            dispatch( updPanel('posts_category') );
            tabChange(activeTab);
        }

        // Получение информации о категории записей
        async function posts_category_load(id) {
            get_posts_category_item(id);
        }

        // Изменение текста поисковой строки в списке категорий записей
        function posts_category_list_search_change (e) { 
            setPosts_category_list_search(e.target.value.replace(/\s+/g,' '));
        }

        // Изменение статуса поисковой формы в списке категорий записей
        function posts_category_list_search_status_change (e) {
            setPosts_category_list_status(e);
        }

        // Изменение категории поисковой формы в списке категорий записей
        function posts_category_list_search_category_change (e) {
            setPosts_category_list_category(e);
        }

        // Открытие модального окна добавления категории записей
        async function show_tab_posts_category_add() {
            form_posts_category_add.resetFields();
            dispatch( updPanel('postsCategoryAdd') );
        }

        // Закрытие модального окна добавления категории записей
        async function hide_tab_posts_category_add() {
            dispatch( updPanel('postsCategoryAdd') );
        }

        // Отправка формы добавления категории записей
        async function form_finish_posts_category_add(v) {

            await addPostsCategory(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [addPostsCategory]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Категория добавлена');
                    hide_tab_posts_category_add();
                    getPostsCategoryList({
                        mode: 'edit'
                    },dispatch,profile);
                    show_tab_posts_category_item(json?.result);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Открытие модального окна категории записей
        async function show_tab_posts_category_item(id) {
            form_posts_category_data.resetFields();
            form_posts_category_content.resetFields();
            var contentBlock = htmlToDraft('');
            if (contentBlock) {
                var contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
                setEditorState(EditorState.createWithContent(contentState));
            }
            form_posts_category_seo.resetFields();
            dispatch( setCategory_id({}) );
            dispatch( updPanel('posts_category') );
            
            await get_posts_category_item(id);
        }

        // Получение информации категории записей
        async function get_posts_category_item(id) {
            await getPostsCategoryId({
                mode: 'edit',
                id: id
            },dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [getPostsCategoryId]`, `color: #A366FF`, json );
                if (json?.code) {
                    if (json?.result) {
                        form_posts_category_data.setFieldsValue({
                            status: json?.result?.status,
                            title: json?.result?.title,
                            category: json?.result?.category,
                            excerpt: json?.result?.excerpt,
                            date_create:moment.unix(json?.result?.date_create).format("DD.MM.YYYY"),
                        });
                        var contentBlock = htmlToDraft(json?.result?.content);
                        if (contentBlock) {
                            var contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
                            setEditorState(EditorState.createWithContent(contentState));
                        }
                        form_posts_category_content.setFieldsValue({
                            content: draftToHtml(convertToRaw(editorState.getCurrentContent()))
                        });
                        form_posts_category_seo.setFieldsValue({
                            seo_title: json?.result?.seo_title,
                            seo_description: json?.result?.seo_description,
                            seo_keywords: json?.result?.seo_keywords,
                        });
                    }
                } else {
                    dispatch( updPanel('posts_category') );
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });

            await getPostsCategoryCategoryList({},dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [getPostsCategoryCategoryList]`, `color: #A366FF`, json );
                if (json?.code) {
                    //Done
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Отправка формы сохранения категории записей (основная информация)
        async function form_finish_posts_category_data(v) {
            if(v['date_create']) { v['date_create'] = Date.parse(moment(v['date_create'],'DD.MM.YYYY').format())/1000; }
            v['id'] = posts.category_id.id;
            
            await updPostsCategoryData(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [updPostsCategoryData]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Данные обновлены');
                    posts_category_load(v['id']);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Отправка формы сохранения записи (SEO)
        async function form_finish_posts_category_seo(v) {
            v['id'] = posts.category_id.id;
            
            await updPostsCategorySeo(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [updPostsCategorySeo]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Данные обновлены');
                    posts_category_load(v['id']);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Отправка формы сохранения записи (контент)
        async function form_finish_posts_category_content(v) {
            v['id'] = posts.category_id.id;

            await editorCorePostsCategory.current.save()
            .then(async data => {
                v['content'] = JSON.stringify(data);
            });
            
            await updPostsCategoryContent(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [updPostsCategoryContent]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Данные обновлены');
                    posts_category_load(v['id']);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Загрузка изображения/большого изображения записи
        const upload_posts_category_image = {
            onChange(info) {
                var error_text = '';
                if (info.file.status === 'removed') {
                    console.log('Файл удалён из списка',info.file);
                }
                if (info.file.status === 'uploading') {
                    console.log('Загрузка',info.file);
                }
                if (info.file.status === 'done') {
                    message.success(`${info.file.name} файл загружен`);
                    posts_category_load(posts.category_id?.id);
                } else if (info.file.status === 'error') {
                    console.log('Файл не загружен',info.file);
                    message.error(`${info.file.name} файл не загружен`);
                }
            }
        };

        // Удаление изображения категории записей
        async function posts_category_image_delete() {
            let v = {};
            v['id'] = posts.category_id.id;
            
            await delPostsCategoryPhoto(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [delPostsCategoryPhoto]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Изображение удалено');
                    posts_category_load(v['id']);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Удаление большого изображения категории записей
        async function posts_category_image_big_delete() {
            let v = {};
            v['id'] = posts.category_id.id;
            
            await delPostsCategoryPhotoBig(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [delPostsCategoryPhotoBig]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Изображение удалено');
                    posts_category_load(v['id']);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }
    /* Категории записей End */

    /* Категории Start */
        // Открытие модального окна добавления категории
        async function show_tab_categories_add() {
            form_categories_add.resetFields();
            dispatch( updPanel('categoriesAdd') );
        }

        // Закрытие модального окна добавления категории
        async function hide_tab_categories_add() {
            dispatch( updPanel('categoriesAdd') );
        }

        // Открытие модального окна категории
        async function show_tab_categories_item(v) {
            form_categories_data.resetFields();
            form_categories_data.setFieldsValue({
                id:v.id,
                title:v.title,
            });
            dispatch( updPanel('categories_item') );
        }

        // Закрытие модального окна категории
        async function hide_tab_categories_data() {
            dispatch( updPanel('categories_item') );
        }

        // Изменение текста поисковой строки в списке категорий
        function categories_list_search_change (e) { 
            setCategories_list_search(e.target.value.replace(/\s+/g,' '));
        }

        // Отправка формы добавления категории
        async function form_finish_categories_add(v) {
            await addOrganizationCategories(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [addCategories]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Категория добавлена');
                    hide_tab_categories_add();
                    await getOrganizationCategoriesList({},dispatch,profile)
                    .then(async json => {
                        console.log ( `%c [API]: [getOrganizationCategoriesList]`, `color: #A366FF`, json );
                        if (json?.code) {
                            //Done
                        } else {
                            let error = [];
                            if (json.error[0]) {
                                error.push(json.error[0]);
                            }
                            if (json.error[1]) {
                                error.push(json.error[1]);
                            }
                            if (json.error[3]) {
                                error.push((json.error[3]).join(', '));
                            }
                            message.error(error.join('. '));
                        }
                    });
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Отправка формы сохранения категории
        async function form_finish_categories_data(v) {
            await updOrganizationCategories(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [updOrganizationCategories]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Категория изменена');
                    await getOrganizationCategoriesList({},dispatch,profile)
                    .then(async json => {
                        console.log ( `%c [API]: [getOrganizationCategoriesList]`, `color: #A366FF`, json );
                        if (json?.code) {
                            //Done
                        } else {
                            let error = [];
                            if (json.error[0]) {
                                error.push(json.error[0]);
                            }
                            if (json.error[1]) {
                                error.push(json.error[1]);
                            }
                            if (json.error[3]) {
                                error.push((json.error[3]).join(', '));
                            }
                            message.error(error.join('. '));
                        }
                    });
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }
        
        // Открытие списка категорий
        async function show_tab_categories_list() {
            await getOrganizationCategoriesList({},dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [getOrganizationCategoriesList]`, `color: #A366FF`, json );
                if (json?.code) {
                    //Done
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }
    /* Категории End */

    /* Пользователи Start */
        // Открытие модального окна пользователя
        async function show_tab_users_item(v) {
            form_users_data.resetFields();
            form_users_data.setFieldsValue({
                id: v?.id,
                firstname: v?.firstname,
                lastname: v?.lastname,
                patronymic: v?.patronymic,
                gender: v?.gender,
                location: v?.location,
                type: v?.type
            });
            if (+v?.birthdate > 0) {
                form_users_data.setFieldsValue({
                    birthdate:moment.unix(v?.birthdate).format("DD.MM.YYYY"),
                });
            }
            dispatch( updPanel('users_item') );
        }

        // Закрытие модального окна пользователя
        async function hide_tab_users_item() {
            dispatch( updPanel('users_item') );
        }

        // Изменение текста поисковой строки в списке пользователей
        function users_list_search_change (e) { 
            setUsers_list_search(e.target.value.replace(/\s+/g,' '));
        }

        // Отправка формы сохранения пользователя
        async function form_finish_users_data(v) {
            if(v['birthdate']) { v['birthdate'] = Date.parse(moment(v['birthdate'],'DD.MM.YYYY').format())/1000; }
            await updUser(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [updUser]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Пользователь изменён');
                    await getUsers({},dispatch,profile)
                    .then(async json => {
                        console.log ( `%c [API]: [getUsers]`, `color: #A366FF`, json );
                        if (json?.code) {
                            //Done
                        } else {
                            let error = [];
                            if (json.error[0]) {
                                error.push(json.error[0]);
                            }
                            if (json.error[1]) {
                                error.push(json.error[1]);
                            }
                            if (json.error[3]) {
                                error.push((json.error[3]).join(', '));
                            }
                            message.error(error.join('. '));
                        }
                    });
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }
        
        // Открытие списка пользователей
        async function show_tab_users_list() {
            await getUsers({},dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [getUsers]`, `color: #A366FF`, json );
                if (json?.code) {
                    //Done
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }
    /* Пользователи End */
    
    /* Опросы Start */
        // Изменение вкладки в открытом опросе
        const tabSurveysEditChange = (key) => {
            setActiveTabSurveysEdit(key);
        };

        // Открытие вкладки списка опросов
        async function show_tab_surveys_list() {
            await getSurveysList({
                mode: 'edit'
            },dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [getSurveysList]`, `color: #A366FF`, json );
                if (json?.code) {
                    //Done
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Закрытие вкладки списка опросов
        async function hide_tab_surveys_item() {
            dispatch( updPanel('surveys') );
            tabChange(activeTab);
        }

        // Получение информации о опросе
        async function surveys_load(id) {
            get_surveys_item(id);
        }

        // Изменение текста поисковой строки в списке опросов
        function surveys_list_search_change (e) { 
            setSurveys_list_search(e.target.value.replace(/\s+/g,' '));
        }

        // Изменение статуса поисковой формы в списке опросов
        function surveys_list_search_status_change (e) {
            setSurveys_list_status(e);
        }

        // Изменение категории поисковой формы в списке опросов
        function surveys_list_search_category_change (e) {
            setSurveys_list_category(e);
        }

        // Открытие модального окна добавления опроса
        async function show_tab_surveys_add() {
            form_surveys_add.resetFields();
            dispatch( updPanel('surveysAdd') );
        }

        // Закрытие модального окна добавления опроса
        async function hide_tab_surveys_add() {
            dispatch( updPanel('surveysAdd') );
        }

        // Отправка формы добавления опроса
        async function form_finish_surveys_add(v) {

            await addSurveys(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [addSurveys]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Опрос добавлен');
                    hide_tab_surveys_add();
                    getSurveysList({
                        mode: 'edit'
                    },dispatch,profile);
                    show_tab_surveys_item(json?.result);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Открытие модального окна опроса
        async function show_tab_surveys_item(id) {
            form_surveys_data.resetFields();
            form_surveys_content.resetFields();
            var contentBlock = htmlToDraft('');
            if (contentBlock) {
                var contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
                setEditorState(EditorState.createWithContent(contentState));
            }
            form_surveys_seo.resetFields();
            dispatch( setSurveys_id({}) );
            dispatch( updPanel('surveys') );
            
            await get_surveys_item(id);
        }

        // Получение информации опроса
        async function get_surveys_item(id) {
            await getSurveysId({
                mode: 'edit',
                id: id
            },dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [getSurveysId]`, `color: #A366FF`, json );
                if (json?.code) {
                    if (json?.result) {
                        form_surveys_data.setFieldsValue({
                            status: json?.result?.status,
                            title: json?.result?.title,
                            excerpt: json?.result?.excerpt,
                            symptom_id: json?.result?.symptom_id,
                            specialization_id: json?.result?.specialization_id,
                            date_create:moment.unix(json?.result?.date_create).format("DD.MM.YYYY"),
                        });
                        var contentBlock = htmlToDraft(json?.result?.content);
                        if (contentBlock) {
                            var contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
                            setEditorState(EditorState.createWithContent(contentState));
                        }
                        form_surveys_content.setFieldsValue({
                            content: draftToHtml(convertToRaw(editorState.getCurrentContent()))
                        });
                        form_surveys_seo.setFieldsValue({
                            seo_title: json?.result?.seo_title,
                            seo_description: json?.result?.seo_description,
                            seo_keywords: json?.result?.seo_keywords,
                        });
                    }
                } else {
                    dispatch( updPanel('surveys') );
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Отправка формы сохранения опроса (основная информация)
        async function form_finish_surveys_data(v) {
            if(v['date_create']) { v['date_create'] = Date.parse(moment(v['date_create'],'DD.MM.YYYY').format())/1000; }
            v['id'] = surveys.surveys_id.id;
            
            await updSurveysData(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [updSurveysData]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Данные обновлены');
                    surveys_load(v['id']);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Отправка формы сохранения организации (SEO)
        async function form_finish_surveys_seo(v) {
            v['id'] = surveys.surveys_id.id;
            
            await updSurveysSeo(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [updSurveysSeo]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Данные обновлены');
                    surveys_load(v['id']);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Отправка формы сохранения опроса (контент)
        async function form_finish_surveys_content(v) {
            v['id'] = surveys.surveys_id.id;

            await editorCoreSurveys.current.save()
            .then(async data => {
                v['content'] = JSON.stringify(data);
            });
            
            await updSurveysContent(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [updSurveysContent]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Данные обновлены');
                    surveys_load(v['id']);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Загрузка изображения/большого изображения опроса
        const upload_surveys_image = {
            onChange(info) {
                var error_text = '';
                if (info.file.status === 'removed') {
                    console.log('Файл удалён из списка',info.file);
                }
                if (info.file.status === 'uploading') {
                    console.log('Загрузка',info.file);
                }
                if (info.file.status === 'done') {
                    message.success(`${info.file.name} файл загружен`);
                    surveys_load(surveys.surveys_id?.id);
                } else if (info.file.status === 'error') {
                    console.log('Файл не загружен',info.file);
                    message.error(`${info.file.name} файл не загружен`);
                }
            }
        };

        // Удаление изображения опроса
        async function surveys_image_delete() {
            let v = {};
            v['id'] = surveys.surveys_id.id;
            
            await delSurveysPhoto(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [delSurveysPhoto]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Изображение удалено');
                    surveys_load(v['id']);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Удаление большого изображения опроса
        async function surveys_image_big_delete() {
            let v = {};
            v['id'] = surveys.surveys_id.id;
            
            await delSurveysPhotoBig(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [delSurveysPhotoBig]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Изображение удалено');
                    surveys_load(v['id']);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Открытие вкладки вопроса опроса
        async function show_tab_surveys_questions(id) {
            let v = {};
            v['id'] = id;
            v['surveys_id'] = surveys.surveys_id.id;
            
            form_surveys_questions.resetFields();
            dispatch( updPanel('surveysQuestions') );
            await questions_load(v);
        }

        async function questions_load(v) {
            await getSurveysQuestionsId(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [getSurveysQuestionsId]`, `color: #A366FF`, json );
                if (json?.code) {
                    if (json?.result) {
                        form_surveys_questions.setFieldsValue({
                            id:json?.result?.id,
                            text:json?.result?.text
                        });
                    }
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Закрытие вкладки вопроса опроса
        async function hide_tab_surveys_questions() {
            dispatch( updPanel('surveysQuestions') );
        }

        // Добавление нового вопроса опроса
        async function clickAddSurveysQuestions() {
            
            await addSurveysQuestions({
                id: surveys.surveys_id.id
            },dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [addSurveysQuestions]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Вопрос добавлен');
                    surveys_load(surveys.surveys_id.id);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Отправка формы сохранения вопроса опроса
        async function form_finish_surveys_questions(v) {
            v['surveys_id'] = surveys.surveys_id.id
            
            await updSurveysQuestions(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [updSurveysQuestions]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Вопрос изменён');
                    surveys_load(v['surveys_id']);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Удаление вопроса опроса
        async function clickDelSurveysQuestions(v) {
            v['surveys_id'] = surveys.surveys_id.id;

            await delSurveysQuestions(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [delSurveysQuestions]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Вопрос удалён');
                    surveys_load(v['surveys_id']);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Удаление ответа вопроса опроса
        async function clickDelSurveysQuestionsAnswers(v) {
            v['questions_id'] = surveys.questions_id.id;
            v['surveys_id'] = surveys.surveys_id.id;

            await delSurveysQuestionsAnswers(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [delSurveysQuestionsAnswers]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Ответ удалён');
                    questions_load({
                        id: surveys.questions_id.id,
                        surveys_id: surveys.surveys_id.id
                    });
                    surveys_load(surveys.surveys_id.id);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Добавление нового ответа вопроса опроса
        async function clickAddSurveysQuestionsAnswers() {
            
            await addSurveysQuestionsAnswers({
                id: surveys.surveys_id.id,
                questions_id: surveys.questions_id.id
            },dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [addSurveysQuestionsAnswers]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Ответ добавлен');
                    questions_load({
                        id: surveys.questions_id.id,
                        surveys_id: surveys.surveys_id.id
                    });
                    surveys_load(surveys.surveys_id.id);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Открытие вкладки ответа вопроса опроса
        async function show_tab_surveys_questions_answers(id) {
            let v = {};
            v['id'] = id;
            v['surveys_id'] = surveys.surveys_id.id;
            v['questions_id'] = surveys.questions_id.id;
            
            form_surveys_questions_answers.resetFields();
            dispatch( updPanel('surveysQuestionsAnswers') );
            await answers_load(v);
        }

        async function answers_load(v) {
            await getSurveysQuestionsAnswersId(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [getSurveysQuestionsAnswersId]`, `color: #A366FF`, json );
                if (json?.code) {
                    if (json?.result) {
                        form_surveys_questions_answers.setFieldsValue({
                            id:json?.result?.id,
                            text:json?.result?.text,
                            scores:json?.result?.scores
                        });
                    }
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Закрытие вкладки ответа вопроса опроса
        async function hide_tab_surveys_questions_answers() {
            dispatch( updPanel('surveysQuestionsAnswers') );
        }

        // Отправка формы сохранения ответа вопроса опроса
        async function form_finish_surveys_questions_answers(v) {
            v['surveys_id'] = surveys.surveys_id.id
            v['questions_id'] = surveys.questions_id.id
            
            await updSurveysQuestionsAnswers(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [updSurveysQuestionsAnswers]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Ответ изменён');
                    answers_load({
                        id: surveys.questions_id.id,
                        surveys_id: surveys.surveys_id.id,
                        questions_id: surveys.questions_id.id
                    });
                    questions_load({
                        id: surveys.questions_id.id,
                        surveys_id: surveys.surveys_id.id
                    });
                    surveys_load(surveys.surveys_id.id);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Удаление Email опроса
        async function clickDelSurveysEmails(v) {
            v['surveys_id'] = surveys.surveys_id.id;

            await delSurveysEmails(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [delSurveysEmails]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Email удалён');
                    surveys_load(v['surveys_id']);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Открытие вкладки Email опроса
        async function show_tab_surveys_emails(id) {
            let v = {};
            v['id'] = id;
            v['surveys_id'] = surveys.surveys_id.id;
            
            form_surveys_emails.resetFields();
            dispatch( updPanel('surveysEmails') );
            await emails_load(v);
        }

        async function emails_load(v) {
            await getSurveysEmailsId(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [getSurveysEmailsId]`, `color: #A366FF`, json );
                if (json?.code) {
                    if (json?.result) {
                        form_surveys_emails.setFieldsValue({
                            id:json?.result?.id,
                            email:json?.result?.email
                        });
                    }
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Закрытие вкладки Email опроса
        async function hide_tab_surveys_emails() {
            dispatch( updPanel('surveysEmails') );
        }

        // Отправка формы сохранения Email опроса
        async function form_finish_surveys_emails(v) {
            v['surveys_id'] = surveys.surveys_id.id
            
            await updSurveysEmails(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [updSurveysEmails]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Email изменён');
                    surveys_load(v['surveys_id']);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Добавление нового email опроса
        async function clickAddSurveysEmails() {
            
            await addSurveysEmails({
                id: surveys.surveys_id.id
            },dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [addSurveysEmails]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Email добавлен');
                    surveys_load(surveys.surveys_id.id);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Добавление нового критерия опроса
        async function clickAddSurveysCriteria() {
            
            await addSurveysCriteria({
                id: surveys.surveys_id.id
            },dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [addSurveysCriteria]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Критерий добавлен');
                    surveys_load(surveys.surveys_id.id);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Удаление критерия опроса
        async function clickDelSurveysCriteria(v) {
            v['surveys_id'] = surveys.surveys_id.id;

            await delSurveysCriteria(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [delSurveysCriteria]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Критерий удалён');
                    surveys_load(v['surveys_id']);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Открытие вкладки критерия опроса
        async function show_tab_surveys_criteria(id, index) {
            console.log(index);
            let v = {};
            v['id'] = id;
            v['surveys_id'] = surveys.surveys_id.id;
            
            if (index) {
                setSurveys_criteria_min_count(+surveys.surveys_id?.сriteria[index-1]?.count+1);
            } else {
                setSurveys_criteria_min_count(1);
            }
            form_surveys_criteria.resetFields();
            dispatch( updPanel('surveysCriteria') );
            await criteria_load(v);
        }

        async function criteria_load(v) {
            await getSurveysCriteriaId(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [getSurveysCriteriaId]`, `color: #A366FF`, json );
                if (json?.code) {
                    if (json?.result) {
                        form_surveys_criteria.setFieldsValue({
                            id:json?.result?.id,
                            count:json?.result?.count
                        });

                        var contentBlock = htmlToDraft(json?.result?.content);
                        if (contentBlock) {
                            var contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
                            setEditorState(EditorState.createWithContent(contentState));
                        }
                        form_surveys_criteria.setFieldsValue({
                            content: draftToHtml(convertToRaw(editorState.getCurrentContent()))
                        });
                    }
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }

        // Закрытие вкладки критерия опроса
        async function hide_tab_surveys_criteria() {
            dispatch( updPanel('surveysCriteria') );
        }

        // Отправка формы сохранения критерия опроса
        async function form_finish_surveys_criteria(v) {
            v['surveys_id'] = surveys.surveys_id.id;

            await editorCoreSurveysCriteria.current.save()
            .then(async data => {
                v['content'] = JSON.stringify(data);
            });
            
            await updSurveysCriteria(v,dispatch,profile)
            .then(async json => {
                console.log ( `%c [API]: [updSurveysCriteria]`, `color: #A366FF`, json );
                if (json?.code) {
                    message.success('Данные обновлены');
                    criteria_load({
                        id: v['id'],
                        surveys_id: surveys.surveys_id.id
                    });
                    surveys_load(surveys.surveys_id.id);
                } else {
                    let error = [];
                    if (json.error[0]) {
                        error.push(json.error[0]);
                    }
                    if (json.error[1]) {
                        error.push(json.error[1]);
                    }
                    if (json.error[3]) {
                        error.push((json.error[3]).join(', '));
                    }
                    message.error(error.join('. '));
                }
            });
        }
    /* Опросы End */

    // Случайный пользователь
    const randomUser = users[Math.floor(Math.random() * (users.length - 0)) + 0];

    // Статусы организаций
    const getOrgStatus = (status) => {
        var list = {
            'publish': {
                text: 'Опубликовано',
                color: '#52c41a'
            },
            'moderation': {
                text: 'На проверке',
                color: '#faad14'
            },
            'draft': {
                text: 'Черновик',
                color: '#8c6722'
            },
            'cancel': {
                text: 'Отклонено',
                color: '#f5222d'
            },
        };
        return <Tooltip title={list[status]['text']} ><Badge style={{marginRight:10}} color={list[status]['color']} /></Tooltip>
    }

    // Теги статусов организаций
    const getOrgStatusTag = (status) => {
        var list = {
            'publish': {
                text: 'Опубликовано',
                color: '#52c41a'
            },
            'moderation': {
                text: 'На проверке',
                color: '#faad14'
            },
            'draft': {
                text: 'Черновик',
                color: '#8c6722'
            },
            'cancel': {
                text: 'Отклонено',
                color: '#f5222d'
            },
        };
        if (status !== undefined) {
            return <Tag color={list[status]['color']} >{list[status]['text']}</Tag>
        }
        return <Tag color="#424242" >Неизвестно</Tag>
    }

    // Проверка пароля
    function checkPassword(e = '') {
        var s_letters = "qwertyuiopasdfghjklzxcvbnm"; // Буквы в нижнем регистре
        var b_letters = "QWERTYUIOPLKJHGFDSAZXCVBNM"; // Буквы в верхнем регистре
        var digits = "0123456789"; // Цифры
        var specials = "!@#$%^&*()_-+=\|/.,:;[]{}"; // Спецсимволы

        var is_s = false; // Есть ли в пароле буквы в нижнем регистре
        var is_b = false; // Есть ли в пароле буквы в верхнем регистре
        var is_d = false; // Есть ли в пароле цифры
        var is_sp = false; // Есть ли в пароле спецсимволы
                
        for (var i = 0; i < e.length; i++) {
            /* Проверяем каждый символ пароля на принадлежность к тому или иному типу */
            if (!is_s && s_letters.indexOf(e[i]) != -1) {
                is_s = true
            }
            else if (!is_b && b_letters.indexOf(e[i]) != -1) {
                is_b = true
            }
            else if (!is_d && digits.indexOf(e[i]) != -1) {
                is_d = true
            }
            else if (!is_sp && specials.indexOf(e[i]) != -1) {
                is_sp = true
            }
        }

        var rating = 0;
        if (is_s) rating++; // Если в пароле есть символы в нижнем регистре, то увеличиваем рейтинг сложности
        if (is_b) rating++; // Если в пароле есть символы в верхнем регистре, то увеличиваем рейтинг сложности
        if (is_d) rating++; // Если в пароле есть цифры, то увеличиваем рейтинг сложности
        if (is_sp) rating++; // Если в пароле есть спецсимволы, то увеличиваем рейтинг сложности
        /* Далее идёт анализ длины пароля и полученного рейтинга, и на основании этого готовится текстовое описание сложности пароля */

        var score = 0;
        if (e.length < 6 && rating < 3) {
            score = 10;
        }
        if (e.length === 0) {
            score = 0;
        }
        else if (e.length < 6 && rating >= 3) {
            score = 50;
        }
        else if (e.length >= 8 && rating < 3) {
            score = 50;
        }
        else if (e.length >= 8 && rating >= 3) {
            score = 100;
        }
        else if (e.length >= 6 && rating == 1) {
            score = 10;
        }
        else if (e.length >= 6 && rating > 1 && rating < 4) {
            score = 50;
        }
        else if (e.length >= 6 && rating == 4) {
            score = 100;
        };

        setPassword_complexity(score);
        return score;
    }

    // Редактирование пароля
    function onChangePassword(e) {
        checkPassword(e.target.value);
    }

    // Загрузка примеров
    const upload_organization_image = {
        onChange(info) {
            var error_text = '';
            if (info.file.status === 'removed') {
                console.log('Файл удалён из списка',info.file);
            }
            if (info.file.status === 'uploading') {
                console.log('Загрузка',info.file);
            }
            if (info.file.status === 'done') {
                message.success(`${info.file.name} файл загружен`);
                organization_load(organization.organization_id?.id);
            } else if (info.file.status === 'error') {
                console.log('Файл не загружен',info.file);
                message.error(`${info.file.name} файл не загружен`);
            }
        }
    };

    // Загрузка примеров
    const upload_specialist_image = {
        onChange(info) {
            var error_text = '';
            if (info.file.status === 'removed') {
                console.log('Файл удалён из списка',info.file);
            }
            if (info.file.status === 'uploading') {
                console.log('Загрузка',info.file);
            }
            if (info.file.status === 'done') {
                message.success(`${info.file.name} файл загружен`);
                specialist_load(specialist.specialist_id?.id);
            } else if (info.file.status === 'error') {
                console.log('Файл не загружен',info.file);
                message.error(`${info.file.name} файл не загружен`);
            }
        }
    };

    // Поле даты
    function TimeInput(props) {
        console.log("OLD: ",props.value);
        let mask = 'dD.mM.YYYY';
        let formatChars = {
            'Y': '[0-9]',
            'd': '[0-3]',
            'D': '[0-9]',
            'm': '[0-1]',
            'M': '[0-9]'
        };
      
        const beforeMaskedValueChange = (newState, oldState, userInput) => {
            let { value } = newState;
            let dateParts = value.split('.');
            let dayPart = dateParts[0];
            let monthPart = dateParts[1];
            
            let formatChars = {
              'Y': '[0-9]',
              'd': '[0-3]',
              'D': '[0-9]',
              'm': '[0-1]',
              'M': '[1-9]'
            };
        
            if (dayPart.startsWith('3')) {
              formatChars['D'] = '[0-1]';
            } else if (dayPart.startsWith('0')) {
              formatChars['D'] = '[1-9]';
            } else {
              formatChars['D'] = '[0-9]';
            }
        
            if (monthPart.startsWith('1')) {
              formatChars['M'] = '[0-2]';
            } else {
              formatChars['M'] = '[1-9]';
            }
        
            console.log("NEW: ",value);
            return { value, selection: newState.selection };
          };
        return (
            <InputMask
                className='ant-input custom_input'
                mask={mask}
                value={props.value}
                onChange={props.onChange}
                formatChars={formatChars}
                beforeMaskedValueChange={beforeMaskedValueChange}>
            </InputMask>
        );
    }

    // Поле времени
    function TimeTimeInput(props) {
        let mask = 'Hh:Ii';
        let formatChars = {
            'H': '[0-2]',
            'h': '[0-9]',
            'I': '[0-5]',
            'i': '[0-9]'
        };
      
        let beforeMaskedValueChange = (newState, oldState, userInput) => {
            let { value } = newState;
        
            // Conditional mask for the 2nd digit base on the first digit
            if(value.startsWith('2'))
              formatChars['2'] = '[0-3]'; // To block 24, 25, etc.
            else
              formatChars['2'] = '[0-9]'; // To allow 05, 12, etc.
            return {value, selection: newState.selection};
        }
        return (
            <InputMask
                className='ant-input custom_input'
                mask={mask}
                value={props.value}
                onChange={props.onChange}
                formatChars={formatChars}
                beforeMaskedValueChange={beforeMaskedValueChange}>
            </InputMask>
        );
    }

    // Изменение отстутов
    function returnText(text) { return !text ? "" : text.replace('\r\n',"<br />"); }
    // 
    function decnum (a,b) { let cases=[2,0,1,1,1,2];return b[(a%100>4 && a%100<20)?2:cases[(a%10<5)?a%10:5]]; }
    
    return (
        <>
            <Modal mask={false} className="modal_custom" key="m1" width={1600} title="Добавление организации" open={app_global.panel.indexOf( 'organizationAdd' ) != -1 } onOk={() => hide_tab_organization_my_add()} onCancel={() => hide_tab_organization_my_add()} footer={null}>
                <div className='header'>
                    <h2>Добавление организации</h2>
                </div>
                <div className='custom_divider' />
                <div className='body'>
                    <div>
                        <Form name="form" className="default_form" form={form_organization_add} onFinish={form_finish_organization_add}>
                            <label>Название</label>
                            <Form.Item name={`orgname`} rules={[{ required: true, message: `Пожалуйста, введите название организации!` }]}>
                                <Input disabled={organization.loading} className='custom_input' placeholder={`Название организации`} />
                            </Form.Item>
                            <label>Категория</label>
                            <Form.Item name={`category`} rules={[{ required: true, message: `Пожалуйста, выберите категорию организации!` }]}>
                                <Select
                                    disabled={organization.loading}
                                    className="custom_select"
                                    showSearch
                                    placeholder="Выбрать категорию"
                                    optionFilterProp="children"
                                    filterOption={(input, option) =>
                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                    options={organization.organization_categories}
                                />
                            </Form.Item>
                            <label>Город</label>
                            <Form.Item name={`location`} rules={[{ required: true, message: `Пожалуйста, укажите город` }]}>
                                <Select
                                    disabled={organization.loading}
                                    className="custom_select"
                                    showSearch
                                    placeholder="Выбрать город"
                                    optionFilterProp="children"
                                    filterOption={(input, option) =>
                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                    options={locations.locations.map((item, indexedDB) => ({value:item.id,label:item.city}))}
                                />
                            </Form.Item>
                            <label>E-mail</label>
                            <Form.Item name={`email`} rules={[{ required: true, message: `Пожалуйста, введите email!` }]}>
                                <MaskedInput
                                    disabled={organization.loading}
                                    mask={emailMask}
                                    placeholder="mail@52c.ru"
                                    render={(ref, props) => (
                                        <input className="ant-input custom_input" type="text" aria-required="true" ref={ref} {...props} />
                                    )}
                                />
                            </Form.Item>
                            <div className='form_buttons'>
                                <Button className='submit' htmlType="submit" loading={organization.loading}>Сохранить</Button>
                            </div>
                        </Form>
                    </div>
                </div>
            </Modal>
            <Modal mask={false} className="modal_custom" key="m2" width={1600} title="Изменение информации" open={app_global.panel.indexOf( 'organization' ) != -1 } onOk={() => hide_tab_organization_item()} onCancel={() => hide_tab_organization_item()} footer={null}>
                <div className='header'>
                    <h2>Изменение информации</h2>
                    <h3>
                        {Object(organization.organization_id).length === 0 ?
                            <Skeleton.Button style={{marginBottom: '5px', maxWidth: '300px'}} size="small" shape="round" active block />
                        :
                            <>{getOrgStatusTag(organization.organization_id?.status)} {organization.organization_id?.orgname}</>
                        }
                    </h3>
                </div>
                <div className='custom_divider' />
                <div className='body'>
                    {Object(organization.organization_id).length === 0 ?
                        <Skeleton active />
                    :
                        <div className="custom-tabs">
                            <Tabs
                                tabPosition={Object.entries(screens)
                                    .filter((screen) => !!screen[1])
                                    .map((screen) => (
                                        screen[0]
                                )).indexOf( 'xl' ) != -1 ? 'left' : 'top'}
                                activeKey={activeTabOrgEdit}
                                defaultActiveKey="data"
                                onChange={tabOrgEditChange}
                                className="profile_tabs"
                                items={[
                                    {
                                        key: 'data',
                                        label: `Основная информация`,
                                        children: 
                                        <div>
                                            <Form name="form" className="default_form" form={form_organization_data} onFinish={form_finish_organization_data}>
                                                {["3","4"].indexOf( profile?.data?.type ) !== -1 ?
                                                    <label>Статус</label>
                                                :
                                                    null
                                                }
                                                <Form.Item name={`status`} hidden={["3","4"].indexOf( profile?.data?.type ) === -1} >
                                                    <Select
                                                        disabled={app_global.loading}
                                                        className="custom_select"
                                                        showSearch
                                                        placeholder="Выбрать статус"
                                                        optionFilterProp="children"
                                                        filterOption={(input, option) =>
                                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                        }
                                                        options={[
                                                            {value:"publish",label:"Опубликовано"},
                                                            {value:"moderation",label:"На проверке"},
                                                            {value:"draft",label:"Черновик"},
                                                            {value:"cancel",label:"Отклонено"}
                                                        ]}
                                                    />
                                                </Form.Item>
                                                <label>Название</label>
                                                <Form.Item name={`orgname`} rules={[{ required: true, message: `Пожалуйста, введите название организации!` }]}>
                                                    <Input disabled={organization.loading} className='custom_input' placeholder={`Название организации`} />
                                                </Form.Item>
                                                <label>Короткое название</label>
                                                <Form.Item name={`orgname_last`} >
                                                    <Input disabled={organization.loading} className='custom_input' placeholder={`Название организации`} />
                                                </Form.Item>
                                                {["3","4"].indexOf( profile?.data?.type ) !== -1 ?
                                                    <label>Тип организации</label>
                                                :
                                                    null
                                                }
                                                <Form.Item name={`type`} hidden={["3","4"].indexOf( profile?.data?.type ) === -1}>
                                                    <Select
                                                        disabled={organization.loading}
                                                        className="custom_select"
                                                        showSearch
                                                        placeholder="Выбрать тип"
                                                        optionFilterProp="children"
                                                        filterOption={(input, option) =>
                                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                        }
                                                        options={[
                                                            {value:"1",label:"Не указано"},
                                                            {value:"2",label:"Государственные клиники. Не заинтересованные в размещении"},
                                                            {value:"3",label:"Государственные клиники. Заинтересованные в размещении"},
                                                            {value:"4",label:"Частные клиники. Бесплатное размещение"},
                                                            {value:"5",label:"Частные клиники. Платное размещение"},
                                                        ]}
                                                    />
                                                </Form.Item>
                                                <label>Категория</label>
                                                <Form.Item name={`category`} rules={[{ required: true, message: `Пожалуйста, выберите категорию организации!` }]}>
                                                    <Select
                                                        disabled={organization.loading}
                                                        className="custom_select"
                                                        showSearch
                                                        placeholder="Выбрать категорию"
                                                        optionFilterProp="children"
                                                        filterOption={(input, option) =>
                                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                        }
                                                        options={organization.organization_categories}
                                                    />
                                                </Form.Item>
                                                <label>Город</label>
                                                <Form.Item name={`location`} rules={[{ required: true, message: `Пожалуйста, укажите город` }]}>
                                                    <Select
                                                        disabled={organization.loading}
                                                        className="custom_select"
                                                        showSearch
                                                        placeholder="Выбрать город"
                                                        optionFilterProp="children"
                                                        filterOption={(input, option) =>
                                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                        }
                                                        options={locations.locations.map((item, indexedDB) => ({value:item.id,label:item.city}))}
                                                    />
                                                </Form.Item>
                                                <label>E-mail</label>
                                                <Form.Item name={`email`} rules={[{ required: true, message: `Пожалуйста, введите email!` }]}>
                                                    <MaskedInput
                                                        disabled={organization.loading}
                                                        mask={emailMask}
                                                        placeholder="mail@52c.ru"
                                                        render={(ref, props) => (
                                                            <input className="ant-input custom_input" type="text" aria-required="true" ref={ref} {...props} />
                                                        )}
                                                    />
                                                </Form.Item>
                                                <div className='form_buttons'>
                                                    {(organization.organization_id?.status === 'draft' && ["2"].indexOf( profile?.data?.type ) !== -1) ?
                                                        <Button className='button_left' onClick={() => clickUpdOrganizationModeration()}>Отправить на проверку</Button>
                                                    :
                                                        null
                                                    }
                                                    <Button className='submit' htmlType="submit" loading={organization.loading}>Сохранить</Button>
                                                </div>
                                            </Form>
                                        </div>
                                    },
                                    {
                                        key: 'seo',
                                        label: `SEO`,
                                        children: 
                                        <div>
                                            <Form name="form" className="default_form" form={form_organization_seo} onFinish={form_finish_organization_seo}>
                                                <label>Заголовок</label>
                                                <Form.Item name={`seo_title`} rules={[{ required: true, message: `Пожалуйста, введите заголовок для поисковиков!` }]}>
                                                    <Input disabled={organization.loading} className='custom_input' placeholder={`Заголовок`} />
                                                </Form.Item>
                                                <label>Описание</label>
                                                <Form.Item name={`seo_description`} >
                                                    <Input disabled={organization.loading} className='custom_input' placeholder={`Описание`} />
                                                </Form.Item>
                                                <label>Ключевые слова</label>
                                                <Form.Item name={`seo_keywords`} >
                                                    <Input disabled={organization.loading} className='custom_input' placeholder={`Ключевые слова`} />
                                                </Form.Item>
                                                <div className='form_buttons'>
                                                    <Button className='submit' htmlType="submit" loading={organization.loading}>Сохранить</Button>
                                                </div>
                                            </Form>
                                        </div>
                                    },
                                    {
                                        key: 'content',
                                        label: `Подробное описание`,
                                        disabled: ["3","4","5"].indexOf( organization.organization_id?.type ) === -1,
                                        children: 
                                        <div>
                                            <Form name="form" className="default_form" form={form_organization_content} onFinish={form_finish_organization_content} >
                                                <div className='form_buttons top'>
                                                    <Button className='submit' htmlType="submit" loading={organization.loading}>Сохранить</Button>
                                                </div>
                                                <ReactEditorJS 
                                                    token={profile.token}
                                                    API={API}
                                                    ref={editorCoreOrganization}
                                                    onInitialize={handleInitialize}
                                                    defaultValue={organization.organization_id?.content !== undefined ? JSON.parse(organization.organization_id?.content) : {}}
                                                />
                                                <Form.Item name={`content`} hidden >
                                                    <TextArea rows={4} disabled />
                                                </Form.Item>
                                                <div className='form_buttons'>
                                                    <Button className='submit' htmlType="submit" loading={organization.loading}>Сохранить</Button>
                                                </div>
                                            </Form>
                                        </div>
                                    },
                                    {
                                        key: 'soc',
                                        label: `Социальные сети`,
                                        disabled: ["3","5"].indexOf( organization.organization_id?.type ) === -1,
                                        children: 
                                        <div>
                                            <Form name="form" className="default_form" form={form_organization_soc} onFinish={form_finish_organization_soc}>
                                                <label>Ссылка на сайт</label>
                                                <Form.Item name={`website`} >
                                                    <Input disabled={organization.loading} className='custom_input' placeholder={`https://52c.ru/`} />
                                                </Form.Item>
                                                {["5"].indexOf( organization.organization_id?.type ) !== -1 ?
                                                    <label>Ссылка на ВКонтакте</label>
                                                :
                                                    null
                                                }
                                                <Form.Item name={`soc_vk`} hidden={["5"].indexOf( organization.organization_id?.type ) === -1} >
                                                    <Input disabled={organization.loading} className='custom_input' placeholder={`https://vk.com/club0/`} />
                                                </Form.Item>
                                                {["5"].indexOf( organization.organization_id?.type ) !== -1 ?
                                                    <label>Ссылка на Facebook</label>
                                                :
                                                    null
                                                }
                                                <Form.Item name={`soc_facebook`} hidden={["5"].indexOf( organization.organization_id?.type ) === -1} >
                                                    <Input disabled={organization.loading} className='custom_input' placeholder={`https://facebook.com/`} />
                                                </Form.Item>
                                                {["5"].indexOf( organization.organization_id?.type ) !== -1 ?
                                                    <label>Ссылка на Instagram</label>
                                                :
                                                    null
                                                }
                                                <Form.Item name={`soc_instagram`} hidden={["5"].indexOf( organization.organization_id?.type ) === -1} >
                                                    <Input disabled={organization.loading} className='custom_input' placeholder={`https://instagram.com/`} />
                                                </Form.Item>
                                                {["5"].indexOf( organization.organization_id?.type ) !== -1 ?
                                                    <label>Ссылка на Telegram</label>
                                                :
                                                    null
                                                }
                                                <Form.Item name={`soc_telegram`} hidden={["5"].indexOf( organization.organization_id?.type ) === -1} >
                                                    <Input disabled={organization.loading} className='custom_input' placeholder={`https://t.me/`} />
                                                </Form.Item>
                                                <div className='form_buttons'>
                                                    <Button className='submit' htmlType="submit" loading={organization.loading}>Сохранить</Button>
                                                </div>
                                            </Form>
                                        </div>
                                    },
                                    {
                                        key: 'location',
                                        label: `Геолокация`,
                                        children: 
                                        <div>
                                            {locations.loading_search ?
                                                <Skeleton active />
                                            :
                                                <>
                                                    <Form name="form" className="default_form" form={form_organization_location} >
                                                        <label>Текущий адрес</label>
                                                        <Form.Item name={`address`} >
                                                            <Input disabled className='custom_input' placeholder={`Нижний Новгород, Нижегородский Кремль`} />
                                                        </Form.Item>
                                                        <label>Широта</label>
                                                        <Form.Item name={`latitude`} >
                                                            <Input disabled className='custom_input' placeholder={`56.238377`} />
                                                        </Form.Item>
                                                        <label>Долгота</label>
                                                        <Form.Item name={`longitude`} >
                                                            <Input disabled className='custom_input' placeholder={`43.461625`} />
                                                        </Form.Item>
                                                    </Form>
                                                    <div className='custom_divider' />
                                                    <Form name="form" className="default_form" form={form_locations_search_search} onFinish={form_finish_locations_search}>
                                                        <Form.Item key="search" name="search">
                                                            <Input className={`custom_input`} placeholder="Поиск по адресу" bordered={false} 
                                                                prefix={
                                                                    <SearchOutlined style={{ color: 'rgba(0,0,0,.45)', fontSize: 20 }} />
                                                                }
                                                                suffix={
                                                                    <Button className='submit_mini' htmlType="submit" loading={locations.loading_search}>Поиск</Button>
                                                                }
                                                            />
                                                        </Form.Item>
                                                    </Form>
                                                    <List
                                                        locale={{ emptyText: <Empty description="Список пуст" />}} 
                                                        className="organization_list"
                                                        dataSource={obj.locations_search}
                                                        pagination={{
                                                            defaultPageSize: 5, 
                                                            hideOnSinglePage: true,
                                                        }}
                                                        renderItem={(item) => (
                                                            <List.Item
                                                                key={item.id}
                                                                actions={[
                                                                    <Button className='submit_mini' loading={locations.loading_search} onClick={() => organization_location_select({
                                                                        address:item.address,
                                                                        latitude:item.latitude,
                                                                        longitude:item.longitude
                                                                    })} >Выбрать</Button>
                                                                ]}
                                                            >
                                                                <span className="orgname">{item?.address}</span>
                                                            </List.Item>
                                                        )}
                                                    />
                                                    <div className="map" style={{height:'fit-content'}}>
                                                        <YMaps style={{marginTop:20}}>
                                                            <Map defaultState={{ center: ["56.328259", "44.003193"], zoom: 9 }} height="400px" width="100%" >
                                                                {obj.locations_search.length > 0 ? 
                                                                    obj.locations_search.map((item, index) => 
                                                                        <Placemark key={index} geometry={[item.latitude, item.longitude]} />
                                                                    )
                                                                :
                                                                    <Placemark key="global" geometry={[organization.organization_id.latitude, organization.organization_id.longitude]} />
                                                                }
                                                            </Map>
                                                        </YMaps>
                                                    </div>
                                                </>
                                            }
                                        </div>
                                    },
                                    {
                                        key: 'phones',
                                        label: `Телефоны регистратуры`,
                                        children: 
                                        <div>
                                            <List
                                                locale={{ emptyText: <Empty description="Список пуст" />}} 
                                                itemLayout="horizontal"
                                                dataSource={organization.organization_id.phones}
                                                pagination={{
                                                    defaultPageSize: 10, 
                                                    hideOnSinglePage: true,
                                                }}
                                                renderItem={item => (
                                                <List.Item actions={[
                                                    <FiEdit style={{color:'#faad14'}} onClick={() => show_tab_organization_phone({
                                                        id:item.id,
                                                        phone:item.phone,
                                                        more:item.more
                                                    })} />,
                                                    <Popconfirm placement="topRight" onConfirm={() => clickDelOrganizationPhone({
                                                        id:item.id
                                                    })} 
                                                        title={
                                                            <Space direction="vertical">
                                                                <Text>Вы действительно хотите удалить данный номер?</Text>
                                                            </Space>
                                                        } 
                                                    okText="Да" cancelText="Нет">
                                                        <FiTrash2 style={{color:'#f5222d',cursor:'pointer'}} />
                                                    </Popconfirm>
                                                ]}>
                                                    <List.Item.Meta
                                                        title={`${customPhoneMask(item.phone)} ${item.more ? `(доб. ${item.more})`:''}`}
                                                    />
                                                </List.Item>
                                                )}
                                            />
                                            <div className="fcenter">
                                                <Button className="submit_mini" icon={<PlusOutlined />} onClick={() => clickAddOrganizationPhone()} />
                                            </div>
                                        </div>
                                    },
                                    {
                                        key: 'worktime',
                                        label: `Режим работы`,
                                        children: 
                                        <div>
                                            <List
                                                locale={{ emptyText: <Empty description="Список пуст" />}} 
                                                itemLayout="horizontal"
                                                dataSource={organization.organization_id.worktime}
                                                pagination={{
                                                    defaultPageSize: 10, 
                                                    hideOnSinglePage: true,
                                                }}
                                                renderItem={item => (
                                                <List.Item actions={[
                                                    <FiEdit style={{color:'#faad14'}} onClick={() => show_tab_organization_worktime({
                                                        id:item.id,
                                                        day:item.day,
                                                        text:item.text,
                                                        type:item.type
                                                    })} />,
                                                    <Popconfirm placement="topRight" onConfirm={() => clickDelOrganizationWorktime({
                                                        id:item.id
                                                    })} 
                                                        title={
                                                            <Space direction="vertical">
                                                                <Text>Вы действительно хотите удалить данный пункт?</Text>
                                                            </Space>
                                                        } 
                                                    okText="Да" cancelText="Нет">
                                                        <FiTrash2 style={{color:'#f5222d',cursor:'pointer'}} />
                                                    </Popconfirm>
                                                ]}>
                                                    <List.Item.Meta
                                                        title={
                                                            <span><b>{item.day ? item.day : 'День/Заголовок не заполнен'}</b> {item.text ? item.text : 'время/описание не заполнено'}</span>
                                                        }
                                                    />
                                                </List.Item>
                                                )}
                                            />
                                            <div className="fcenter">
                                                <Button className="submit_mini" icon={<PlusOutlined />} onClick={() => clickAddOrganizationWorktime()} />
                                            </div>
                                        </div>
                                    },
                                    {
                                        key: 'worktime_admin',
                                        label: `Режим работы администрации`,
                                        children: 
                                        <div>
                                            <List
                                                locale={{ emptyText: <Empty description="Список пуст" />}} 
                                                itemLayout="horizontal"
                                                dataSource={organization.organization_id.worktime_admin}
                                                pagination={{
                                                    defaultPageSize: 10, 
                                                    hideOnSinglePage: true,
                                                }}
                                                renderItem={item => (
                                                <List.Item actions={[
                                                    <FiEdit style={{color:'#faad14'}} onClick={() => show_tab_organization_worktime_admin({
                                                        id:item.id,
                                                        day:item.day,
                                                        text:item.text,
                                                        type:item.type
                                                    })} />,
                                                    <Popconfirm placement="topRight" onConfirm={() => clickDelOrganizationWorktime_admin({
                                                        id:item.id
                                                    })} 
                                                        title={
                                                            <Space direction="vertical">
                                                                <Text>Вы действительно хотите удалить данный пункт?</Text>
                                                            </Space>
                                                        } 
                                                    okText="Да" cancelText="Нет">
                                                        <FiTrash2 style={{color:'#f5222d',cursor:'pointer'}} />
                                                    </Popconfirm>
                                                ]}>
                                                    <List.Item.Meta
                                                        title={
                                                            <span><b>{item.day ? item.day : 'День/Заголовок не заполнен'}</b> {item.text ? item.text : 'время/описание не заполнено'}</span>
                                                        }
                                                    />
                                                </List.Item>
                                                )}
                                            />
                                            <div className="fcenter">
                                                <Button className="submit_mini" icon={<PlusOutlined />} onClick={() => clickAddOrganizationWorktime_admin()} />
                                            </div>
                                        </div>
                                    },
                                    {
                                        key: 'services',
                                        label: `ТОП-10 услуг`,
                                        disabled: ["5"].indexOf( organization.organization_id?.type ) === -1,
                                        children: 
                                        <div>
                                            <List
                                                locale={{ emptyText: <Empty description="Список пуст" />}} 
                                                itemLayout="horizontal"
                                                dataSource={organization.organization_id.services}
                                                pagination={{
                                                    defaultPageSize: 10, 
                                                    hideOnSinglePage: true,
                                                }}
                                                renderItem={item => (
                                                <List.Item actions={[
                                                    <FiEdit style={{color:'#faad14'}} onClick={() => show_tab_organization_services({
                                                        id:item.id,
                                                        name:item.name,
                                                        amount:item.amount
                                                    })} />,
                                                    <Popconfirm placement="topRight" onConfirm={() => clickDelOrganizationServices({
                                                        id:item.id
                                                    })} 
                                                        title={
                                                            <Space direction="vertical">
                                                                <Text>Вы действительно хотите удалить данный пункт?</Text>
                                                            </Space>
                                                        } 
                                                    okText="Да" cancelText="Нет">
                                                        <FiTrash2 style={{color:'#f5222d',cursor:'pointer'}} />
                                                    </Popconfirm>
                                                ]}>
                                                    <List.Item.Meta
                                                        title={
                                                            <span><b>{item.name ? item.name : 'Название не заполнено'}</b> {item.amount ? item.amount : 'стоимость не заполнена'}</span>
                                                        }
                                                    />
                                                </List.Item>
                                                )}
                                            />
                                            <div className="fcenter">
                                                <Button className="submit_mini" icon={<PlusOutlined />} onClick={() => clickAddOrganizationServices()} />
                                            </div>
                                        </div>
                                    },
                                    {
                                        key: 'logo',
                                        label: `Логотип`,
                                        disabled: ["4","5"].indexOf( organization.organization_id?.type ) === -1,
                                        children: 
                                        <div>
                                            <Form className="default_form">
                                                <div className="fcenter images_column">
                                                    {organization.organization_id?.logo === null ?
                                                        null
                                                    :
                                                        <div className='image_preview'>
                                                            <Image
                                                                className="image-preview"
                                                                height={100}
                                                                width={100}
                                                                src={`${UPLOADS}/${organization.organization_id?.logo}`}
                                                                preview={{
                                                                    src: `${UPLOADS}/${organization.organization_id?.logo}`
                                                                }}
                                                            />
                                                            <div className="float_remove">
                                                                <Popconfirm placement="topRight" onConfirm={() => organization_logo_delete()} 
                                                                    title={
                                                                        <Space direction="vertical">
                                                                            <Text>Вы действительно хотите удалить данный логотип?</Text>
                                                                        </Space>
                                                                    } 
                                                                okText="Да" cancelText="Нет">
                                                                    <FiTrash2 style={{color:'#f5222d',cursor:'pointer'}} />
                                                                </Popconfirm>
                                                            </div>
                                                        </div>
                                                    }
                                                    <Dragger
                                                        className="no-list"
                                                        {...upload_config_one_png_image}
                                                        {...upload_organization_image}
                                                        data={{'method':'upload_organizations_logo','token':profile?.token,'organization_id':organization.organization_id?.id}}
                                                    >
                                                        <div className='upload_content'>
                                                            <div className='left'>
                                                                <svg width="42" height="36" viewBox="0 0 42 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                    <path d="M36.2727 15.1181C35.7664 15.1181 35.2808 15.3172 34.9228 15.6716C34.5648 16.026 34.3636 16.5067 34.3636 17.0079V30.2362C34.3636 30.7374 34.1625 31.2181 33.8045 31.5725C33.4465 31.9269 32.9609 32.126 32.4545 32.126H5.72727C5.22095 32.126 4.73537 31.9269 4.37734 31.5725C4.01932 31.2181 3.81818 30.7374 3.81818 30.2362V15.1181C3.81818 14.6169 4.01932 14.1362 4.37734 13.7818C4.73537 13.4274 5.22095 13.2283 5.72727 13.2283H9.54545C9.96176 13.2499 10.3737 13.1359 10.7185 12.9038C11.0632 12.6718 11.3217 12.3345 11.4545 11.9433L12.4855 8.84409C12.6135 8.46875 12.8575 8.14274 13.183 7.91214C13.5084 7.68155 13.8989 7.55803 14.2991 7.55906H24.8182C25.3245 7.55906 25.8101 7.35996 26.1681 7.00556C26.5261 6.65116 26.7273 6.17049 26.7273 5.66929C26.7273 5.1681 26.5261 4.68743 26.1681 4.33303C25.8101 3.97863 25.3245 3.77953 24.8182 3.77953H14.2036C13.0039 3.78175 11.8351 4.15688 10.8623 4.85198C9.88947 5.54708 9.16174 6.52703 8.78182 7.65354L8.17091 9.54331H5.72727C4.20831 9.54331 2.75155 10.1406 1.67748 11.2038C0.603407 12.267 0 13.709 0 15.2126V30.3307C0 31.8343 0.603407 33.2763 1.67748 34.3395C2.75155 35.4027 4.20831 36 5.72727 36H32.4545C33.9735 36 35.4303 35.4027 36.5043 34.3395C37.5784 33.2763 38.1818 31.8343 38.1818 30.3307V17.1024C38.1948 16.8464 38.155 16.5904 38.0648 16.3501C37.9747 16.1098 37.8361 15.8902 37.6576 15.7046C37.479 15.519 37.2641 15.3713 37.026 15.2704C36.788 15.1696 36.5317 15.1178 36.2727 15.1181ZM19.0909 13.2283C17.5806 13.2283 16.1042 13.6717 14.8484 14.5023C13.5926 15.3329 12.6138 16.5134 12.0358 17.8947C11.4579 19.2759 11.3066 20.7958 11.6013 22.2621C11.8959 23.7284 12.6232 25.0753 13.6912 26.1325C14.7591 27.1896 16.1198 27.9095 17.6011 28.2012C19.0824 28.4929 20.6179 28.3432 22.0132 27.7711C23.4086 27.1989 24.6012 26.2301 25.4403 24.987C26.2794 23.7439 26.7273 22.2824 26.7273 20.7874C26.7273 18.7826 25.9227 16.8599 24.4906 15.4423C23.0585 14.0247 21.1162 13.2283 19.0909 13.2283ZM19.0909 24.5669C18.3357 24.5669 17.5975 24.3453 16.9696 23.93C16.3417 23.5147 15.8524 22.9244 15.5634 22.2338C15.2744 21.5431 15.1988 20.7832 15.3461 20.0501C15.4934 19.3169 15.8571 18.6434 16.391 18.1149C16.925 17.5863 17.6054 17.2263 18.346 17.0805C19.0867 16.9347 19.8544 17.0095 20.5521 17.2956C21.2497 17.5816 21.8461 18.0661 22.2656 18.6876C22.6852 19.3091 22.9091 20.0399 22.9091 20.7874C22.9091 21.7898 22.5068 22.7511 21.7908 23.4599C21.0747 24.1687 20.1036 24.5669 19.0909 24.5669ZM40.0909 3.77953H38.1818V1.88976C38.1818 1.38857 37.9807 0.907898 37.6227 0.553499C37.2646 0.199099 36.779 0 36.2727 0C35.7664 0 35.2808 0.199099 34.9228 0.553499C34.5648 0.907898 34.3636 1.38857 34.3636 1.88976V3.77953H32.4545C31.9482 3.77953 31.4626 3.97863 31.1046 4.33303C30.7466 4.68743 30.5455 5.1681 30.5455 5.66929C30.5455 6.17049 30.7466 6.65116 31.1046 7.00556C31.4626 7.35996 31.9482 7.55906 32.4545 7.55906H34.3636V9.44882C34.3636 9.95002 34.5648 10.4307 34.9228 10.7851C35.2808 11.1395 35.7664 11.3386 36.2727 11.3386C36.779 11.3386 37.2646 11.1395 37.6227 10.7851C37.9807 10.4307 38.1818 9.95002 38.1818 9.44882V7.55906H40.0909C40.5972 7.55906 41.0828 7.35996 41.4408 7.00556C41.7989 6.65116 42 6.17049 42 5.66929C42 5.1681 41.7989 4.68743 41.4408 4.33303C41.0828 3.97863 40.5972 3.77953 40.0909 3.77953Z" fill="#9B9B9B"/>
                                                                </svg>
                                                            </div>
                                                            <div className='right'>
                                                                [Логотип] Нажмите на кнопку или перетащите фото в эту область в формате PNG.
                                                            </div>
                                                        </div>
                                                    </Dragger>
                                                </div>
                                            </Form>
                                        </div>
                                    },
                                    {
                                        key: 'header',
                                        label: `Изображение в шапке`,
                                        disabled: ["5"].indexOf( organization.organization_id?.type ) === -1,
                                        children: 
                                        <div>
                                            <Form className="default_form">
                                                <div className="fcenter images_column">
                                                    {organization.organization_id?.image_header_mob === null ?
                                                        null
                                                    :
                                                        <div className='image_preview'>
                                                            <Image
                                                                className="image-preview"
                                                                height={100}
                                                                width={100}
                                                                src={`${UPLOADS}/${organization.organization_id?.image_header_mob_preview}`}
                                                                preview={{
                                                                    src: `${UPLOADS}/${organization.organization_id?.image_header_mob}`
                                                                }}
                                                            /> 
                                                            <div className="float_remove">
                                                                <Popconfirm placement="topRight" onConfirm={() => organizations_image_header_mob_delete()} 
                                                                    title={
                                                                        <Space direction="vertical">
                                                                            <Text>Вы действительно хотите удалить данное изображение?</Text>
                                                                        </Space>
                                                                    } 
                                                                okText="Да" cancelText="Нет">
                                                                    <FiTrash2 style={{color:'#f5222d',cursor:'pointer'}} />
                                                                </Popconfirm>
                                                            </div>
                                                        </div>
                                                    }
                                                    <ImgCrop quality={1} aspect={1.457} modalTitle="Загрузка изображения для мобильной версии" modalOk="Сохранить" modalCancel="Отмена" >
                                                        <Dragger
                                                            className="no-list"
                                                            {...upload_config_one_image}
                                                            {...upload_organization_image}
                                                            data={{'method':'upload_organizations_header_mob','token':profile?.token,'organization_id':organization.organization_id?.id}}
                                                        >
                                                            <div className='upload_content'>
                                                                <div className='left'>
                                                                    <svg width="42" height="36" viewBox="0 0 42 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                        <path d="M36.2727 15.1181C35.7664 15.1181 35.2808 15.3172 34.9228 15.6716C34.5648 16.026 34.3636 16.5067 34.3636 17.0079V30.2362C34.3636 30.7374 34.1625 31.2181 33.8045 31.5725C33.4465 31.9269 32.9609 32.126 32.4545 32.126H5.72727C5.22095 32.126 4.73537 31.9269 4.37734 31.5725C4.01932 31.2181 3.81818 30.7374 3.81818 30.2362V15.1181C3.81818 14.6169 4.01932 14.1362 4.37734 13.7818C4.73537 13.4274 5.22095 13.2283 5.72727 13.2283H9.54545C9.96176 13.2499 10.3737 13.1359 10.7185 12.9038C11.0632 12.6718 11.3217 12.3345 11.4545 11.9433L12.4855 8.84409C12.6135 8.46875 12.8575 8.14274 13.183 7.91214C13.5084 7.68155 13.8989 7.55803 14.2991 7.55906H24.8182C25.3245 7.55906 25.8101 7.35996 26.1681 7.00556C26.5261 6.65116 26.7273 6.17049 26.7273 5.66929C26.7273 5.1681 26.5261 4.68743 26.1681 4.33303C25.8101 3.97863 25.3245 3.77953 24.8182 3.77953H14.2036C13.0039 3.78175 11.8351 4.15688 10.8623 4.85198C9.88947 5.54708 9.16174 6.52703 8.78182 7.65354L8.17091 9.54331H5.72727C4.20831 9.54331 2.75155 10.1406 1.67748 11.2038C0.603407 12.267 0 13.709 0 15.2126V30.3307C0 31.8343 0.603407 33.2763 1.67748 34.3395C2.75155 35.4027 4.20831 36 5.72727 36H32.4545C33.9735 36 35.4303 35.4027 36.5043 34.3395C37.5784 33.2763 38.1818 31.8343 38.1818 30.3307V17.1024C38.1948 16.8464 38.155 16.5904 38.0648 16.3501C37.9747 16.1098 37.8361 15.8902 37.6576 15.7046C37.479 15.519 37.2641 15.3713 37.026 15.2704C36.788 15.1696 36.5317 15.1178 36.2727 15.1181ZM19.0909 13.2283C17.5806 13.2283 16.1042 13.6717 14.8484 14.5023C13.5926 15.3329 12.6138 16.5134 12.0358 17.8947C11.4579 19.2759 11.3066 20.7958 11.6013 22.2621C11.8959 23.7284 12.6232 25.0753 13.6912 26.1325C14.7591 27.1896 16.1198 27.9095 17.6011 28.2012C19.0824 28.4929 20.6179 28.3432 22.0132 27.7711C23.4086 27.1989 24.6012 26.2301 25.4403 24.987C26.2794 23.7439 26.7273 22.2824 26.7273 20.7874C26.7273 18.7826 25.9227 16.8599 24.4906 15.4423C23.0585 14.0247 21.1162 13.2283 19.0909 13.2283ZM19.0909 24.5669C18.3357 24.5669 17.5975 24.3453 16.9696 23.93C16.3417 23.5147 15.8524 22.9244 15.5634 22.2338C15.2744 21.5431 15.1988 20.7832 15.3461 20.0501C15.4934 19.3169 15.8571 18.6434 16.391 18.1149C16.925 17.5863 17.6054 17.2263 18.346 17.0805C19.0867 16.9347 19.8544 17.0095 20.5521 17.2956C21.2497 17.5816 21.8461 18.0661 22.2656 18.6876C22.6852 19.3091 22.9091 20.0399 22.9091 20.7874C22.9091 21.7898 22.5068 22.7511 21.7908 23.4599C21.0747 24.1687 20.1036 24.5669 19.0909 24.5669ZM40.0909 3.77953H38.1818V1.88976C38.1818 1.38857 37.9807 0.907898 37.6227 0.553499C37.2646 0.199099 36.779 0 36.2727 0C35.7664 0 35.2808 0.199099 34.9228 0.553499C34.5648 0.907898 34.3636 1.38857 34.3636 1.88976V3.77953H32.4545C31.9482 3.77953 31.4626 3.97863 31.1046 4.33303C30.7466 4.68743 30.5455 5.1681 30.5455 5.66929C30.5455 6.17049 30.7466 6.65116 31.1046 7.00556C31.4626 7.35996 31.9482 7.55906 32.4545 7.55906H34.3636V9.44882C34.3636 9.95002 34.5648 10.4307 34.9228 10.7851C35.2808 11.1395 35.7664 11.3386 36.2727 11.3386C36.779 11.3386 37.2646 11.1395 37.6227 10.7851C37.9807 10.4307 38.1818 9.95002 38.1818 9.44882V7.55906H40.0909C40.5972 7.55906 41.0828 7.35996 41.4408 7.00556C41.7989 6.65116 42 6.17049 42 5.66929C42 5.1681 41.7989 4.68743 41.4408 4.33303C41.0828 3.97863 40.5972 3.77953 40.0909 3.77953Z" fill="#9B9B9B"/>
                                                                    </svg>
                                                                </div>
                                                                <div className='right'>
                                                                    [Мобильная версия] Нажмите на кнопку или перетащите фото в эту область в формате PNG, JPEG.
                                                                </div>
                                                            </div>
                                                        </Dragger>
                                                    </ImgCrop>
                                                </div>
                                                <div className="fcenter images_column">
                                                    {organization.organization_id?.image_header_pc === null ?
                                                        null
                                                    :
                                                        <div className='image_preview'>
                                                            <Image
                                                                className="image-preview"
                                                                height={100}
                                                                width={100}
                                                                src={`${UPLOADS}/${organization.organization_id?.image_header_pc_preview}`}
                                                                preview={{
                                                                    src: `${UPLOADS}/${organization.organization_id?.image_header_pc}`
                                                                }}
                                                            />
                                                            <div className="float_remove">
                                                                <Popconfirm placement="topRight" onConfirm={() => organizations_image_header_pc_delete()} 
                                                                    title={
                                                                        <Space direction="vertical">
                                                                            <Text>Вы действительно хотите удалить данное изображение?</Text>
                                                                        </Space>
                                                                    } 
                                                                okText="Да" cancelText="Нет">
                                                                    <FiTrash2 style={{color:'#f5222d',cursor:'pointer'}} />
                                                                </Popconfirm>
                                                            </div>
                                                        </div>
                                                    }
                                                    <ImgCrop quality={1} aspect={3.2} modalTitle="Загрузка изображения для компьютерной версии" modalOk="Сохранить" modalCancel="Отмена" >
                                                        <Dragger
                                                            className="no-list"
                                                            {...upload_config_one_image}
                                                            {...upload_organization_image}
                                                            data={{'method':'upload_organizations_header_pc','token':profile?.token,'organization_id':organization.organization_id?.id}}
                                                        >
                                                            <div className='upload_content'>
                                                                <div className='left'>
                                                                    <svg width="42" height="36" viewBox="0 0 42 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                        <path d="M36.2727 15.1181C35.7664 15.1181 35.2808 15.3172 34.9228 15.6716C34.5648 16.026 34.3636 16.5067 34.3636 17.0079V30.2362C34.3636 30.7374 34.1625 31.2181 33.8045 31.5725C33.4465 31.9269 32.9609 32.126 32.4545 32.126H5.72727C5.22095 32.126 4.73537 31.9269 4.37734 31.5725C4.01932 31.2181 3.81818 30.7374 3.81818 30.2362V15.1181C3.81818 14.6169 4.01932 14.1362 4.37734 13.7818C4.73537 13.4274 5.22095 13.2283 5.72727 13.2283H9.54545C9.96176 13.2499 10.3737 13.1359 10.7185 12.9038C11.0632 12.6718 11.3217 12.3345 11.4545 11.9433L12.4855 8.84409C12.6135 8.46875 12.8575 8.14274 13.183 7.91214C13.5084 7.68155 13.8989 7.55803 14.2991 7.55906H24.8182C25.3245 7.55906 25.8101 7.35996 26.1681 7.00556C26.5261 6.65116 26.7273 6.17049 26.7273 5.66929C26.7273 5.1681 26.5261 4.68743 26.1681 4.33303C25.8101 3.97863 25.3245 3.77953 24.8182 3.77953H14.2036C13.0039 3.78175 11.8351 4.15688 10.8623 4.85198C9.88947 5.54708 9.16174 6.52703 8.78182 7.65354L8.17091 9.54331H5.72727C4.20831 9.54331 2.75155 10.1406 1.67748 11.2038C0.603407 12.267 0 13.709 0 15.2126V30.3307C0 31.8343 0.603407 33.2763 1.67748 34.3395C2.75155 35.4027 4.20831 36 5.72727 36H32.4545C33.9735 36 35.4303 35.4027 36.5043 34.3395C37.5784 33.2763 38.1818 31.8343 38.1818 30.3307V17.1024C38.1948 16.8464 38.155 16.5904 38.0648 16.3501C37.9747 16.1098 37.8361 15.8902 37.6576 15.7046C37.479 15.519 37.2641 15.3713 37.026 15.2704C36.788 15.1696 36.5317 15.1178 36.2727 15.1181ZM19.0909 13.2283C17.5806 13.2283 16.1042 13.6717 14.8484 14.5023C13.5926 15.3329 12.6138 16.5134 12.0358 17.8947C11.4579 19.2759 11.3066 20.7958 11.6013 22.2621C11.8959 23.7284 12.6232 25.0753 13.6912 26.1325C14.7591 27.1896 16.1198 27.9095 17.6011 28.2012C19.0824 28.4929 20.6179 28.3432 22.0132 27.7711C23.4086 27.1989 24.6012 26.2301 25.4403 24.987C26.2794 23.7439 26.7273 22.2824 26.7273 20.7874C26.7273 18.7826 25.9227 16.8599 24.4906 15.4423C23.0585 14.0247 21.1162 13.2283 19.0909 13.2283ZM19.0909 24.5669C18.3357 24.5669 17.5975 24.3453 16.9696 23.93C16.3417 23.5147 15.8524 22.9244 15.5634 22.2338C15.2744 21.5431 15.1988 20.7832 15.3461 20.0501C15.4934 19.3169 15.8571 18.6434 16.391 18.1149C16.925 17.5863 17.6054 17.2263 18.346 17.0805C19.0867 16.9347 19.8544 17.0095 20.5521 17.2956C21.2497 17.5816 21.8461 18.0661 22.2656 18.6876C22.6852 19.3091 22.9091 20.0399 22.9091 20.7874C22.9091 21.7898 22.5068 22.7511 21.7908 23.4599C21.0747 24.1687 20.1036 24.5669 19.0909 24.5669ZM40.0909 3.77953H38.1818V1.88976C38.1818 1.38857 37.9807 0.907898 37.6227 0.553499C37.2646 0.199099 36.779 0 36.2727 0C35.7664 0 35.2808 0.199099 34.9228 0.553499C34.5648 0.907898 34.3636 1.38857 34.3636 1.88976V3.77953H32.4545C31.9482 3.77953 31.4626 3.97863 31.1046 4.33303C30.7466 4.68743 30.5455 5.1681 30.5455 5.66929C30.5455 6.17049 30.7466 6.65116 31.1046 7.00556C31.4626 7.35996 31.9482 7.55906 32.4545 7.55906H34.3636V9.44882C34.3636 9.95002 34.5648 10.4307 34.9228 10.7851C35.2808 11.1395 35.7664 11.3386 36.2727 11.3386C36.779 11.3386 37.2646 11.1395 37.6227 10.7851C37.9807 10.4307 38.1818 9.95002 38.1818 9.44882V7.55906H40.0909C40.5972 7.55906 41.0828 7.35996 41.4408 7.00556C41.7989 6.65116 42 6.17049 42 5.66929C42 5.1681 41.7989 4.68743 41.4408 4.33303C41.0828 3.97863 40.5972 3.77953 40.0909 3.77953Z" fill="#9B9B9B"/>
                                                                    </svg>
                                                                </div>
                                                                <div className='right'>
                                                                    [Компьютерная версия] Нажмите на кнопку или перетащите фото в эту область в формате PNG, JPEG.
                                                                </div>
                                                            </div>
                                                        </Dragger>
                                                    </ImgCrop>
                                                </div>
                                            </Form>
                                        </div>
                                    },
                                    {
                                        key: 'photos',
                                        label: `Фотографии`,
                                        disabled: ["3","4","5"].indexOf( organization.organization_id?.type ) === -1,
                                        children: 
                                        <div>
                                            <List
                                                locale={{ emptyText: <Empty description="Список пуст" />}} 
                                                itemLayout="horizontal"
                                                dataSource={organization.organization_id.photos}
                                                pagination={{
                                                    defaultPageSize: 10, 
                                                    hideOnSinglePage: true,
                                                }}
                                                renderItem={item => (
                                                <List.Item actions={[
                                                    <Popconfirm placement="topRight" onConfirm={() => clickDelOrganizationPhotos({
                                                        id:item.id
                                                    })} 
                                                        title={
                                                            <Space direction="vertical">
                                                                <Text>Вы действительно хотите удалить данное изображение?</Text>
                                                            </Space>
                                                        } 
                                                    okText="Да" cancelText="Нет">
                                                        <FiTrash2 style={{color:'#f5222d',cursor:'pointer'}} />
                                                    </Popconfirm>
                                                ]}>
                                                    <List.Item.Meta
                                                        title={
                                                            <span className="item-list-items">
                                                                <Image
                                                                    className="image-preview"
                                                                    height={50}
                                                                    width={50}
                                                                    src={`${UPLOADS}/${item?.file_preview}`}
                                                                    preview={{
                                                                        src: `${UPLOADS}/${item?.file}`
                                                                    }}
                                                                /> 
                                                                Добавлено {moment.unix(item?.date).fromNow()}
                                                            </span>
                                                        }
                                                    />
                                                </List.Item>
                                                )}
                                            />
                                            <div className="fcenter">
                                                <Dragger
                                                    className="no-list"
                                                    {...upload_config_multi_image}
                                                    {...upload_organization_image}
                                                    data={{'method':'upload_organizations_photos','token':profile?.token,'organization_id':organization.organization_id?.id}}
                                                >
                                                    <div className='upload_content'>
                                                        <div className='left'>
                                                            <svg width="42" height="36" viewBox="0 0 42 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                <path d="M36.2727 15.1181C35.7664 15.1181 35.2808 15.3172 34.9228 15.6716C34.5648 16.026 34.3636 16.5067 34.3636 17.0079V30.2362C34.3636 30.7374 34.1625 31.2181 33.8045 31.5725C33.4465 31.9269 32.9609 32.126 32.4545 32.126H5.72727C5.22095 32.126 4.73537 31.9269 4.37734 31.5725C4.01932 31.2181 3.81818 30.7374 3.81818 30.2362V15.1181C3.81818 14.6169 4.01932 14.1362 4.37734 13.7818C4.73537 13.4274 5.22095 13.2283 5.72727 13.2283H9.54545C9.96176 13.2499 10.3737 13.1359 10.7185 12.9038C11.0632 12.6718 11.3217 12.3345 11.4545 11.9433L12.4855 8.84409C12.6135 8.46875 12.8575 8.14274 13.183 7.91214C13.5084 7.68155 13.8989 7.55803 14.2991 7.55906H24.8182C25.3245 7.55906 25.8101 7.35996 26.1681 7.00556C26.5261 6.65116 26.7273 6.17049 26.7273 5.66929C26.7273 5.1681 26.5261 4.68743 26.1681 4.33303C25.8101 3.97863 25.3245 3.77953 24.8182 3.77953H14.2036C13.0039 3.78175 11.8351 4.15688 10.8623 4.85198C9.88947 5.54708 9.16174 6.52703 8.78182 7.65354L8.17091 9.54331H5.72727C4.20831 9.54331 2.75155 10.1406 1.67748 11.2038C0.603407 12.267 0 13.709 0 15.2126V30.3307C0 31.8343 0.603407 33.2763 1.67748 34.3395C2.75155 35.4027 4.20831 36 5.72727 36H32.4545C33.9735 36 35.4303 35.4027 36.5043 34.3395C37.5784 33.2763 38.1818 31.8343 38.1818 30.3307V17.1024C38.1948 16.8464 38.155 16.5904 38.0648 16.3501C37.9747 16.1098 37.8361 15.8902 37.6576 15.7046C37.479 15.519 37.2641 15.3713 37.026 15.2704C36.788 15.1696 36.5317 15.1178 36.2727 15.1181ZM19.0909 13.2283C17.5806 13.2283 16.1042 13.6717 14.8484 14.5023C13.5926 15.3329 12.6138 16.5134 12.0358 17.8947C11.4579 19.2759 11.3066 20.7958 11.6013 22.2621C11.8959 23.7284 12.6232 25.0753 13.6912 26.1325C14.7591 27.1896 16.1198 27.9095 17.6011 28.2012C19.0824 28.4929 20.6179 28.3432 22.0132 27.7711C23.4086 27.1989 24.6012 26.2301 25.4403 24.987C26.2794 23.7439 26.7273 22.2824 26.7273 20.7874C26.7273 18.7826 25.9227 16.8599 24.4906 15.4423C23.0585 14.0247 21.1162 13.2283 19.0909 13.2283ZM19.0909 24.5669C18.3357 24.5669 17.5975 24.3453 16.9696 23.93C16.3417 23.5147 15.8524 22.9244 15.5634 22.2338C15.2744 21.5431 15.1988 20.7832 15.3461 20.0501C15.4934 19.3169 15.8571 18.6434 16.391 18.1149C16.925 17.5863 17.6054 17.2263 18.346 17.0805C19.0867 16.9347 19.8544 17.0095 20.5521 17.2956C21.2497 17.5816 21.8461 18.0661 22.2656 18.6876C22.6852 19.3091 22.9091 20.0399 22.9091 20.7874C22.9091 21.7898 22.5068 22.7511 21.7908 23.4599C21.0747 24.1687 20.1036 24.5669 19.0909 24.5669ZM40.0909 3.77953H38.1818V1.88976C38.1818 1.38857 37.9807 0.907898 37.6227 0.553499C37.2646 0.199099 36.779 0 36.2727 0C35.7664 0 35.2808 0.199099 34.9228 0.553499C34.5648 0.907898 34.3636 1.38857 34.3636 1.88976V3.77953H32.4545C31.9482 3.77953 31.4626 3.97863 31.1046 4.33303C30.7466 4.68743 30.5455 5.1681 30.5455 5.66929C30.5455 6.17049 30.7466 6.65116 31.1046 7.00556C31.4626 7.35996 31.9482 7.55906 32.4545 7.55906H34.3636V9.44882C34.3636 9.95002 34.5648 10.4307 34.9228 10.7851C35.2808 11.1395 35.7664 11.3386 36.2727 11.3386C36.779 11.3386 37.2646 11.1395 37.6227 10.7851C37.9807 10.4307 38.1818 9.95002 38.1818 9.44882V7.55906H40.0909C40.5972 7.55906 41.0828 7.35996 41.4408 7.00556C41.7989 6.65116 42 6.17049 42 5.66929C42 5.1681 41.7989 4.68743 41.4408 4.33303C41.0828 3.97863 40.5972 3.77953 40.0909 3.77953Z" fill="#9B9B9B"/>
                                                            </svg>
                                                        </div>
                                                        <div className='right'>
                                                            Нажмите на кнопку или перетащите фото в эту область до 10 изображений в формате PNG, JPEG.
                                                        </div>
                                                    </div>
                                                </Dragger>
                                            </div>
                                        </div>
                                    },
                                    {
                                        key: 'certificates',
                                        label: `Сертификаты`,
                                        disabled: ["3","4","5"].indexOf( organization.organization_id?.type ) === -1,
                                        children: 
                                        <div>
                                            <List
                                                locale={{ emptyText: <Empty description="Список пуст" />}} 
                                                itemLayout="horizontal"
                                                dataSource={organization.organization_id.certificates}
                                                pagination={{
                                                    defaultPageSize: 10, 
                                                    hideOnSinglePage: true,
                                                }}
                                                renderItem={item => (
                                                <List.Item actions={[
                                                    <Popconfirm placement="topRight" onConfirm={() => clickDelOrganizationCertificates({
                                                        id:item.id
                                                    })} 
                                                        title={
                                                            <Space direction="vertical">
                                                                <Text>Вы действительно хотите удалить данное изображение?</Text>
                                                            </Space>
                                                        } 
                                                    okText="Да" cancelText="Нет">
                                                        <FiTrash2 style={{color:'#f5222d',cursor:'pointer'}} />
                                                    </Popconfirm>
                                                ]}>
                                                    <List.Item.Meta
                                                        title={
                                                            <span className="item-list-items">
                                                                <Image
                                                                    className="image-preview"
                                                                    height={50}
                                                                    width={50}
                                                                    src={`${UPLOADS}/${item?.file_preview}`}
                                                                    preview={{
                                                                        src: `${UPLOADS}/${item?.file}`
                                                                    }}
                                                                /> 
                                                                Добавлено {moment.unix(item?.date).fromNow()}
                                                            </span>
                                                        }
                                                    />
                                                </List.Item>
                                                )}
                                            />
                                            <div className="fcenter">
                                                <Dragger
                                                    className="no-list"
                                                    {...upload_config_multi_image}
                                                    {...upload_organization_image}
                                                    data={{'method':'upload_organizations_certificates','token':profile?.token,'organization_id':organization.organization_id?.id}}
                                                >
                                                    <div className='upload_content'>
                                                        <div className='left'>
                                                            <svg width="42" height="36" viewBox="0 0 42 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                <path d="M36.2727 15.1181C35.7664 15.1181 35.2808 15.3172 34.9228 15.6716C34.5648 16.026 34.3636 16.5067 34.3636 17.0079V30.2362C34.3636 30.7374 34.1625 31.2181 33.8045 31.5725C33.4465 31.9269 32.9609 32.126 32.4545 32.126H5.72727C5.22095 32.126 4.73537 31.9269 4.37734 31.5725C4.01932 31.2181 3.81818 30.7374 3.81818 30.2362V15.1181C3.81818 14.6169 4.01932 14.1362 4.37734 13.7818C4.73537 13.4274 5.22095 13.2283 5.72727 13.2283H9.54545C9.96176 13.2499 10.3737 13.1359 10.7185 12.9038C11.0632 12.6718 11.3217 12.3345 11.4545 11.9433L12.4855 8.84409C12.6135 8.46875 12.8575 8.14274 13.183 7.91214C13.5084 7.68155 13.8989 7.55803 14.2991 7.55906H24.8182C25.3245 7.55906 25.8101 7.35996 26.1681 7.00556C26.5261 6.65116 26.7273 6.17049 26.7273 5.66929C26.7273 5.1681 26.5261 4.68743 26.1681 4.33303C25.8101 3.97863 25.3245 3.77953 24.8182 3.77953H14.2036C13.0039 3.78175 11.8351 4.15688 10.8623 4.85198C9.88947 5.54708 9.16174 6.52703 8.78182 7.65354L8.17091 9.54331H5.72727C4.20831 9.54331 2.75155 10.1406 1.67748 11.2038C0.603407 12.267 0 13.709 0 15.2126V30.3307C0 31.8343 0.603407 33.2763 1.67748 34.3395C2.75155 35.4027 4.20831 36 5.72727 36H32.4545C33.9735 36 35.4303 35.4027 36.5043 34.3395C37.5784 33.2763 38.1818 31.8343 38.1818 30.3307V17.1024C38.1948 16.8464 38.155 16.5904 38.0648 16.3501C37.9747 16.1098 37.8361 15.8902 37.6576 15.7046C37.479 15.519 37.2641 15.3713 37.026 15.2704C36.788 15.1696 36.5317 15.1178 36.2727 15.1181ZM19.0909 13.2283C17.5806 13.2283 16.1042 13.6717 14.8484 14.5023C13.5926 15.3329 12.6138 16.5134 12.0358 17.8947C11.4579 19.2759 11.3066 20.7958 11.6013 22.2621C11.8959 23.7284 12.6232 25.0753 13.6912 26.1325C14.7591 27.1896 16.1198 27.9095 17.6011 28.2012C19.0824 28.4929 20.6179 28.3432 22.0132 27.7711C23.4086 27.1989 24.6012 26.2301 25.4403 24.987C26.2794 23.7439 26.7273 22.2824 26.7273 20.7874C26.7273 18.7826 25.9227 16.8599 24.4906 15.4423C23.0585 14.0247 21.1162 13.2283 19.0909 13.2283ZM19.0909 24.5669C18.3357 24.5669 17.5975 24.3453 16.9696 23.93C16.3417 23.5147 15.8524 22.9244 15.5634 22.2338C15.2744 21.5431 15.1988 20.7832 15.3461 20.0501C15.4934 19.3169 15.8571 18.6434 16.391 18.1149C16.925 17.5863 17.6054 17.2263 18.346 17.0805C19.0867 16.9347 19.8544 17.0095 20.5521 17.2956C21.2497 17.5816 21.8461 18.0661 22.2656 18.6876C22.6852 19.3091 22.9091 20.0399 22.9091 20.7874C22.9091 21.7898 22.5068 22.7511 21.7908 23.4599C21.0747 24.1687 20.1036 24.5669 19.0909 24.5669ZM40.0909 3.77953H38.1818V1.88976C38.1818 1.38857 37.9807 0.907898 37.6227 0.553499C37.2646 0.199099 36.779 0 36.2727 0C35.7664 0 35.2808 0.199099 34.9228 0.553499C34.5648 0.907898 34.3636 1.38857 34.3636 1.88976V3.77953H32.4545C31.9482 3.77953 31.4626 3.97863 31.1046 4.33303C30.7466 4.68743 30.5455 5.1681 30.5455 5.66929C30.5455 6.17049 30.7466 6.65116 31.1046 7.00556C31.4626 7.35996 31.9482 7.55906 32.4545 7.55906H34.3636V9.44882C34.3636 9.95002 34.5648 10.4307 34.9228 10.7851C35.2808 11.1395 35.7664 11.3386 36.2727 11.3386C36.779 11.3386 37.2646 11.1395 37.6227 10.7851C37.9807 10.4307 38.1818 9.95002 38.1818 9.44882V7.55906H40.0909C40.5972 7.55906 41.0828 7.35996 41.4408 7.00556C41.7989 6.65116 42 6.17049 42 5.66929C42 5.1681 41.7989 4.68743 41.4408 4.33303C41.0828 3.97863 40.5972 3.77953 40.0909 3.77953Z" fill="#9B9B9B"/>
                                                            </svg>
                                                        </div>
                                                        <div className='right'>
                                                            Нажмите на кнопку или перетащите фото в эту область до 10 изображений в формате PNG, JPEG.
                                                        </div>
                                                    </div>
                                                </Dragger>
                                            </div>
                                        </div>
                                    },
                                ]} 
                            />
                        </div>
                    }
                </div>
            </Modal>
            <Modal mask={false} className="modal_custom" key="m3" width={900} title="Изменение номера телефона" open={app_global.panel.indexOf( 'organizationPhone' ) != -1 } onOk={() => hide_tab_organization_phone()} onCancel={() => hide_tab_organization_phone()} footer={null}>
                <div className='header'>
                    <h2>Изменение номера телефона</h2>
                    <h3>
                        {Object(organization.organization_id).length > 0 ?
                            <Skeleton.Button style={{marginBottom: '5px', maxWidth: '300px'}} size="small" shape="round" active block />
                        :
                            organization.organization_id?.orgname
                        }
                    </h3>
                </div>
                <div className='custom_divider' />
                <div className='body'>
                    <Form name="form" className="default_form" form={form_organization_phone} onFinish={form_finish_organization_phone} >
                        <Form.Item hidden name={`id`} rules={[{ required: true, message: `Пожалуйста, введите ID номера!` }]}>
                            <Input disabled={organization.loading} className='custom_input' />
                        </Form.Item>
                        <label style={{marginTop:20}}>Телефон</label>
                        <Form.Item name={`phone`} rules={[{ required: true, message: `Пожалуйста, введите номер телефона!` }]}>
                            <MaskedInput
                                disabled={organization.loading}
                                mask={SETTINGS_APP.mask[SETTINGS_APP.project_format]['default']}
                                placeholder={SETTINGS_APP.mask[SETTINGS_APP.project_format]['placeholder']}
                                render={(ref, props) => (
                                    <input className="ant-input custom_input" type="text" aria-required="true" ref={ref} {...props} />
                                )}
                            />
                        </Form.Item>
                        <label style={{marginTop:20}}>Добавочный номер (если есть)</label>
                        <Form.Item name={`more`} >
                            <Input disabled={organization.loading} className='custom_input' />
                        </Form.Item>
                        <div className='form_buttons'>
                            <Button className='submit' htmlType="submit" loading={organization.loading}>Сохранить</Button>
                        </div>
                    </Form>
                </div>
            </Modal>
            <Modal mask={false} className="modal_custom" key="m4" width={900} title="Изменение пункта" open={app_global.panel.indexOf( 'organizationWorktime' ) != -1 } onOk={() => hide_tab_organization_worktime()} onCancel={() => hide_tab_organization_worktime()} footer={null}>
                <div className='header'>
                    <h2>Изменение пункта времени работы</h2>
                    <h3>
                        {Object(organization.organization_id).length > 0 ?
                            <Skeleton.Button style={{marginBottom: '5px', maxWidth: '300px'}} size="small" shape="round" active block />
                        :
                            organization.organization_id?.orgname
                        }
                    </h3>
                </div>
                <div className='custom_divider' />
                <div className='body'>
                    <Form name="form" className="default_form" form={form_organization_worktime} onFinish={form_finish_organization_worktime} >
                        <Form.Item hidden name={`id`} rules={[{ required: true, message: `Пожалуйста, введите ID пункта!` }]}>
                            <Input disabled={organization.loading} className='custom_input' />
                        </Form.Item>
                        <Form.Item name={`type`}rules={[{ required: true, message: `Пожалуйста, выберите тип!` }]} >
                            <Select
                                defaultValue="day"
                                disabled={organization.loading}
                                className="custom_select"
                                placeholder="Выбрать тип"
                                options={[
                                    {value:"title",label:"Заголовок"},
                                    {value:"day",label:"Обычный пункт"},
                                ]}
                            />
                        </Form.Item>
                        <label style={{marginTop:20}}>День/Заголовок</label>
                        <Form.Item name={`day`} rules={[{ required: true, message: `Пожалуйста, введите день недели/заголовок!` }]}>
                            <Input disabled={organization.loading} className='custom_input' placeholder="Пн-Вс  /   Хирургическое отделение" />
                        </Form.Item>
                        <label style={{marginTop:20}}>Время/Описание</label>
                        <Form.Item name={`text`}>
                            <Input disabled={organization.loading} className='custom_input' placeholder="с 9:00 до 21:00    /   Выходной" />
                        </Form.Item>
                        <div className='form_buttons'>
                            <Button className='submit' htmlType="submit" loading={organization.loading}>Сохранить</Button>
                        </div>
                    </Form>
                </div>
            </Modal>
            <Modal mask={false} className="modal_custom" key="m5" width={900} title="Изменение пункта администрации" open={app_global.panel.indexOf( 'organizationWorktime_admin' ) != -1 } onOk={() => hide_tab_organization_worktime_admin()} onCancel={() => hide_tab_organization_worktime_admin()} footer={null}>
                <div className='header'>
                    <h2>Изменение пункта времени работы администрации</h2>
                    <h3>
                        {Object(organization.organization_id).length > 0 ?
                            <Skeleton.Button style={{marginBottom: '5px', maxWidth: '300px'}} size="small" shape="round" active block />
                        :
                            organization.organization_id?.orgname
                        }
                    </h3>
                </div>
                <div className='custom_divider' />
                <div className='body'>
                    <Form name="form" className="default_form" form={form_organization_worktime_admin} onFinish={form_finish_organization_worktime_admin} >
                        <Form.Item hidden name={`id`} rules={[{ required: true, message: `Пожалуйста, введите ID пункта!` }]}>
                            <Input disabled={organization.loading} className='custom_input' />
                        </Form.Item>
                        <label>Статус</label>    
                        <Form.Item name={`type`} >
                            <Select
                                disabled={organization.loading}
                                className="custom_select"
                                placeholder="Выбрать тип"
                                options={[
                                    {value:"title",label:"Заголовок"},
                                    {value:"day",label:"Обычный пункт"},
                                ]}
                            />
                        </Form.Item>
                        <label style={{marginTop:20}}>День/Заголовок</label>
                        <Form.Item name={`day`} rules={[{ required: true, message: `Пожалуйста, введите день недели/заголовок!` }]}>
                            <Input disabled={organization.loading} className='custom_input' placeholder="Пн-Вс  /   Хирургическое отделение" />
                        </Form.Item>
                        <label style={{marginTop:20}}>Время/Описание</label>
                        <Form.Item name={`text`}>
                            <Input disabled={organization.loading} className='custom_input' placeholder="с 9:00 до 21:00    /   Выходной" />
                        </Form.Item>
                        <div className='form_buttons'>
                            <Button className='submit' htmlType="submit" loading={organization.loading}>Сохранить</Button>
                        </div>
                    </Form>
                </div>
            </Modal>
            <Modal mask centered className="modal_custom" key="m6" width={700} title="Подтверждение почты" open={app_global.panel.indexOf( 'confirmProfileEmail' ) != -1 } onOk={() => hide_modal_confirm_profile_email()} onCancel={() => hide_modal_confirm_profile_email()} footer={null}>
                <div className='header'>
                    <h2>Подтверждение почты</h2>
                    <h3>Введите код из письма</h3>
                </div>
                <div className="custom_divider"></div>
                <div className='body'>
                    {profile.auth ?
                        <div className="block_pin">
                            {profile.loading ?
                                <Skeleton.Button style={{marginBottom: '20px',width:260,height:50}} shape="round" active block={false} />
                            :
                                <ReactCodeInput
                                    type="number"
                                    fields={6}
                                    onChange={(e) => form_finish_profile_email_confirm(e)}
                                />
                            }
                        </div>
                        :
                        <>
                            <div className='result'>
                                <>
                                    <h3>Вы не авторизованы</h3>
                                </>
                            </div>
                        </>
                    }
                </div>
                <div className='footer'>
                    
                </div>
            </Modal>
            <Modal mask={false} className="modal_custom modal_review" key="m7" width={900} title="Просмотр отзыва" open={app_global.panel.indexOf( 'reviews' ) != -1 } onOk={() => hide_tab_reviews_item()} onCancel={() => hide_tab_reviews_item()} footer={null}>
                <div className='header'>
                    <h2>Просмотр отзыва</h2>
                    <h3>
                        {Object(reviews.reviews_id).length > 0 ?
                            <Skeleton.Button style={{marginBottom: '5px', maxWidth: '300px'}} size="small" shape="round" active block />
                        :
                            <>{getOrgStatusTag(reviews.reviews_id?.status)} {reviews.reviews_id?.orgname}</>
                        }
                    </h3>
                </div>
                <div className='custom_divider' />
                <div className='body'>
                    {(Object(reviews.reviews_id).length === 0) ?
                            <Skeleton active />
                        :
                        <div style={{width:'100%'}}>
                            <div className='r_item'>
                                <div className='r_header'>
                                    <span className='rate'>{reviews.reviews_id?.rate}</span>
                                    <StarFilled style={{color:'#487CAB'}} />
                                    <span className='author'>{reviews.reviews_id?.firstname}</span>
                                    <span className='date'>{moment.unix(reviews.reviews_id?.date_create).format('DD MMMM YYYY в HH:mm')}</span>
                                </div>
                                <div className='r_body'>
                                    <p dangerouslySetInnerHTML={{ __html: returnText(reviews.reviews_id?.text)}}></p>
                                </div>
                            </div>
                            <Form name="form" className="default_form" form={form_reviews_data} onFinish={form_finish_reviews_data}>
                                {["3","4"].indexOf( profile?.data?.type ) !== -1 ?
                                    <>
                                        <label>Статус</label>    
                                        <Form.Item name={`status`} hidden={["3","4"].indexOf( profile?.data?.type ) === -1} >
                                            <Select
                                                disabled={app_global.loading}
                                                className="custom_select"
                                                showSearch
                                                placeholder="Выбрать статус"
                                                optionFilterProp="children"
                                                filterOption={(input, option) =>
                                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                }
                                                options={[
                                                    {value:"publish",label:"Опубликовано"},
                                                    {value:"moderation",label:"На проверке"},
                                                    {value:"cancel",label:"Отклонено"}
                                                ]}
                                            />
                                        </Form.Item>
                                        <label>Ответ</label>   
                                        <Form.Item name={`text_answer`} >
                                            <TextArea className="custom_input" style={{height:'auto'}} rows={4} disabled={app_global.loading} />
                                        </Form.Item>
                                    </>
                                :
                                    null
                                }
                                <div className='form_buttons'>
                                    { ["2","3","4"].indexOf( profile?.data?.type ) !== -1 ?
                                        <Button className='button_left red' onClick={() => clickDeleteReview()}>Удалить</Button>
                                    :
                                        null
                                    }
                                    {["3","4"].indexOf( profile?.data?.type ) !== -1 ?
                                        <Button className='submit' htmlType="submit" loading={organization.loading}>Сохранить</Button>
                                    :
                                        null
                                    }
                                </div>
                            </Form>
                        </div>
                    }
                </div>
            </Modal>
            <Modal mask={false} className="modal_custom" key="m8" width={1600} title="Добавление записи" open={app_global.panel.indexOf( 'postsAdd' ) != -1 } onOk={() => hide_tab_posts_add()} onCancel={() => hide_tab_posts_add()} footer={null}>
                <div className='header'>
                    <h2>Добавление записи</h2>
                </div>
                <div className='custom_divider' />
                <div className='body'>
                    <div>
                        <Form name="form" className="default_form" form={form_posts_add} onFinish={form_finish_posts_add}>
                            <label>Название</label>
                            <Form.Item name={`title`} rules={[{ required: true, message: `Пожалуйста, введите название записи!` }]}>
                                <Input disabled={posts.loading} className='custom_input' placeholder={`Название записи`} />
                            </Form.Item>
                            <label>Категория</label>
                            <Form.Item name={`category`} rules={[{ required: true, message: `Пожалуйста, выберите категорию записи!` }]}>
                                <Select
                                    disabled={organization.loading}
                                    className="custom_select"
                                    showSearch
                                    placeholder="Выбрать категорию"
                                    optionFilterProp="children"
                                    filterOption={(input, option) =>
                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                    options={[
                                        {value:"1",label:"Новости портала"},
                                        {value:"6",label:"Новости минздрава"},
                                        {value:"3",label:"Статьи о здоровье"},
                                        {value:"9",label:"Полезная информация"},
                                        {value:"4",label:"Памятки для пациентов"},
                                        {value:"5",label:"Направления медицины"},
                                        {value:"7",label:"Симптоматика"},
                                        {value:"8",label:"Продукты питания"},
                                        {value:"11",label:"Советы по правильному питанию"},
                                        {value:"12",label:"Физическая активность"},
                                        {value:"13",label:"Здоровый сон"},
                                        {value:"14",label:"Статьи"},
                                    ]}
                                />
                            </Form.Item>
                            <div className='form_buttons'>
                                <Button className='submit' htmlType="submit" loading={organization.loading}>Сохранить</Button>
                            </div>
                        </Form>
                    </div>
                </div>
            </Modal>
            <Modal mask={false} className="modal_custom" key="m9" width={1600} title="Изменение записи" open={app_global.panel.indexOf( 'posts' ) != -1 } onOk={() => hide_tab_posts_item()} onCancel={() => hide_tab_posts_item()} footer={null}>
                <div className='header'>
                    <h2>Изменение информации</h2>
                    <h3>
                        {Object(posts.posts_id).length === 0 ?
                            <Skeleton.Button style={{marginBottom: '5px', maxWidth: '300px'}} size="small" shape="round" active block />
                        :
                            <>{getOrgStatusTag(posts.posts_id?.status)} {posts.posts_id?.title}</>
                        }
                    </h3>
                </div>
                <div className='custom_divider' />
                <div className='body'>
                    {Object(posts.posts_id).length === 0 ?
                        <Skeleton active />
                    :
                        <div className="custom-tabs">
                            <Tabs
                                tabPosition={Object.entries(screens)
                                    .filter((screen) => !!screen[1])
                                    .map((screen) => (
                                        screen[0]
                                )).indexOf( 'xl' ) != -1 ? 'left' : 'top'}
                                activeKey={activeTabPostsEdit}
                                defaultActiveKey="data"
                                onChange={tabPostsEditChange}
                                className="profile_tabs"
                                items={[
                                    {
                                        key: 'data',
                                        label: `Основная информация`,
                                        children: 
                                        <div>
                                            <Form name="form" className="default_form" form={form_posts_data} onFinish={form_finish_posts_data}>
                                                {["3","4"].indexOf( profile?.data?.type ) !== -1 ?
                                                    <label>Статус</label>
                                                :
                                                    null
                                                }
                                                <Form.Item name={`status`} hidden={["3","4"].indexOf( profile?.data?.type ) === -1} >
                                                    <Select
                                                        disabled={app_global.loading}
                                                        className="custom_select"
                                                        showSearch
                                                        placeholder="Выбрать статус"
                                                        optionFilterProp="children"
                                                        filterOption={(input, option) =>
                                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                        }
                                                        options={[
                                                            {value:"publish",label:"Опубликовано"},
                                                            {value:"moderation",label:"На проверке"},
                                                            {value:"draft",label:"Черновик"},
                                                            {value:"cancel",label:"Отклонено"}
                                                        ]}
                                                    />
                                                </Form.Item>
                                                <label>Название</label>
                                                <Form.Item name={`title`} rules={[{ required: true, message: `Пожалуйста, введите название записи!` }]}>
                                                    <Input disabled={posts.loading} className='custom_input' placeholder={`Название записи`} />
                                                </Form.Item>
                                                <label>Категория</label>
                                                <Form.Item name={`category`} rules={[{ required: true, message: `Пожалуйста, выберите категорию записи!` }]}>
                                                    <Select
                                                        disabled={posts.loading}
                                                        className="custom_select"
                                                        showSearch
                                                        placeholder="Выбрать категорию"
                                                        optionFilterProp="children"
                                                        filterOption={(input, option) =>
                                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                        }
                                                        options={[
                                                            {value:"1",label:"Новости портала"},
                                                            {value:"6",label:"Новости минздрава"},
                                                            {value:"3",label:"Статьи о здоровье"},
                                                            {value:"9",label:"Полезная информация"},
                                                            {value:"4",label:"Памятки для пациентов"},
                                                            {value:"5",label:"Направления медицины"},
                                                            {value:"7",label:"Симптоматика"},
                                                            {value:"8",label:"Продукты питания"},
                                                            {value:"11",label:"Советы по правильному питанию"},
                                                            {value:"12",label:"Физическая активность"},
                                                            {value:"13",label:"Здоровый сон"},
                                                            {value:"14",label:"Статьи"},
                                                        ]}
                                                    />
                                                </Form.Item>
                                                <label>Описание</label>
                                                <Form.Item name={`excerpt`} >
                                                    <TextArea className='custom_input' placeholder="Описание записи" rows={4} showCount  maxLength={1000} />
                                                </Form.Item>
                                                <label>Дата публикации</label>
                                                <Form.Item name={`date_create`} >
                                                    <TimeInput
                                                        onChange={({ target: { value } }) => form_posts_data.setFieldsValue({
                                                            date_create:value  
                                                        })}>
                                                    </TimeInput>
                                                </Form.Item>
                                                <div className='form_buttons'>
                                                    <Button className='submit' htmlType="submit" loading={posts.loading}>Сохранить</Button>
                                                </div>
                                            </Form>
                                        </div>
                                    },
                                    {
                                        key: 'seo',
                                        label: `SEO`,
                                        children: 
                                        <div>
                                            <Form name="form" className="default_form" form={form_posts_seo} onFinish={form_finish_posts_seo}>
                                                <label>Заголовок</label>
                                                <Form.Item name={`seo_title`} rules={[{ required: true, message: `Пожалуйста, введите заголовок для поисковиков!` }]}>
                                                    <Input disabled={posts.loading} className='custom_input' placeholder={`Заголовок`} />
                                                </Form.Item>
                                                <label>Описание</label>
                                                <Form.Item name={`seo_description`} >
                                                    <Input disabled={posts.loading} className='custom_input' placeholder={`Описание`} />
                                                </Form.Item>
                                                <label>Ключевые слова</label>
                                                <Form.Item name={`seo_keywords`} >
                                                    <Input disabled={posts.loading} className='custom_input' placeholder={`Ключевые слова`} />
                                                </Form.Item>
                                                <div className='form_buttons'>
                                                    <Button className='submit' htmlType="submit" loading={posts.loading}>Сохранить</Button>
                                                </div>
                                            </Form>
                                        </div>
                                    },
                                    {
                                        key: 'content',
                                        label: `Контент`,
                                        children: 
                                        <div>
                                            <Form name="form" className="default_form" form={form_posts_content} onFinish={form_finish_posts_content} >
                                                <div className='form_buttons top'>
                                                    <Button className='submit' htmlType="submit" loading={posts.loading}>Сохранить</Button>
                                                </div>
                                                <ReactEditorJS 
                                                    token={profile.token}
                                                    API={API}
                                                    ref={editorCorePosts}
                                                    onInitialize={handleInitializePosts}
                                                    defaultValue={posts.posts_id?.content !== undefined ? JSON.parse(posts.posts_id?.content) : {}}
                                                />
                                                <Form.Item name={`content`} hidden >
                                                    <TextArea rows={4} disabled />
                                                </Form.Item>
                                                <div className='form_buttons'>
                                                    <Button className='submit' htmlType="submit" loading={posts.loading}>Сохранить</Button>
                                                </div>
                                            </Form>
                                        </div>
                                    },
                                    {
                                        key: 'images',
                                        label: `Изображения`,
                                        children: 
                                        <div>
                                            <Form className="default_form">
                                                <div className="fcenter images_column">
                                                    {posts.posts_id?.photo_mini === null ?
                                                        null
                                                    :
                                                        <div className='image_preview'>
                                                            <Image
                                                                className="image-preview"
                                                                height={100}
                                                                width={100}
                                                                src={`${UPLOADS}/${posts.posts_id?.photo_mini_preview}`}
                                                                preview={{
                                                                    src: `${UPLOADS}/${posts.posts_id?.photo_mini}`
                                                                }}
                                                            /> 
                                                            <div className="float_remove">
                                                                <Popconfirm placement="topRight" onConfirm={() => posts_image_delete()} 
                                                                    title={
                                                                        <Space direction="vertical">
                                                                            <Text>Вы действительно хотите удалить данное изображение?</Text>
                                                                        </Space>
                                                                    } 
                                                                okText="Да" cancelText="Нет">
                                                                    <FiTrash2 style={{color:'#f5222d',cursor:'pointer'}} />
                                                                </Popconfirm>
                                                            </div>
                                                        </div>
                                                    }
                                                    <ImgCrop quality={1} aspect={1.495} modalTitle="Загрузка миниатюры" modalOk="Сохранить" modalCancel="Отмена" >
                                                        <Dragger
                                                            className="no-list"
                                                            {...upload_config_one_image}
                                                            {...upload_posts_image}
                                                            data={{'method':'upload_posts_photo','token':profile?.token,'posts_id':posts.posts_id?.id}}
                                                        >
                                                            <div className='upload_content'>
                                                                <div className='left'>
                                                                    <svg width="42" height="36" viewBox="0 0 42 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                        <path d="M36.2727 15.1181C35.7664 15.1181 35.2808 15.3172 34.9228 15.6716C34.5648 16.026 34.3636 16.5067 34.3636 17.0079V30.2362C34.3636 30.7374 34.1625 31.2181 33.8045 31.5725C33.4465 31.9269 32.9609 32.126 32.4545 32.126H5.72727C5.22095 32.126 4.73537 31.9269 4.37734 31.5725C4.01932 31.2181 3.81818 30.7374 3.81818 30.2362V15.1181C3.81818 14.6169 4.01932 14.1362 4.37734 13.7818C4.73537 13.4274 5.22095 13.2283 5.72727 13.2283H9.54545C9.96176 13.2499 10.3737 13.1359 10.7185 12.9038C11.0632 12.6718 11.3217 12.3345 11.4545 11.9433L12.4855 8.84409C12.6135 8.46875 12.8575 8.14274 13.183 7.91214C13.5084 7.68155 13.8989 7.55803 14.2991 7.55906H24.8182C25.3245 7.55906 25.8101 7.35996 26.1681 7.00556C26.5261 6.65116 26.7273 6.17049 26.7273 5.66929C26.7273 5.1681 26.5261 4.68743 26.1681 4.33303C25.8101 3.97863 25.3245 3.77953 24.8182 3.77953H14.2036C13.0039 3.78175 11.8351 4.15688 10.8623 4.85198C9.88947 5.54708 9.16174 6.52703 8.78182 7.65354L8.17091 9.54331H5.72727C4.20831 9.54331 2.75155 10.1406 1.67748 11.2038C0.603407 12.267 0 13.709 0 15.2126V30.3307C0 31.8343 0.603407 33.2763 1.67748 34.3395C2.75155 35.4027 4.20831 36 5.72727 36H32.4545C33.9735 36 35.4303 35.4027 36.5043 34.3395C37.5784 33.2763 38.1818 31.8343 38.1818 30.3307V17.1024C38.1948 16.8464 38.155 16.5904 38.0648 16.3501C37.9747 16.1098 37.8361 15.8902 37.6576 15.7046C37.479 15.519 37.2641 15.3713 37.026 15.2704C36.788 15.1696 36.5317 15.1178 36.2727 15.1181ZM19.0909 13.2283C17.5806 13.2283 16.1042 13.6717 14.8484 14.5023C13.5926 15.3329 12.6138 16.5134 12.0358 17.8947C11.4579 19.2759 11.3066 20.7958 11.6013 22.2621C11.8959 23.7284 12.6232 25.0753 13.6912 26.1325C14.7591 27.1896 16.1198 27.9095 17.6011 28.2012C19.0824 28.4929 20.6179 28.3432 22.0132 27.7711C23.4086 27.1989 24.6012 26.2301 25.4403 24.987C26.2794 23.7439 26.7273 22.2824 26.7273 20.7874C26.7273 18.7826 25.9227 16.8599 24.4906 15.4423C23.0585 14.0247 21.1162 13.2283 19.0909 13.2283ZM19.0909 24.5669C18.3357 24.5669 17.5975 24.3453 16.9696 23.93C16.3417 23.5147 15.8524 22.9244 15.5634 22.2338C15.2744 21.5431 15.1988 20.7832 15.3461 20.0501C15.4934 19.3169 15.8571 18.6434 16.391 18.1149C16.925 17.5863 17.6054 17.2263 18.346 17.0805C19.0867 16.9347 19.8544 17.0095 20.5521 17.2956C21.2497 17.5816 21.8461 18.0661 22.2656 18.6876C22.6852 19.3091 22.9091 20.0399 22.9091 20.7874C22.9091 21.7898 22.5068 22.7511 21.7908 23.4599C21.0747 24.1687 20.1036 24.5669 19.0909 24.5669ZM40.0909 3.77953H38.1818V1.88976C38.1818 1.38857 37.9807 0.907898 37.6227 0.553499C37.2646 0.199099 36.779 0 36.2727 0C35.7664 0 35.2808 0.199099 34.9228 0.553499C34.5648 0.907898 34.3636 1.38857 34.3636 1.88976V3.77953H32.4545C31.9482 3.77953 31.4626 3.97863 31.1046 4.33303C30.7466 4.68743 30.5455 5.1681 30.5455 5.66929C30.5455 6.17049 30.7466 6.65116 31.1046 7.00556C31.4626 7.35996 31.9482 7.55906 32.4545 7.55906H34.3636V9.44882C34.3636 9.95002 34.5648 10.4307 34.9228 10.7851C35.2808 11.1395 35.7664 11.3386 36.2727 11.3386C36.779 11.3386 37.2646 11.1395 37.6227 10.7851C37.9807 10.4307 38.1818 9.95002 38.1818 9.44882V7.55906H40.0909C40.5972 7.55906 41.0828 7.35996 41.4408 7.00556C41.7989 6.65116 42 6.17049 42 5.66929C42 5.1681 41.7989 4.68743 41.4408 4.33303C41.0828 3.97863 40.5972 3.77953 40.0909 3.77953Z" fill="#9B9B9B"/>
                                                                    </svg>
                                                                </div>
                                                                <div className='right'>
                                                                    [Миниатюра] Нажмите на кнопку или перетащите фото в эту область в формате PNG, JPEG.
                                                                </div>
                                                            </div>
                                                        </Dragger>
                                                    </ImgCrop>
                                                </div>
                                                <div className="fcenter images_column">
                                                    {posts.posts_id?.photo_big === null ?
                                                        null
                                                    :
                                                        <div className='image_preview'>
                                                            <Image
                                                                className="image-preview"
                                                                height={100}
                                                                width={100}
                                                                src={`${UPLOADS}/${posts.posts_id?.photo_big_preview}`}
                                                                preview={{
                                                                    src: `${UPLOADS}/${posts.posts_id?.photo_big}`
                                                                }}
                                                            />
                                                            <div className="float_remove">
                                                                <Popconfirm placement="topRight" onConfirm={() => posts_image_big_delete()} 
                                                                    title={
                                                                        <Space direction="vertical">
                                                                            <Text>Вы действительно хотите удалить данное изображение?</Text>
                                                                        </Space>
                                                                    } 
                                                                okText="Да" cancelText="Нет">
                                                                    <FiTrash2 style={{color:'#f5222d',cursor:'pointer'}} />
                                                                </Popconfirm>
                                                            </div>
                                                        </div>
                                                    }
                                                    <ImgCrop quality={1} aspect={2.98} modalTitle="Загрузка большого изображения" modalOk="Сохранить" modalCancel="Отмена" >
                                                        <Dragger
                                                            className="no-list"
                                                            {...upload_config_one_image}
                                                            {...upload_posts_image}
                                                            data={{'method':'upload_posts_photo_big','token':profile?.token,'posts_id':posts.posts_id?.id}}
                                                        >
                                                            <div className='upload_content'>
                                                                <div className='left'>
                                                                    <svg width="42" height="36" viewBox="0 0 42 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                        <path d="M36.2727 15.1181C35.7664 15.1181 35.2808 15.3172 34.9228 15.6716C34.5648 16.026 34.3636 16.5067 34.3636 17.0079V30.2362C34.3636 30.7374 34.1625 31.2181 33.8045 31.5725C33.4465 31.9269 32.9609 32.126 32.4545 32.126H5.72727C5.22095 32.126 4.73537 31.9269 4.37734 31.5725C4.01932 31.2181 3.81818 30.7374 3.81818 30.2362V15.1181C3.81818 14.6169 4.01932 14.1362 4.37734 13.7818C4.73537 13.4274 5.22095 13.2283 5.72727 13.2283H9.54545C9.96176 13.2499 10.3737 13.1359 10.7185 12.9038C11.0632 12.6718 11.3217 12.3345 11.4545 11.9433L12.4855 8.84409C12.6135 8.46875 12.8575 8.14274 13.183 7.91214C13.5084 7.68155 13.8989 7.55803 14.2991 7.55906H24.8182C25.3245 7.55906 25.8101 7.35996 26.1681 7.00556C26.5261 6.65116 26.7273 6.17049 26.7273 5.66929C26.7273 5.1681 26.5261 4.68743 26.1681 4.33303C25.8101 3.97863 25.3245 3.77953 24.8182 3.77953H14.2036C13.0039 3.78175 11.8351 4.15688 10.8623 4.85198C9.88947 5.54708 9.16174 6.52703 8.78182 7.65354L8.17091 9.54331H5.72727C4.20831 9.54331 2.75155 10.1406 1.67748 11.2038C0.603407 12.267 0 13.709 0 15.2126V30.3307C0 31.8343 0.603407 33.2763 1.67748 34.3395C2.75155 35.4027 4.20831 36 5.72727 36H32.4545C33.9735 36 35.4303 35.4027 36.5043 34.3395C37.5784 33.2763 38.1818 31.8343 38.1818 30.3307V17.1024C38.1948 16.8464 38.155 16.5904 38.0648 16.3501C37.9747 16.1098 37.8361 15.8902 37.6576 15.7046C37.479 15.519 37.2641 15.3713 37.026 15.2704C36.788 15.1696 36.5317 15.1178 36.2727 15.1181ZM19.0909 13.2283C17.5806 13.2283 16.1042 13.6717 14.8484 14.5023C13.5926 15.3329 12.6138 16.5134 12.0358 17.8947C11.4579 19.2759 11.3066 20.7958 11.6013 22.2621C11.8959 23.7284 12.6232 25.0753 13.6912 26.1325C14.7591 27.1896 16.1198 27.9095 17.6011 28.2012C19.0824 28.4929 20.6179 28.3432 22.0132 27.7711C23.4086 27.1989 24.6012 26.2301 25.4403 24.987C26.2794 23.7439 26.7273 22.2824 26.7273 20.7874C26.7273 18.7826 25.9227 16.8599 24.4906 15.4423C23.0585 14.0247 21.1162 13.2283 19.0909 13.2283ZM19.0909 24.5669C18.3357 24.5669 17.5975 24.3453 16.9696 23.93C16.3417 23.5147 15.8524 22.9244 15.5634 22.2338C15.2744 21.5431 15.1988 20.7832 15.3461 20.0501C15.4934 19.3169 15.8571 18.6434 16.391 18.1149C16.925 17.5863 17.6054 17.2263 18.346 17.0805C19.0867 16.9347 19.8544 17.0095 20.5521 17.2956C21.2497 17.5816 21.8461 18.0661 22.2656 18.6876C22.6852 19.3091 22.9091 20.0399 22.9091 20.7874C22.9091 21.7898 22.5068 22.7511 21.7908 23.4599C21.0747 24.1687 20.1036 24.5669 19.0909 24.5669ZM40.0909 3.77953H38.1818V1.88976C38.1818 1.38857 37.9807 0.907898 37.6227 0.553499C37.2646 0.199099 36.779 0 36.2727 0C35.7664 0 35.2808 0.199099 34.9228 0.553499C34.5648 0.907898 34.3636 1.38857 34.3636 1.88976V3.77953H32.4545C31.9482 3.77953 31.4626 3.97863 31.1046 4.33303C30.7466 4.68743 30.5455 5.1681 30.5455 5.66929C30.5455 6.17049 30.7466 6.65116 31.1046 7.00556C31.4626 7.35996 31.9482 7.55906 32.4545 7.55906H34.3636V9.44882C34.3636 9.95002 34.5648 10.4307 34.9228 10.7851C35.2808 11.1395 35.7664 11.3386 36.2727 11.3386C36.779 11.3386 37.2646 11.1395 37.6227 10.7851C37.9807 10.4307 38.1818 9.95002 38.1818 9.44882V7.55906H40.0909C40.5972 7.55906 41.0828 7.35996 41.4408 7.00556C41.7989 6.65116 42 6.17049 42 5.66929C42 5.1681 41.7989 4.68743 41.4408 4.33303C41.0828 3.97863 40.5972 3.77953 40.0909 3.77953Z" fill="#9B9B9B"/>
                                                                    </svg>
                                                                </div>
                                                                <div className='right'>
                                                                    [Большое изображение] Нажмите на кнопку или перетащите фото в эту область в формате PNG, JPEG.
                                                                </div>
                                                            </div>
                                                        </Dragger>
                                                    </ImgCrop>
                                                </div>
                                            </Form>
                                        </div>
                                    }
                                ]} 
                            />
                        </div>
                    }
                </div>
            </Modal>
            <Modal mask={false} className="modal_custom" key="m10" width={900} title="Изменение специалиста" open={app_global.panel.indexOf( 'organizationSpecialists' ) != -1 } onOk={() => hide_tab_organization_specialists()} onCancel={() => hide_tab_organization_specialists()} footer={null}>
                <div className='header'>
                    <h2>Изменение специалиста</h2>
                    <h3>
                        {Object(organization.organization_id).length > 0 ?
                            <Skeleton.Button style={{marginBottom: '5px', maxWidth: '300px'}} size="small" shape="round" active block />
                        :
                            organization.organization_id?.orgname
                        }
                    </h3>
                </div>
                <div className='custom_divider' />
                <div className='body'>
                    <Form name="form" className="default_form" form={form_organization_specialists} onFinish={form_finish_organization_specialists} >
                        <Form.Item hidden name={`id`} rules={[{ required: true, message: `Пожалуйста, введите ID специалиста!` }]}>
                            <Input disabled={organization.loading} className='custom_input' />
                        </Form.Item>
                        <label>ФИО</label>
                        <Form.Item name={`name`} rules={[{ required: true, message: `Пожалуйста, введите ФИО!` }]}>
                            <Input disabled={organization.loading} className='custom_input' />
                        </Form.Item>
                        <label>Должность</label>
                        <Form.Item name={`post`} rules={[{ required: true, message: `Пожалуйста, введите должность!` }]}>
                            <Input disabled={organization.loading} className='custom_input' />
                        </Form.Item>
                        <label>Отделение</label>
                        <Form.Item name={`department`} >
                            <Input disabled={organization.loading} className='custom_input' />
                        </Form.Item>
                        <label>Специализация</label>
                        <Form.Item name={`specializations`} >
                            <Select
                                mode="tags"
                                onChange= {(newValue) => {
                                    console.log(newValue);
                                }}
                                disabled={organization.loading}
                                className="custom_select"
                                placeholder="Выбрать специализацию"
                                options={organization.organization_specialists_specialization}
                            />
                        </Form.Item>
                        <div className='form_buttons'>
                            <Button className='submit' htmlType="submit" loading={organization.loading}>Сохранить</Button>
                        </div>
                    </Form>
                </div>
            </Modal>
            <Modal mask={false} className="modal_custom" key="m11" width={900} title="Изменение файла" open={app_global.panel.indexOf( 'organizationPrice' ) != -1 } onOk={() => hide_tab_organization_price()} onCancel={() => hide_tab_organization_price()} footer={null}>
                <div className='header'>
                    <h2>Изменение файла</h2>
                    <h3>
                        {Object(organization.organization_id).length > 0 ?
                            <Skeleton.Button style={{marginBottom: '5px', maxWidth: '300px'}} size="small" shape="round" active block />
                        :
                            organization.organization_id?.orgname
                        }
                    </h3>
                </div>
                <div className='custom_divider' />
                <div className='body'>
                    <Form name="form" className="default_form" form={form_organization_price} onFinish={form_finish_organization_price} >
                        <Form.Item hidden name={`id`} rules={[{ required: true, message: `Пожалуйста, введите ID пункта!` }]}>
                            <Input disabled={organization.loading} className='custom_input' />
                        </Form.Item>
                        <label style={{marginTop:20}}>Название</label>
                        <Form.Item name={`text`} rules={[{ required: true, message: `Пожалуйста, введите название!` }]}>
                            <Input disabled={organization.loading} className='custom_input' />
                        </Form.Item>
                        <div className='form_buttons'>
                            <Button className='submit' htmlType="submit" loading={organization.loading}>Сохранить</Button>
                        </div>
                    </Form>
                </div>
            </Modal>
            <Modal mask={false} className="modal_custom" key="m12" width={900} title="Добавление категории" open={app_global.panel.indexOf( 'categoriesAdd' ) != -1 } onOk={() => hide_tab_categories_add()} onCancel={() => hide_tab_categories_add()} footer={null}>
                <div className='header'>
                    <h2>Добавление категории</h2>
                </div>
                <div className='custom_divider' />
                <div className='body'>
                    <div>
                        <Form name="form" className="default_form" form={form_categories_add} onFinish={form_finish_categories_add}>
                            <label>Название</label>
                            <Form.Item name={`title`} rules={[{ required: true, message: `Пожалуйста, введите название категории!` }]}>
                                <Input disabled={posts.loading} className='custom_input' placeholder={`Название категории`} />
                            </Form.Item>
                            <div className='form_buttons'>
                                <Button className='submit' htmlType="submit" loading={organization.loading}>Сохранить</Button>
                            </div>
                        </Form>
                    </div>
                </div>
            </Modal>
            <Modal mask={false} className="modal_custom" key="m13" width={900} title="Изменение категории" open={app_global.panel.indexOf( 'categories_item' ) != -1 } onOk={() => hide_tab_categories_data()} onCancel={() => hide_tab_categories_data()} footer={null}>
                <div className='header'>
                    <h2>Изменение категории</h2>
                </div>
                <div className='custom_divider' />
                <div className='body'>
                    <Form name="form" className="default_form" form={form_categories_data} onFinish={form_finish_categories_data} >
                        <Form.Item hidden name={`id`} rules={[{ required: true, message: `Пожалуйста, введите ID пункта!` }]}>
                            <Input disabled={organization.loading} className='custom_input' />
                        </Form.Item>
                        <label>Название</label>
                        <Form.Item name={`title`} rules={[{ required: true, message: `Пожалуйста, введите название категории!` }]}>
                            <Input disabled={posts.loading} className='custom_input' placeholder={`Название категории`} />
                        </Form.Item>
                        <div className='form_buttons'>
                            <Button className='submit' htmlType="submit" loading={organization.loading}>Сохранить</Button>
                        </div>
                    </Form>
                </div>
            </Modal>
            <Modal mask={false} className="modal_custom" key="m14" width={1600} title="Добавление опроса" open={app_global.panel.indexOf( 'surveysAdd' ) != -1 } onOk={() => hide_tab_surveys_add()} onCancel={() => hide_tab_surveys_add()} footer={null}>
                <div className='header'>
                    <h2>Добавление опроса</h2>
                </div>
                <div className='custom_divider' />
                <div className='body'>
                    <div>
                        <Form name="form" className="default_form" form={form_surveys_add} onFinish={form_finish_surveys_add}>
                            <label>Название</label>
                            <Form.Item name={`title`} rules={[{ required: true, message: `Пожалуйста, введите название опроса!` }]}>
                                <Input disabled={surveys.loading} className='custom_input' placeholder={`Название опроса`} />
                            </Form.Item>
                            <div className='form_buttons'>
                                <Button className='submit' htmlType="submit" loading={organization.loading}>Сохранить</Button>
                            </div>
                        </Form>
                    </div>
                </div>
            </Modal>
            <Modal mask={false} className="modal_custom" key="m15" width={1600} title="Изменение опроса" open={app_global.panel.indexOf( 'surveys' ) != -1 } onOk={() => hide_tab_surveys_item()} onCancel={() => hide_tab_surveys_item()} footer={null}>
                <div className='header'>
                    <h2>Изменение информации</h2>
                    <h3>
                        {Object(surveys.surveys_id).length === 0 ?
                            <Skeleton.Button style={{marginBottom: '5px', maxWidth: '300px'}} size="small" shape="round" active block />
                        :
                            <>{getOrgStatusTag(surveys.surveys_id?.status)} {surveys.surveys_id?.title}</>
                        }
                    </h3>
                </div>
                <div className='custom_divider' />
                <div className='body'>
                    {Object(surveys.surveys_id).length === 0 ?
                        <Skeleton active />
                    :
                        <div className="custom-tabs">
                            <Tabs
                                tabPosition={Object.entries(screens)
                                    .filter((screen) => !!screen[1])
                                    .map((screen) => (
                                        screen[0]
                                )).indexOf( 'xl' ) != -1 ? 'left' : 'top'}
                                activeKey={activeTabPostsEdit}
                                defaultActiveKey="data"
                                onChange={tabPostsEditChange}
                                className="profile_tabs"
                                items={[
                                    {
                                        key: 'data',
                                        label: `Основная информация`,
                                        children: 
                                        <div>
                                            <Form name="form" className="default_form" form={form_surveys_data} onFinish={form_finish_surveys_data}>
                                                {["3","4"].indexOf( profile?.data?.type ) !== -1 ?
                                                    <label>Статус</label>
                                                :
                                                    null
                                                }
                                                <Form.Item name={`status`} hidden={["3","4"].indexOf( profile?.data?.type ) === -1} >
                                                    <Select
                                                        disabled={app_global.loading}
                                                        className="custom_select"
                                                        showSearch
                                                        placeholder="Выбрать статус"
                                                        optionFilterProp="children"
                                                        filterOption={(input, option) =>
                                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                        }
                                                        options={[
                                                            {value:"publish",label:"Опубликовано"},
                                                            {value:"moderation",label:"На проверке"},
                                                            {value:"draft",label:"Черновик"},
                                                            {value:"cancel",label:"Отклонено"}
                                                        ]}
                                                    />
                                                </Form.Item>
                                                <label>Название</label>
                                                <Form.Item name={`title`} rules={[{ required: true, message: `Пожалуйста, введите название записи!` }]}>
                                                    <Input disabled={surveys.loading} className='custom_input' placeholder={`Название записи`} />
                                                </Form.Item>
                                                <label>Описание</label>
                                                <Form.Item name={`excerpt`} >
                                                    <TextArea className='custom_input' placeholder="Описание записи" rows={4} showCount  maxLength={1000} />
                                                </Form.Item>
                                                <label>Пост с советами</label>
                                                <Form.Item name={`specialization_id`} >
                                                    <Select
                                                        disabled={specialist.loading}
                                                        className="custom_select"
                                                        showSearch
                                                        placeholder="Выбрать пост"
                                                        optionFilterProp="children"
                                                        filterOption={(input, option) =>
                                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                        }
                                                        options={surveys.surveys_id?.posts?.specializations}
                                                    />
                                                </Form.Item>
                                                <label>Пост с симптомами</label>
                                                <Form.Item name={`symptom_id`} >
                                                    <Select
                                                        disabled={specialist.loading}
                                                        className="custom_select"
                                                        showSearch
                                                        placeholder="Выбрать пост"
                                                        optionFilterProp="children"
                                                        filterOption={(input, option) =>
                                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                        }
                                                        options={surveys.surveys_id?.posts?.symptoms}
                                                    />
                                                </Form.Item>
                                                <label>Дата публикации</label>
                                                <Form.Item name={`date_create`} >
                                                    <TimeInput
                                                        onChange={({ target: { value } }) => form_surveys_data.setFieldsValue({
                                                            date_create:value  
                                                        })}>
                                                    </TimeInput>
                                                </Form.Item>
                                                <div className='form_buttons'>
                                                    <Button className='submit' htmlType="submit" loading={surveys.loading}>Сохранить</Button>
                                                </div>
                                            </Form>
                                        </div>
                                    },
                                    {
                                        key: 'seo',
                                        label: `SEO`,
                                        children: 
                                        <div>
                                            <Form name="form" className="default_form" form={form_surveys_seo} onFinish={form_finish_surveys_seo}>
                                                <label>Заголовок</label>
                                                <Form.Item name={`seo_title`} rules={[{ required: true, message: `Пожалуйста, введите заголовок для поисковиков!` }]}>
                                                    <Input disabled={surveys.loading} className='custom_input' placeholder={`Заголовок`} />
                                                </Form.Item>
                                                <label>Описание</label>
                                                <Form.Item name={`seo_description`} >
                                                    <Input disabled={surveys.loading} className='custom_input' placeholder={`Описание`} />
                                                </Form.Item>
                                                <label>Ключевые слова</label>
                                                <Form.Item name={`seo_keywords`} >
                                                    <Input disabled={surveys.loading} className='custom_input' placeholder={`Ключевые слова`} />
                                                </Form.Item>
                                                <div className='form_buttons'>
                                                    <Button className='submit' htmlType="submit" loading={surveys.loading}>Сохранить</Button>
                                                </div>
                                            </Form>
                                        </div>
                                    },
                                    {
                                        key: 'content',
                                        label: `Контент`,
                                        children: 
                                        <div>
                                            <Form name="form" className="default_form" form={form_surveys_content} onFinish={form_finish_surveys_content} >
                                                <div className='form_buttons top'>
                                                    <Button className='submit' htmlType="submit" loading={surveys.loading}>Сохранить</Button>
                                                </div>
                                                <ReactEditorJS 
                                                    token={profile.token}
                                                    API={API}
                                                    ref={editorCoreSurveys}
                                                    onInitialize={handleInitializeSurveys}
                                                    defaultValue={surveys.surveys_id?.content !== undefined ? JSON.parse(surveys.surveys_id?.content) : {}}
                                                />
                                                <Form.Item name={`content`} hidden >
                                                    <TextArea rows={4} disabled />
                                                </Form.Item>
                                                <div className='form_buttons'>
                                                    <Button className='submit' htmlType="submit" loading={surveys.loading}>Сохранить</Button>
                                                </div>
                                            </Form>
                                        </div>
                                    },
                                    {
                                        key: 'images',
                                        label: `Изображения`,
                                        children: 
                                        <div>
                                            <Form className="default_form">
                                                <div className="fcenter images_column">
                                                    {surveys.surveys_id?.photo_mini === null ?
                                                        null
                                                    :
                                                        <div className='image_preview'>
                                                            <Image
                                                                className="image-preview"
                                                                height={100}
                                                                width={100}
                                                                src={`${UPLOADS}/${surveys.surveys_id?.photo_mini_preview}`}
                                                                preview={{
                                                                    src: `${UPLOADS}/${surveys.surveys_id?.photo_mini}`
                                                                }}
                                                            /> 
                                                            <div className="float_remove">
                                                                <Popconfirm placement="topRight" onConfirm={() => surveys_image_delete()} 
                                                                    title={
                                                                        <Space direction="vertical">
                                                                            <Text>Вы действительно хотите удалить данное изображение?</Text>
                                                                        </Space>
                                                                    } 
                                                                okText="Да" cancelText="Нет">
                                                                    <FiTrash2 style={{color:'#f5222d',cursor:'pointer'}} />
                                                                </Popconfirm>
                                                            </div>
                                                        </div>
                                                    }
                                                    <ImgCrop quality={1} aspect={1.87} modalTitle="Загрузка миниатюры" modalOk="Сохранить" modalCancel="Отмена" >
                                                        <Dragger
                                                            className="no-list"
                                                            {...upload_config_one_image}
                                                            {...upload_surveys_image}
                                                            data={{'method':'upload_surveys_photo','token':profile?.token,'surveys_id':surveys.surveys_id?.id}}
                                                        >
                                                            <div className='upload_content'>
                                                                <div className='left'>
                                                                    <svg width="42" height="36" viewBox="0 0 42 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                        <path d="M36.2727 15.1181C35.7664 15.1181 35.2808 15.3172 34.9228 15.6716C34.5648 16.026 34.3636 16.5067 34.3636 17.0079V30.2362C34.3636 30.7374 34.1625 31.2181 33.8045 31.5725C33.4465 31.9269 32.9609 32.126 32.4545 32.126H5.72727C5.22095 32.126 4.73537 31.9269 4.37734 31.5725C4.01932 31.2181 3.81818 30.7374 3.81818 30.2362V15.1181C3.81818 14.6169 4.01932 14.1362 4.37734 13.7818C4.73537 13.4274 5.22095 13.2283 5.72727 13.2283H9.54545C9.96176 13.2499 10.3737 13.1359 10.7185 12.9038C11.0632 12.6718 11.3217 12.3345 11.4545 11.9433L12.4855 8.84409C12.6135 8.46875 12.8575 8.14274 13.183 7.91214C13.5084 7.68155 13.8989 7.55803 14.2991 7.55906H24.8182C25.3245 7.55906 25.8101 7.35996 26.1681 7.00556C26.5261 6.65116 26.7273 6.17049 26.7273 5.66929C26.7273 5.1681 26.5261 4.68743 26.1681 4.33303C25.8101 3.97863 25.3245 3.77953 24.8182 3.77953H14.2036C13.0039 3.78175 11.8351 4.15688 10.8623 4.85198C9.88947 5.54708 9.16174 6.52703 8.78182 7.65354L8.17091 9.54331H5.72727C4.20831 9.54331 2.75155 10.1406 1.67748 11.2038C0.603407 12.267 0 13.709 0 15.2126V30.3307C0 31.8343 0.603407 33.2763 1.67748 34.3395C2.75155 35.4027 4.20831 36 5.72727 36H32.4545C33.9735 36 35.4303 35.4027 36.5043 34.3395C37.5784 33.2763 38.1818 31.8343 38.1818 30.3307V17.1024C38.1948 16.8464 38.155 16.5904 38.0648 16.3501C37.9747 16.1098 37.8361 15.8902 37.6576 15.7046C37.479 15.519 37.2641 15.3713 37.026 15.2704C36.788 15.1696 36.5317 15.1178 36.2727 15.1181ZM19.0909 13.2283C17.5806 13.2283 16.1042 13.6717 14.8484 14.5023C13.5926 15.3329 12.6138 16.5134 12.0358 17.8947C11.4579 19.2759 11.3066 20.7958 11.6013 22.2621C11.8959 23.7284 12.6232 25.0753 13.6912 26.1325C14.7591 27.1896 16.1198 27.9095 17.6011 28.2012C19.0824 28.4929 20.6179 28.3432 22.0132 27.7711C23.4086 27.1989 24.6012 26.2301 25.4403 24.987C26.2794 23.7439 26.7273 22.2824 26.7273 20.7874C26.7273 18.7826 25.9227 16.8599 24.4906 15.4423C23.0585 14.0247 21.1162 13.2283 19.0909 13.2283ZM19.0909 24.5669C18.3357 24.5669 17.5975 24.3453 16.9696 23.93C16.3417 23.5147 15.8524 22.9244 15.5634 22.2338C15.2744 21.5431 15.1988 20.7832 15.3461 20.0501C15.4934 19.3169 15.8571 18.6434 16.391 18.1149C16.925 17.5863 17.6054 17.2263 18.346 17.0805C19.0867 16.9347 19.8544 17.0095 20.5521 17.2956C21.2497 17.5816 21.8461 18.0661 22.2656 18.6876C22.6852 19.3091 22.9091 20.0399 22.9091 20.7874C22.9091 21.7898 22.5068 22.7511 21.7908 23.4599C21.0747 24.1687 20.1036 24.5669 19.0909 24.5669ZM40.0909 3.77953H38.1818V1.88976C38.1818 1.38857 37.9807 0.907898 37.6227 0.553499C37.2646 0.199099 36.779 0 36.2727 0C35.7664 0 35.2808 0.199099 34.9228 0.553499C34.5648 0.907898 34.3636 1.38857 34.3636 1.88976V3.77953H32.4545C31.9482 3.77953 31.4626 3.97863 31.1046 4.33303C30.7466 4.68743 30.5455 5.1681 30.5455 5.66929C30.5455 6.17049 30.7466 6.65116 31.1046 7.00556C31.4626 7.35996 31.9482 7.55906 32.4545 7.55906H34.3636V9.44882C34.3636 9.95002 34.5648 10.4307 34.9228 10.7851C35.2808 11.1395 35.7664 11.3386 36.2727 11.3386C36.779 11.3386 37.2646 11.1395 37.6227 10.7851C37.9807 10.4307 38.1818 9.95002 38.1818 9.44882V7.55906H40.0909C40.5972 7.55906 41.0828 7.35996 41.4408 7.00556C41.7989 6.65116 42 6.17049 42 5.66929C42 5.1681 41.7989 4.68743 41.4408 4.33303C41.0828 3.97863 40.5972 3.77953 40.0909 3.77953Z" fill="#9B9B9B"/>
                                                                    </svg>
                                                                </div>
                                                                <div className='right'>
                                                                    [Миниатюра] Нажмите на кнопку или перетащите фото в эту область в формате PNG, JPEG.
                                                                </div>
                                                            </div>
                                                        </Dragger>
                                                    </ImgCrop>
                                                </div>
                                                <div className="fcenter images_column">
                                                    {surveys.surveys_id?.photo_big === null ?
                                                        null
                                                    :
                                                        <div className='image_preview'>
                                                            <Image
                                                                className="image-preview"
                                                                height={100}
                                                                width={100}
                                                                src={`${UPLOADS}/${surveys.surveys_id?.photo_big_preview}`}
                                                                preview={{
                                                                    src: `${UPLOADS}/${surveys.surveys_id?.photo_big}`
                                                                }}
                                                            />
                                                            <div className="float_remove">
                                                                <Popconfirm placement="topRight" onConfirm={() => surveys_image_big_delete()} 
                                                                    title={
                                                                        <Space direction="vertical">
                                                                            <Text>Вы действительно хотите удалить данное изображение?</Text>
                                                                        </Space>
                                                                    } 
                                                                okText="Да" cancelText="Нет">
                                                                    <FiTrash2 style={{color:'#f5222d',cursor:'pointer'}} />
                                                                </Popconfirm>
                                                            </div>
                                                        </div>
                                                    }
                                                    <ImgCrop quality={1} aspect={2.1} modalTitle="Загрузка большого изображения" modalOk="Сохранить" modalCancel="Отмена" >
                                                        <Dragger
                                                            className="no-list"
                                                            {...upload_config_one_image}
                                                            {...upload_surveys_image}
                                                            data={{'method':'upload_surveys_photo_big','token':profile?.token,'surveys_id':surveys.surveys_id?.id}}
                                                        >
                                                            <div className='upload_content'>
                                                                <div className='left'>
                                                                    <svg width="42" height="36" viewBox="0 0 42 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                        <path d="M36.2727 15.1181C35.7664 15.1181 35.2808 15.3172 34.9228 15.6716C34.5648 16.026 34.3636 16.5067 34.3636 17.0079V30.2362C34.3636 30.7374 34.1625 31.2181 33.8045 31.5725C33.4465 31.9269 32.9609 32.126 32.4545 32.126H5.72727C5.22095 32.126 4.73537 31.9269 4.37734 31.5725C4.01932 31.2181 3.81818 30.7374 3.81818 30.2362V15.1181C3.81818 14.6169 4.01932 14.1362 4.37734 13.7818C4.73537 13.4274 5.22095 13.2283 5.72727 13.2283H9.54545C9.96176 13.2499 10.3737 13.1359 10.7185 12.9038C11.0632 12.6718 11.3217 12.3345 11.4545 11.9433L12.4855 8.84409C12.6135 8.46875 12.8575 8.14274 13.183 7.91214C13.5084 7.68155 13.8989 7.55803 14.2991 7.55906H24.8182C25.3245 7.55906 25.8101 7.35996 26.1681 7.00556C26.5261 6.65116 26.7273 6.17049 26.7273 5.66929C26.7273 5.1681 26.5261 4.68743 26.1681 4.33303C25.8101 3.97863 25.3245 3.77953 24.8182 3.77953H14.2036C13.0039 3.78175 11.8351 4.15688 10.8623 4.85198C9.88947 5.54708 9.16174 6.52703 8.78182 7.65354L8.17091 9.54331H5.72727C4.20831 9.54331 2.75155 10.1406 1.67748 11.2038C0.603407 12.267 0 13.709 0 15.2126V30.3307C0 31.8343 0.603407 33.2763 1.67748 34.3395C2.75155 35.4027 4.20831 36 5.72727 36H32.4545C33.9735 36 35.4303 35.4027 36.5043 34.3395C37.5784 33.2763 38.1818 31.8343 38.1818 30.3307V17.1024C38.1948 16.8464 38.155 16.5904 38.0648 16.3501C37.9747 16.1098 37.8361 15.8902 37.6576 15.7046C37.479 15.519 37.2641 15.3713 37.026 15.2704C36.788 15.1696 36.5317 15.1178 36.2727 15.1181ZM19.0909 13.2283C17.5806 13.2283 16.1042 13.6717 14.8484 14.5023C13.5926 15.3329 12.6138 16.5134 12.0358 17.8947C11.4579 19.2759 11.3066 20.7958 11.6013 22.2621C11.8959 23.7284 12.6232 25.0753 13.6912 26.1325C14.7591 27.1896 16.1198 27.9095 17.6011 28.2012C19.0824 28.4929 20.6179 28.3432 22.0132 27.7711C23.4086 27.1989 24.6012 26.2301 25.4403 24.987C26.2794 23.7439 26.7273 22.2824 26.7273 20.7874C26.7273 18.7826 25.9227 16.8599 24.4906 15.4423C23.0585 14.0247 21.1162 13.2283 19.0909 13.2283ZM19.0909 24.5669C18.3357 24.5669 17.5975 24.3453 16.9696 23.93C16.3417 23.5147 15.8524 22.9244 15.5634 22.2338C15.2744 21.5431 15.1988 20.7832 15.3461 20.0501C15.4934 19.3169 15.8571 18.6434 16.391 18.1149C16.925 17.5863 17.6054 17.2263 18.346 17.0805C19.0867 16.9347 19.8544 17.0095 20.5521 17.2956C21.2497 17.5816 21.8461 18.0661 22.2656 18.6876C22.6852 19.3091 22.9091 20.0399 22.9091 20.7874C22.9091 21.7898 22.5068 22.7511 21.7908 23.4599C21.0747 24.1687 20.1036 24.5669 19.0909 24.5669ZM40.0909 3.77953H38.1818V1.88976C38.1818 1.38857 37.9807 0.907898 37.6227 0.553499C37.2646 0.199099 36.779 0 36.2727 0C35.7664 0 35.2808 0.199099 34.9228 0.553499C34.5648 0.907898 34.3636 1.38857 34.3636 1.88976V3.77953H32.4545C31.9482 3.77953 31.4626 3.97863 31.1046 4.33303C30.7466 4.68743 30.5455 5.1681 30.5455 5.66929C30.5455 6.17049 30.7466 6.65116 31.1046 7.00556C31.4626 7.35996 31.9482 7.55906 32.4545 7.55906H34.3636V9.44882C34.3636 9.95002 34.5648 10.4307 34.9228 10.7851C35.2808 11.1395 35.7664 11.3386 36.2727 11.3386C36.779 11.3386 37.2646 11.1395 37.6227 10.7851C37.9807 10.4307 38.1818 9.95002 38.1818 9.44882V7.55906H40.0909C40.5972 7.55906 41.0828 7.35996 41.4408 7.00556C41.7989 6.65116 42 6.17049 42 5.66929C42 5.1681 41.7989 4.68743 41.4408 4.33303C41.0828 3.97863 40.5972 3.77953 40.0909 3.77953Z" fill="#9B9B9B"/>
                                                                    </svg>
                                                                </div>
                                                                <div className='right'>
                                                                    [Большое изображение] Нажмите на кнопку или перетащите фото в эту область в формате PNG, JPEG.
                                                                </div>
                                                            </div>
                                                        </Dragger>
                                                    </ImgCrop>
                                                </div>
                                            </Form>
                                        </div>
                                    },
                                    {
                                        key: 'questions',
                                        label: `Вопросы`,
                                        children: 
                                        <div>
                                            <p>Общее количество баллов - {surveys.surveys_id?.count_scores}</p>
                                            <List
                                                locale={{ emptyText: <Empty description="Список пуст" />}} 
                                                itemLayout="horizontal"
                                                dataSource={surveys.surveys_id?.questions}
                                                pagination={{
                                                    defaultPageSize: 10, 
                                                    hideOnSinglePage: true,
                                                }}
                                                renderItem={item => (
                                                <List.Item actions={[
                                                    <FiEdit style={{color:'#faad14'}} onClick={() => show_tab_surveys_questions(item?.id)} />,
                                                    <Popconfirm placement="topRight" onConfirm={() => clickDelSurveysQuestions({
                                                        id:item.id
                                                    })} 
                                                        title={
                                                            <Space direction="vertical">
                                                                <Text>Вы действительно хотите удалить данный вопрос?</Text>
                                                            </Space>
                                                        } 
                                                    okText="Да" cancelText="Нет">
                                                        <FiTrash2 style={{color:'#f5222d',cursor:'pointer'}} />
                                                    </Popconfirm>
                                                ]}>
                                                    <List.Item.Meta
                                                        title={
                                                            <span><b>{item.text ? item.text : 'Вопрос не заполнен'}</b> {item.answers.length ? <Tag color="#87d068">ответы присутствуют</Tag> : <Tag color="#ee4545">ответы отсутствуют</Tag>}</span>
                                                        }
                                                    />
                                                </List.Item>
                                                )}
                                            />
                                            <div className="fcenter">
                                                <Button className="submit_mini" icon={<PlusOutlined />} onClick={() => clickAddSurveysQuestions()} />
                                            </div>
                                        </div>
                                    },
                                    {
                                        key: 'criteria',
                                        label: `Критерии`,
                                        children: 
                                        <div>
                                            <p>Общее количество баллов - {surveys.surveys_id?.count_scores}</p>
                                            <List
                                                locale={{ emptyText: <Empty description="Список пуст" />}} 
                                                itemLayout="horizontal"
                                                dataSource={surveys.surveys_id?.сriteria}
                                                pagination={{
                                                    defaultPageSize: 10, 
                                                    hideOnSinglePage: true,
                                                }}
                                                renderItem={(item, index) => (
                                                <List.Item actions={[
                                                    <FiEdit style={{color:'#faad14'}} onClick={() => show_tab_surveys_criteria(item?.id, index)} />,
                                                    <Popconfirm placement="topRight" onConfirm={() => clickDelSurveysCriteria({
                                                        id:item.id
                                                    })} 
                                                        title={
                                                            <Space direction="vertical">
                                                                <Text>Вы действительно хотите удалить данный критерий?</Text>
                                                            </Space>
                                                        } 
                                                    okText="Да" cancelText="Нет">
                                                        <FiTrash2 style={{color:'#f5222d',cursor:'pointer'}} />
                                                    </Popconfirm>
                                                ]}>
                                                    <List.Item.Meta
                                                        title={
                                                            <span><b>{item.count ? `до ${item.count} ${decnum(item.count,[ 'балла','баллов','баллов' ])} ` : 'критерий не заполнен'}</b></span>
                                                        }
                                                    />
                                                </List.Item>
                                                )}
                                            />
                                            <div className="fcenter">
                                                <Button className="submit_mini" icon={<PlusOutlined />} onClick={() => clickAddSurveysCriteria()} />
                                            </div>
                                        </div>
                                    },
                                    {
                                        key: 'emails',
                                        label: `Email для отправки`,
                                        children: 
                                        <div>
                                            <List
                                                locale={{ emptyText: <Empty description="Список пуст" />}} 
                                                itemLayout="horizontal"
                                                dataSource={surveys.surveys_id?.emails}
                                                pagination={{
                                                    defaultPageSize: 10, 
                                                    hideOnSinglePage: true,
                                                }}
                                                renderItem={item => (
                                                <List.Item actions={[
                                                    <FiEdit style={{color:'#faad14'}} onClick={() => show_tab_surveys_emails(item?.id)} />,
                                                    <Popconfirm placement="topRight" onConfirm={() => clickDelSurveysEmails({
                                                        id:item.id
                                                    })} 
                                                        title={
                                                            <Space direction="vertical">
                                                                <Text>Вы действительно хотите удалить данный Email?</Text>
                                                            </Space>
                                                        } 
                                                    okText="Да" cancelText="Нет">
                                                        <FiTrash2 style={{color:'#f5222d',cursor:'pointer'}} />
                                                    </Popconfirm>
                                                ]}>
                                                    <List.Item.Meta
                                                        title={
                                                            <span><b>{item.email ? item.email : 'Email не заполнен'}</b></span>
                                                        }
                                                    />
                                                </List.Item>
                                                )}
                                            />
                                            <div className="fcenter">
                                                <Button className="submit_mini" icon={<PlusOutlined />} onClick={() => clickAddSurveysEmails()} />
                                            </div>
                                        </div>
                                    }
                                ]} 
                            />
                        </div>
                    }
                </div>
            </Modal>
            <Modal mask={false} className="modal_custom" key="m16" width={900} title="Изменение вопроса" open={app_global.panel.indexOf( 'surveysQuestions' ) != -1 } onOk={() => hide_tab_surveys_questions()} onCancel={() => hide_tab_surveys_questions()} footer={null}>
                <div className='header'>
                    <h2>Изменение вопроса к опросу</h2>
                    <h3>
                        {Object(surveys.surveys_id).length > 0 ?
                            <Skeleton.Button style={{marginBottom: '5px', maxWidth: '300px'}} size="small" shape="round" active block />
                        :
                            surveys.surveys_id?.title
                        }
                    </h3>
                </div>
                <div className='custom_divider' />
                <div className='body'>
                    {Object(surveys.questions_id).length === 0 ?
                        <Skeleton active />
                    :
                        <>
                            <Form name="form" className="default_form" form={form_surveys_questions} onFinish={form_finish_surveys_questions} >
                                <Form.Item hidden name={`id`} rules={[{ required: true, message: `Пожалуйста, введите ID вопроса!` }]}>
                                    <Input disabled={surveys.loading} className='custom_input' />
                                </Form.Item>
                                <label style={{marginTop:20}}>Вопрос</label>
                                <Form.Item name={`text`} rules={[{ required: true, message: `Пожалуйста, введите вопрос!` }]}>
                                    <Input disabled={surveys.loading} className='custom_input' placeholder="" />
                                </Form.Item>
                                <div className='form_buttons'>
                                    <Button className='submit' htmlType="submit" loading={surveys.loading}>Сохранить</Button>
                                </div>
                                <label style={{marginTop:20}}>Ответы</label>
                            </Form>

                            <List
                                locale={{ emptyText: <Empty description="Список пуст" />}} 
                                itemLayout="horizontal"
                                dataSource={surveys.questions_id?.answers}
                                pagination={{
                                    defaultPageSize: 10, 
                                    hideOnSinglePage: true,
                                }}
                                renderItem={item => (
                                <List.Item actions={[
                                    <FiEdit style={{color:'#faad14'}} onClick={() => show_tab_surveys_questions_answers(item?.id)} />,
                                    <Popconfirm placement="topRight" onConfirm={() => clickDelSurveysQuestionsAnswers({
                                        id:item.id
                                    })} 
                                        title={
                                            <Space direction="vertical">
                                                <Text>Вы действительно хотите удалить данный ответ?</Text>
                                            </Space>
                                        } 
                                    okText="Да" cancelText="Нет">
                                        <FiTrash2 style={{color:'#f5222d',cursor:'pointer'}} />
                                    </Popconfirm>
                                ]}>
                                    <List.Item.Meta
                                        title={
                                            <span style={{display:'flex',gap:5}}><Tag color="#55acee">{item.scores}</Tag><b>{item.text ? item.text : 'ответ не заполнен'}</b></span>
                                        }
                                    />
                                </List.Item>
                                )}
                            />
                            <div className="fcenter">
                                <Button className="submit_mini" icon={<PlusOutlined />} onClick={() => clickAddSurveysQuestionsAnswers()} />
                            </div>
                        </>
                    }
                </div>
            </Modal>
            <Modal mask={false} className="modal_custom" key="m17" width={900} title="Изменение вопроса" open={app_global.panel.indexOf( 'surveysQuestionsAnswers' ) != -1 } onOk={() => hide_tab_surveys_questions_answers()} onCancel={() => hide_tab_surveys_questions_answers()} footer={null}>
                <div className='header'>
                    <h2>Изменение ответа к вопросу</h2>
                    <h3>
                        {Object(surveys.questions_id).length > 0 ?
                            <Skeleton.Button style={{marginBottom: '5px', maxWidth: '300px'}} size="small" shape="round" active block />
                        :
                            surveys.questions_id?.title
                        }
                    </h3>
                </div>
                <div className='custom_divider' />
                <div className='body'>
                    {Object(surveys.answers_id).length === 0 ?
                        <Skeleton active />
                    :
                        <>
                            <Form name="form" className="default_form" form={form_surveys_questions_answers} onFinish={form_finish_surveys_questions_answers} >
                                <Form.Item hidden name={`id`} rules={[{ required: true, message: `Пожалуйста, введите ID ответа!` }]}>
                                    <Input disabled={surveys.loading} className='custom_input' />
                                </Form.Item>
                                <label style={{marginTop:20}}>Ответ</label>
                                <Form.Item name={`text`} rules={[{ required: true, message: `Пожалуйста, введите ответ!` }]}>
                                    <Input disabled={surveys.loading} className='custom_input' placeholder="" />
                                </Form.Item>
                                <label style={{marginTop:20}}>Количество баллов</label>
                                <Form.Item name={`scores`} rules={[{ required: true, message: `Пожалуйста, введите количество баллов!` }]}>
                                    <InputNumber min={1} disabled={surveys.loading} className='custom_input' placeholder="" />
                                </Form.Item>
                                <div className='form_buttons'>
                                    <Button className='submit' htmlType="submit" loading={surveys.loading}>Сохранить</Button>
                                </div>
                            </Form>
                        </>
                    }
                </div>
            </Modal>
            <Modal mask={false} className="modal_custom" key="m18" width={900} title="Изменение вопроса" open={app_global.panel.indexOf( 'surveysEmails' ) != -1 } onOk={() => hide_tab_surveys_emails()} onCancel={() => hide_tab_surveys_emails()} footer={null}>
                <div className='header'>
                    <h2>Изменение Email к опросу</h2>
                    <h3>
                        {Object(surveys.surveys_id).length > 0 ?
                            <Skeleton.Button style={{marginBottom: '5px', maxWidth: '300px'}} size="small" shape="round" active block />
                        :
                            surveys.surveys_id?.title
                        }
                    </h3>
                </div>
                <div className='custom_divider' />
                <div className='body'>
                    {Object(surveys.surveys_id).length === 0 ?
                        <Skeleton active />
                    :
                        <>
                            <Form name="form" className="default_form" form={form_surveys_emails} onFinish={form_finish_surveys_emails} >
                                <Form.Item hidden name={`id`} rules={[{ required: true, message: `Пожалуйста, введите ID Email!` }]}>
                                    <Input disabled={surveys.loading} className='custom_input' />
                                </Form.Item>
                                <label style={{marginTop:20}}>Email</label>
                                <Form.Item name={`email`} rules={[{ required: true, message: `Пожалуйста, введите email!` }]}>
                                    <MaskedInput
                                        disabled={surveys.loading}
                                        mask={emailMask}
                                        placeholder="mail@52c.ru"
                                        render={(ref, props) => (
                                            <input className="ant-input custom_input" type="text" aria-required="true" ref={ref} {...props} />
                                        )}
                                    />
                                </Form.Item>
                                <div className='form_buttons'>
                                    <Button className='submit' htmlType="submit" loading={surveys.loading}>Сохранить</Button>
                                </div>
                            </Form>
                        </>
                    }
                </div>
            </Modal>
            <Modal mask={false} className="modal_custom" key="m19" width={900} title="Изменение критерия" open={app_global.panel.indexOf( 'surveysCriteria' ) != -1 } onOk={() => hide_tab_surveys_criteria()} onCancel={() => hide_tab_surveys_criteria()} footer={null}>
                <div className='header'>
                    <h2>Изменение критерия к опросу</h2>
                    <h3>
                        {Object(surveys.surveys_id).length > 0 ?
                            <Skeleton.Button style={{marginBottom: '5px', maxWidth: '300px'}} size="small" shape="round" active block />
                        :
                            surveys.surveys_id?.title
                        }
                    </h3>
                </div>
                <div className='custom_divider' />
                <div className='body'>
                    {Object(surveys.criteria_id).length === 0 ?
                        <Skeleton active />
                    :
                        <>
                            <Form name="form" className="default_form" form={form_surveys_criteria} onFinish={form_finish_surveys_criteria} >
                                <Form.Item hidden name={`id`} rules={[{ required: true, message: `Пожалуйста, введите ID критерия!` }]}>
                                    <Input disabled={surveys.loading} className='custom_input' />
                                </Form.Item>
                                <label style={{marginTop:20}}>Критерий от {surveys_criteria_min_count} {decnum(surveys_criteria_min_count,[ 'балла','баллов','баллов' ])}. Максимум до {surveys.surveys_id?.count_scores} {decnum(surveys.surveys_id?.count_scores,[ 'балла','баллов','баллов' ])}</label>
                                <Form.Item name={`count`} rules={[{ required: true, message: `Пожалуйста, введите количество баллов!` }]}>
                                    <InputNumber min={surveys_criteria_min_count} max={surveys.surveys_id?.count_scores} disabled={surveys.loading} className='custom_input' placeholder="" />
                                </Form.Item>
                                <label style={{marginTop:20}}>Текст</label>
                                <ReactEditorJS 
                                    token={profile.token}
                                    API={API}
                                    ref={editorCoreSurveysCriteria}
                                    onInitialize={handleInitializeSurveysCriteria}
                                    defaultValue={surveys.criteria_id?.content !== undefined ? JSON.parse(surveys.criteria_id?.content) : {}}
                                />
                                <Form.Item name={`content`} hidden >
                                    <TextArea rows={4} disabled />
                                </Form.Item>
                                <div className='form_buttons'>
                                    <Button className='submit' htmlType="submit" loading={surveys.loading}>Сохранить</Button>
                                </div>
                            </Form>
                        </>
                    }
                </div>
            </Modal>
            <Modal mask={false} className="modal_custom" key="m20" width={900} title="Изменение пункта" open={app_global.panel.indexOf( 'organizationServices' ) != -1 } onOk={() => hide_tab_organization_services()} onCancel={() => hide_tab_organization_services()} footer={null}>
                <div className='header'>
                    <h2>Изменение пункта времени работы</h2>
                    <h3>
                        {Object(organization.organization_id).length > 0 ?
                            <Skeleton.Button style={{marginBottom: '5px', maxWidth: '300px'}} size="small" shape="round" active block />
                        :
                            organization.organization_id?.orgname
                        }
                    </h3>
                </div>
                <div className='custom_divider' />
                <div className='body'>
                    <Form name="form" className="default_form" form={form_organization_services} onFinish={form_finish_organization_services} >
                        <Form.Item hidden name={`id`} rules={[{ required: true, message: `Пожалуйста, введите ID пункта!` }]}>
                            <Input disabled={organization.loading} className='custom_input' />
                        </Form.Item>
                        <label style={{marginTop:20}}>Название услуги</label>
                        <Form.Item name={`name`} rules={[{ required: true, message: `Пожалуйста, введите название услуги!` }]}>
                            <Input disabled={organization.loading} className='custom_input' placeholder="Консультация" />
                        </Form.Item>
                        <label style={{marginTop:20}}>Стоимость</label>
                        <Form.Item name={`amount`} >
                            <InputNumber disabled={organization.loading} min={1} className='custom_input' placeholder="100000" />
                        </Form.Item>
                        <div className='form_buttons'>
                            <Button className='submit' htmlType="submit" loading={organization.loading}>Сохранить</Button>
                        </div>
                    </Form>
                </div>
            </Modal>
            <Modal mask centered className="modal_custom" key="m21" width={700} title="Подтверждение телефона" open={app_global.panel.indexOf( 'confirmProfilePhone' ) != -1 } onOk={() => hide_modal_confirm_profile_phone()} onCancel={() => hide_modal_confirm_profile_phone()} footer={null}>
                <div className='header'>
                    <h2>Подтверждение телефона</h2>
                    <h3>Введите код из SMS</h3>
                </div>
                <div className="custom_divider"></div>
                <div className='body'>
                    {profile.auth ?
                        <div className="block_pin">
                            {profile.loading ?
                                <Skeleton.Button style={{marginBottom: '20px',width:260,height:50}} shape="round" active block={false} />
                            :
                                <ReactCodeInput
                                    type="number"
                                    fields={6}
                                    onChange={(e) => form_finish_profile_phone_confirm(e)}
                                />
                            }
                        </div>
                        :
                        <>
                            <div className='result'>
                                <>
                                    <h3>Вы не авторизованы</h3>
                                </>
                            </div>
                        </>
                    }
                </div>
                <div className='footer'>
                    
                </div>
            </Modal>
            <Modal mask={false} className="modal_custom" key="m22" width={1600} title="Добавление специалиста" open={app_global.panel.indexOf( 'specialistAdd' ) != -1 } onOk={() => hide_tab_specialist_my_add()} onCancel={() => hide_tab_specialist_my_add()} footer={null}>
                <div className='header'>
                    <h2>Добавление специалиста</h2>
                </div>
                <div className='custom_divider' />
                <div className='body'>
                    <div>
                        <Form name="form" className="default_form" form={form_specialist_add} onFinish={form_finish_specialist_add}>
                            <label>ФИО</label>
                            <Form.Item name={`name`} rules={[{ required: true, message: `Пожалуйста, введите ФИО!` }]}>
                                <Input disabled={specialist.loading} className='custom_input' placeholder={`${randomUser['lastname']} ${randomUser['firstname']} ${randomUser['patronymic']}`} />
                            </Form.Item>
                            <label>Пол</label>
                            <Form.Item name={`gender`} rules={[{ required: true, message: `Пожалуйста, укажите пол!` }]}>
                                <Select
                                    disabled={specialist.loading}
                                    className="custom_select"
                                    showSearch
                                    placeholder="Выбрать пол"
                                    optionFilterProp="children"
                                    filterOption={(input, option) =>
                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                    options={[
                                        {value:"1",label:"Мужской"},
                                        {value:"2",label:"Женский"}
                                    ]}
                                />
                            </Form.Item>
                            <label>Организация</label>
                            <Form.Item name={`organization_id`} rules={[{ required: true, message: `Пожалуйста, выберите организацию!` }]}>
                                <Select
                                    disabled={specialist.loading}
                                    className="custom_select"
                                    showSearch
                                    placeholder="Выбрать организацию"
                                    optionFilterProp="children"
                                    filterOption={(input, option) =>
                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                    options={[{label:'Без организации',value:'-1'},...specialist.specialist_organization]}
                                />
                            </Form.Item>
                            <label>Стаж (количество лет)</label>
                            <Form.Item name={`experience`} >
                                <InputNumber min={0} disabled={specialist.loading} className='custom_input' placeholder={`14`} />
                            </Form.Item>
                            <label>Специализация (основная)</label>
                            <Form.Item name={`specialization`} >
                                <Input disabled={specialist.loading} className='custom_input' placeholder={`Врач-стоматолог`} />
                            </Form.Item>
                            <Form.Item
                                noStyle
                                shouldUpdate={(prevValues, currentValues) => prevValues[`organization_id`] !== currentValues[`organization_id`]}
                            >
                                {({ getFieldValue }) =>
                                    (getFieldValue(`organization_id`) === '-1')  ? (
                                        <>
                                            <label>Город</label>
                                            <Form.Item name={`personal_location`} rules={[{ required: true, message: `Пожалуйста, укажите город` }]}>
                                                <Select
                                                    disabled={organization.loading}
                                                    className="custom_select"
                                                    showSearch
                                                    placeholder="Выбрать город"
                                                    optionFilterProp="children"
                                                    filterOption={(input, option) =>
                                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                    }
                                                    options={locations.locations.map((item, indexedDB) => ({value:item.id,label:item.city}))}
                                                />
                                            </Form.Item>
                                        </>
                                    ) : null
                                }
                            </Form.Item>
                            
                            <div className='form_buttons'>
                                <Button className='submit' htmlType="submit" loading={specialist.loading}>Сохранить</Button>
                            </div>
                        </Form>
                    </div>
                </div>
            </Modal>
            <Modal mask={false} className="modal_custom" key="m23" width={1600} title="Изменение информации" open={app_global.panel.indexOf( 'specialist' ) != -1 } onOk={() => hide_tab_specialist_item()} onCancel={() => hide_tab_specialist_item()} footer={null}>
                <div className='header'>
                    <h2>Изменение информации</h2>
                    <h3>
                        {Object(specialist.specialist_id).length === 0 ?
                            <Skeleton.Button style={{marginBottom: '5px', maxWidth: '300px'}} size="small" shape="round" active block />
                        :
                            <>{getOrgStatusTag(specialist.specialist_id?.status)} {specialist.specialist_id?.name}</>
                        }
                    </h3>
                </div>
                <div className='custom_divider' />
                <div className='body'>
                    {Object(specialist.specialist_id).length === 0 ?
                        <Skeleton active />
                    :
                        <div className="custom-tabs">
                            <Tabs
                                tabPosition={Object.entries(screens)
                                    .filter((screen) => !!screen[1])
                                    .map((screen) => (
                                        screen[0]
                                )).indexOf( 'xl' ) != -1 ? 'left' : 'top'}
                                activeKey={activeTabOrgEdit}
                                defaultActiveKey="data"
                                onChange={tabOrgEditChange}
                                className="profile_tabs"
                                items={[
                                    {
                                        key: 'data',
                                        label: `Основная информация`,
                                        children: 
                                        <div>
                                            <Form name="form" className="default_form" form={form_specialist_data} onFinish={form_finish_specialist_data}>
                                                {["3","4"].indexOf( profile?.data?.type ) !== -1 ?
                                                    <label>Статус</label>
                                                :
                                                    null
                                                }
                                                <Form.Item name={`status`} hidden={["3","4"].indexOf( profile?.data?.type ) === -1} >
                                                    <Select
                                                        disabled={app_global.loading}
                                                        className="custom_select"
                                                        showSearch
                                                        placeholder="Выбрать статус"
                                                        optionFilterProp="children"
                                                        filterOption={(input, option) =>
                                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                        }
                                                        options={[
                                                            {value:"publish",label:"Опубликовано"},
                                                            {value:"moderation",label:"На проверке"},
                                                            {value:"draft",label:"Черновик"},
                                                            {value:"cancel",label:"Отклонено"}
                                                        ]}
                                                    />
                                                </Form.Item>
                                                <label>ФИО</label>
                                                <Form.Item name={`name`} rules={[{ required: true, message: `Пожалуйста, введите ФИО!` }]}>
                                                    <Input disabled={specialist.loading} className='custom_input' placeholder={`${randomUser['lastname']} ${randomUser['firstname']} ${randomUser['patronymic']}`} />
                                                </Form.Item>
                                                <label>Пол</label>
                                                <Form.Item name={`gender`} rules={[{ required: true, message: `Пожалуйста, укажите пол!` }]}>
                                                    <Select
                                                        disabled={specialist.loading}
                                                        className="custom_select"
                                                        showSearch
                                                        placeholder="Выбрать пол"
                                                        optionFilterProp="children"
                                                        filterOption={(input, option) =>
                                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                        }
                                                        options={[
                                                            {value:"1",label:"Мужской"},
                                                            {value:"2",label:"Женский"}
                                                        ]}
                                                    />
                                                </Form.Item>
                                                <label>Основная организация</label>
                                                <Form.Item name={`organization_id`} rules={[{ required: true, message: `Пожалуйста, выберите организацию!` }]}>
                                                    <Select
                                                        disabled={specialist.loading}
                                                        className="custom_select"
                                                        showSearch
                                                        placeholder="Выбрать организацию"
                                                        optionFilterProp="children"
                                                        filterOption={(input, option) =>
                                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                        }
                                                        options={[{label:'Без организации',value:'-1'},...specialist.specialist_organization]}
                                                    />
                                                </Form.Item>
                                                <label>Организации</label>
                                                <Form.Item name={`organizations`} >
                                                    <Select
                                                        mode="tags"
                                                        disabled={specialist.loading}
                                                        className="custom_select"
                                                        placeholder="Выбрать организацию"
                                                        optionFilterProp="children"
                                                        filterOption={(input, option) =>
                                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                        }
                                                        options={specialist.specialist_organization}
                                                    />
                                                </Form.Item>
                                                <label>Стаж (количество лет)</label>
                                                <Form.Item name={`experience`} >
                                                    <InputNumber min={0} disabled={specialist.loading} className='custom_input' placeholder={`14`} />
                                                </Form.Item>
                                                <label>Специализация (основная)</label>
                                                <Form.Item name={`specialization`} >
                                                    <Input disabled={specialist.loading} className='custom_input' placeholder={`Врач-стоматолог`} />
                                                </Form.Item>
                                                <label>Специализации</label>
                                                <Form.Item name={`specializations`} >
                                                    <Select
                                                        mode="tags"
                                                        disabled={specialist.loading}
                                                        className="custom_select"
                                                        placeholder="Выбрать специализацию"
                                                        options={specialist.specialist_specialization}
                                                    />
                                                </Form.Item>
                                                <Form.Item
                                                    noStyle
                                                    shouldUpdate={(prevValues, currentValues) => prevValues[`organization_id`] !== currentValues[`organization_id`]}
                                                >
                                                    {({ getFieldValue }) =>
                                                        (getFieldValue(`organization_id`) === '-1')  ? (
                                                            <>
                                                                <label>Город</label>
                                                                <Form.Item name={`personal_location`} rules={[{ required: true, message: `Пожалуйста, укажите город` }]}>
                                                                    <Select
                                                                        disabled={organization.loading}
                                                                        className="custom_select"
                                                                        showSearch
                                                                        placeholder="Выбрать город"
                                                                        optionFilterProp="children"
                                                                        filterOption={(input, option) =>
                                                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                                        }
                                                                        options={locations.locations.map((item, indexedDB) => ({value:item.id,label:item.city}))}
                                                                    />
                                                                </Form.Item>
                                                            </>
                                                        ) : null
                                                    }
                                                </Form.Item>
                                                <div className='form_buttons'>
                                                    {(specialist.specialist_id?.status === 'draft' && ["2","5"].indexOf( profile?.data?.type ) !== -1) ?
                                                        <Button className='button_left' onClick={() => clickUpdSpecialistModeration()}>Отправить на проверку</Button>
                                                    :
                                                        null
                                                    }
                                                    {(["publish","moderation"].indexOf( specialist.specialist_id?.status ) !== -1 && ["2","5"].indexOf( profile?.data?.type ) !== -1) ?
                                                        <Button className='button_left' onClick={() => clickUpdSpecialistDraft()}>Скрыть анкету</Button>
                                                    :
                                                        null
                                                    }
                                                    <Button className='submit' htmlType="submit" loading={specialist.loading}>Сохранить</Button>
                                                </div>
                                            </Form>
                                        </div>
                                    },
                                    {
                                        key: 'seo',
                                        label: `SEO`,
                                        children: 
                                        <div>
                                            <Form name="form" className="default_form" form={form_specialist_seo} onFinish={form_finish_specialist_seo}>
                                                <label>Заголовок</label>
                                                <Form.Item name={`seo_title`} rules={[{ required: true, message: `Пожалуйста, введите заголовок для поисковиков!` }]}>
                                                    <Input disabled={specialist.loading} className='custom_input' placeholder={`Заголовок`} />
                                                </Form.Item>
                                                <label>Описание</label>
                                                <Form.Item name={`seo_description`} >
                                                    <Input disabled={specialist.loading} className='custom_input' placeholder={`Описание`} />
                                                </Form.Item>
                                                <label>Ключевые слова</label>
                                                <Form.Item name={`seo_keywords`} >
                                                    <Input disabled={specialist.loading} className='custom_input' placeholder={`Ключевые слова`} />
                                                </Form.Item>
                                                <div className='form_buttons'>
                                                    <Button className='submit' htmlType="submit" loading={specialist.loading}>Сохранить</Button>
                                                </div>
                                            </Form>
                                        </div>
                                    },
                                    {
                                        key: 'location',
                                        label: `Геолокация`,
                                        disabled: specialist.specialist_id?.organization_id !== '-1',
                                        children: 
                                        <div>
                                            {locations.loading_search ?
                                                <Skeleton active />
                                            :
                                                <>
                                                    <Form name="form" className="default_form" form={form_specialist_location} >
                                                        <label>Текущий адрес</label>
                                                        <Form.Item name={`address`} >
                                                            <Input disabled className='custom_input' placeholder={`Нижний Новгород, Нижегородский Кремль`} />
                                                        </Form.Item>
                                                        <label>Широта</label>
                                                        <Form.Item name={`latitude`} >
                                                            <Input disabled className='custom_input' placeholder={`56.238377`} />
                                                        </Form.Item>
                                                        <label>Долгота</label>
                                                        <Form.Item name={`longitude`} >
                                                            <Input disabled className='custom_input' placeholder={`43.461625`} />
                                                        </Form.Item>
                                                    </Form>
                                                    <div className='custom_divider' />
                                                    <Form name="form" className="default_form" form={form_locations_search_search} onFinish={form_finish_locations_search}>
                                                        <Form.Item key="search" name="search">
                                                            <Input className={`custom_input`} placeholder="Поиск по адресу" bordered={false} 
                                                                prefix={
                                                                    <SearchOutlined style={{ color: 'rgba(0,0,0,.45)', fontSize: 20 }} />
                                                                }
                                                                suffix={
                                                                    <Button className='submit_mini' htmlType="submit" loading={locations.loading_search}>Поиск</Button>
                                                                }
                                                            />
                                                        </Form.Item>
                                                    </Form>
                                                    <List
                                                        locale={{ emptyText: <Empty description="Список пуст" />}} 
                                                        className="organization_list"
                                                        dataSource={obj.locations_search}
                                                        pagination={{
                                                            defaultPageSize: 5, 
                                                            hideOnSinglePage: true,
                                                        }}
                                                        renderItem={(item) => (
                                                            <List.Item
                                                                key={item.id}
                                                                actions={[
                                                                    <Button className='submit_mini' loading={locations.loading_search} onClick={() => specialist_location_select({
                                                                        address:item.address,
                                                                        latitude:item.latitude,
                                                                        longitude:item.longitude
                                                                    })} >Выбрать</Button>
                                                                ]}
                                                            >
                                                                <span className="orgname">{item?.address}</span>
                                                            </List.Item>
                                                        )}
                                                    />
                                                    <div className="map" style={{height:'fit-content'}}>
                                                        <YMaps style={{marginTop:20}}>
                                                            <Map defaultState={{ center: ["56.328259", "44.003193"], zoom: 9 }} height="400px" width="100%" >
                                                                {obj.locations_search.length > 0 ? 
                                                                    obj.locations_search.map((item, index) => 
                                                                        <Placemark key={index} geometry={[item.latitude, item.longitude]} />
                                                                    )
                                                                :
                                                                    <Placemark key="global" geometry={[specialist.specialist_id.latitude, specialist.specialist_id.longitude]} />
                                                                }
                                                            </Map>
                                                        </YMaps>
                                                    </div>
                                                </>
                                            }
                                        </div>
                                    },
                                    {
                                        key: 'phones',
                                        label: `Контактные номера`,
                                        disabled: specialist.specialist_id?.organization_id !== '-1',
                                        children: 
                                        <div>
                                            <List
                                                locale={{ emptyText: <Empty description="Список пуст" />}} 
                                                itemLayout="horizontal"
                                                dataSource={specialist.specialist_id.phones}
                                                pagination={{
                                                    defaultPageSize: 10, 
                                                    hideOnSinglePage: true,
                                                }}
                                                renderItem={item => (
                                                <List.Item actions={[
                                                    <FiEdit style={{color:'#faad14'}} onClick={() => show_tab_specialist_phone({
                                                        id:item.id,
                                                        phone:item.phone,
                                                        more:item.more
                                                    })} />,
                                                    <Popconfirm placement="topRight" onConfirm={() => clickDelSpecialistPhone({
                                                        id:item.id
                                                    })} 
                                                        title={
                                                            <Space direction="vertical">
                                                                <Text>Вы действительно хотите удалить данный номер?</Text>
                                                            </Space>
                                                        } 
                                                    okText="Да" cancelText="Нет">
                                                        <FiTrash2 style={{color:'#f5222d',cursor:'pointer'}} />
                                                    </Popconfirm>
                                                ]}>
                                                    <List.Item.Meta
                                                        title={`${customPhoneMask(item.phone)} ${item.more ? `(доб. ${item.more})`:''}`}
                                                    />
                                                </List.Item>
                                                )}
                                            />
                                            <div className="fcenter">
                                                <Button className="submit_mini" icon={<PlusOutlined />} onClick={() => clickAddSpecialistPhone()} />
                                            </div>
                                        </div>
                                    },
                                    {
                                        key: 'education',
                                        label: `Образование`,
                                        children: 
                                        <div>
                                            <List
                                                locale={{ emptyText: <Empty description="Список пуст" />}} 
                                                itemLayout="horizontal"
                                                dataSource={specialist.specialist_id?.education_list}
                                                pagination={{
                                                    defaultPageSize: 10, 
                                                    hideOnSinglePage: true,
                                                }}
                                                renderItem={item => (
                                                <List.Item actions={[
                                                    <FiEdit style={{color:'#faad14'}} onClick={() => show_tab_specialist_education({
                                                        id:item.id,
                                                        date:item.date,
                                                        text:item.text
                                                    })} />,
                                                    <Popconfirm placement="topRight" onConfirm={() => clickDelSpecialistEducation({
                                                        id:item.id
                                                    })} 
                                                        title={
                                                            <Space direction="vertical">
                                                                <Text>Вы действительно хотите удалить данный пункт?</Text>
                                                            </Space>
                                                        } 
                                                    okText="Да" cancelText="Нет">
                                                        <FiTrash2 style={{color:'#f5222d',cursor:'pointer'}} />
                                                    </Popconfirm>
                                                ]}>
                                                    <List.Item.Meta
                                                        title={
                                                            <span><b>{item.date ? item.date : 'Дата не заполнена'}</b> {item.text ? item.text : 'описание не заполнено'}</span>
                                                        }
                                                    />
                                                </List.Item>
                                                )}
                                            />
                                            <div className="fcenter">
                                                <Button className="submit_mini" icon={<PlusOutlined />} onClick={() => clickAddSpecialistEducation()} />
                                            </div>
                                        </div>
                                    },
                                    {
                                        key: 'experience',
                                        label: `Опыт и достижения`,
                                        children: 
                                        <div>
                                            <List
                                                locale={{ emptyText: <Empty description="Список пуст" />}} 
                                                itemLayout="horizontal"
                                                dataSource={specialist.specialist_id?.experience_list}
                                                pagination={{
                                                    defaultPageSize: 10, 
                                                    hideOnSinglePage: true,
                                                }}
                                                renderItem={item => (
                                                <List.Item actions={[
                                                    <FiEdit style={{color:'#faad14'}} onClick={() => show_tab_specialist_experience({
                                                        id:item.id,
                                                        text:item.text
                                                    })} />,
                                                    <Popconfirm placement="topRight" onConfirm={() => clickDelSpecialistExperience({
                                                        id:item.id
                                                    })} 
                                                        title={
                                                            <Space direction="vertical">
                                                                <Text>Вы действительно хотите удалить данный пункт?</Text>
                                                            </Space>
                                                        } 
                                                    okText="Да" cancelText="Нет">
                                                        <FiTrash2 style={{color:'#f5222d',cursor:'pointer'}} />
                                                    </Popconfirm>
                                                ]}>
                                                    <List.Item.Meta
                                                        title={
                                                            <span>{item.text ? item.text : 'Описание не заполнено'}</span>
                                                        }
                                                    />
                                                </List.Item>
                                                )}
                                            />
                                            <div className="fcenter">
                                                <Button className="submit_mini" icon={<PlusOutlined />} onClick={() => clickAddSpecialistExperience()} />
                                            </div>
                                        </div>
                                    },
                                    {
                                        key: 'photo',
                                        label: `Фотография`,
                                        children: 
                                        <div>
                                            <Form className="default_form">
                                                <div className="fcenter images_column">
                                                    {specialist.specialist_id?.photo === null ?
                                                        null
                                                    :
                                                        <div className='image_preview'>
                                                            <Image
                                                                className="image-preview"
                                                                height={100}
                                                                width={100}
                                                                src={`${UPLOADS}/${specialist.specialist_id?.photo}`}
                                                                preview={{
                                                                    src: `${UPLOADS}/${specialist.specialist_id?.photo}`
                                                                }}
                                                            />
                                                            <div className="float_remove">
                                                                <Popconfirm placement="topRight" onConfirm={() => specialist_photo_delete()} 
                                                                    title={
                                                                        <Space direction="vertical">
                                                                            <Text>Вы действительно хотите удалить данную фотогарфию?</Text>
                                                                        </Space>
                                                                    } 
                                                                okText="Да" cancelText="Нет">
                                                                    <FiTrash2 style={{color:'#f5222d',cursor:'pointer'}} />
                                                                </Popconfirm>
                                                            </div>
                                                        </div>
                                                    }
                                                    <Dragger
                                                        className="no-list"
                                                        {...upload_config_one_image}
                                                        {...upload_specialist_image}specialist
                                                        data={{'method':'upload_specialist_photo','token':profile?.token,'specialist_id':specialist.specialist_id?.id}}
                                                    >
                                                        <div className='upload_content'>
                                                            <div className='left'>
                                                                <svg width="42" height="36" viewBox="0 0 42 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                    <path d="M36.2727 15.1181C35.7664 15.1181 35.2808 15.3172 34.9228 15.6716C34.5648 16.026 34.3636 16.5067 34.3636 17.0079V30.2362C34.3636 30.7374 34.1625 31.2181 33.8045 31.5725C33.4465 31.9269 32.9609 32.126 32.4545 32.126H5.72727C5.22095 32.126 4.73537 31.9269 4.37734 31.5725C4.01932 31.2181 3.81818 30.7374 3.81818 30.2362V15.1181C3.81818 14.6169 4.01932 14.1362 4.37734 13.7818C4.73537 13.4274 5.22095 13.2283 5.72727 13.2283H9.54545C9.96176 13.2499 10.3737 13.1359 10.7185 12.9038C11.0632 12.6718 11.3217 12.3345 11.4545 11.9433L12.4855 8.84409C12.6135 8.46875 12.8575 8.14274 13.183 7.91214C13.5084 7.68155 13.8989 7.55803 14.2991 7.55906H24.8182C25.3245 7.55906 25.8101 7.35996 26.1681 7.00556C26.5261 6.65116 26.7273 6.17049 26.7273 5.66929C26.7273 5.1681 26.5261 4.68743 26.1681 4.33303C25.8101 3.97863 25.3245 3.77953 24.8182 3.77953H14.2036C13.0039 3.78175 11.8351 4.15688 10.8623 4.85198C9.88947 5.54708 9.16174 6.52703 8.78182 7.65354L8.17091 9.54331H5.72727C4.20831 9.54331 2.75155 10.1406 1.67748 11.2038C0.603407 12.267 0 13.709 0 15.2126V30.3307C0 31.8343 0.603407 33.2763 1.67748 34.3395C2.75155 35.4027 4.20831 36 5.72727 36H32.4545C33.9735 36 35.4303 35.4027 36.5043 34.3395C37.5784 33.2763 38.1818 31.8343 38.1818 30.3307V17.1024C38.1948 16.8464 38.155 16.5904 38.0648 16.3501C37.9747 16.1098 37.8361 15.8902 37.6576 15.7046C37.479 15.519 37.2641 15.3713 37.026 15.2704C36.788 15.1696 36.5317 15.1178 36.2727 15.1181ZM19.0909 13.2283C17.5806 13.2283 16.1042 13.6717 14.8484 14.5023C13.5926 15.3329 12.6138 16.5134 12.0358 17.8947C11.4579 19.2759 11.3066 20.7958 11.6013 22.2621C11.8959 23.7284 12.6232 25.0753 13.6912 26.1325C14.7591 27.1896 16.1198 27.9095 17.6011 28.2012C19.0824 28.4929 20.6179 28.3432 22.0132 27.7711C23.4086 27.1989 24.6012 26.2301 25.4403 24.987C26.2794 23.7439 26.7273 22.2824 26.7273 20.7874C26.7273 18.7826 25.9227 16.8599 24.4906 15.4423C23.0585 14.0247 21.1162 13.2283 19.0909 13.2283ZM19.0909 24.5669C18.3357 24.5669 17.5975 24.3453 16.9696 23.93C16.3417 23.5147 15.8524 22.9244 15.5634 22.2338C15.2744 21.5431 15.1988 20.7832 15.3461 20.0501C15.4934 19.3169 15.8571 18.6434 16.391 18.1149C16.925 17.5863 17.6054 17.2263 18.346 17.0805C19.0867 16.9347 19.8544 17.0095 20.5521 17.2956C21.2497 17.5816 21.8461 18.0661 22.2656 18.6876C22.6852 19.3091 22.9091 20.0399 22.9091 20.7874C22.9091 21.7898 22.5068 22.7511 21.7908 23.4599C21.0747 24.1687 20.1036 24.5669 19.0909 24.5669ZM40.0909 3.77953H38.1818V1.88976C38.1818 1.38857 37.9807 0.907898 37.6227 0.553499C37.2646 0.199099 36.779 0 36.2727 0C35.7664 0 35.2808 0.199099 34.9228 0.553499C34.5648 0.907898 34.3636 1.38857 34.3636 1.88976V3.77953H32.4545C31.9482 3.77953 31.4626 3.97863 31.1046 4.33303C30.7466 4.68743 30.5455 5.1681 30.5455 5.66929C30.5455 6.17049 30.7466 6.65116 31.1046 7.00556C31.4626 7.35996 31.9482 7.55906 32.4545 7.55906H34.3636V9.44882C34.3636 9.95002 34.5648 10.4307 34.9228 10.7851C35.2808 11.1395 35.7664 11.3386 36.2727 11.3386C36.779 11.3386 37.2646 11.1395 37.6227 10.7851C37.9807 10.4307 38.1818 9.95002 38.1818 9.44882V7.55906H40.0909C40.5972 7.55906 41.0828 7.35996 41.4408 7.00556C41.7989 6.65116 42 6.17049 42 5.66929C42 5.1681 41.7989 4.68743 41.4408 4.33303C41.0828 3.97863 40.5972 3.77953 40.0909 3.77953Z" fill="#9B9B9B"/>
                                                                </svg>
                                                            </div>
                                                            <div className='right'>
                                                                [Фотография] Нажмите на кнопку или перетащите фото в эту область в формате PNG.
                                                            </div>
                                                        </div>
                                                    </Dragger>
                                                </div>
                                            </Form>
                                        </div>
                                    },
                                    {
                                        key: 'photos',
                                        label: `Фотографии`,
                                        children: 
                                        <div>
                                            <List
                                                locale={{ emptyText: <Empty description="Список пуст" />}} 
                                                itemLayout="horizontal"
                                                dataSource={specialist.specialist_id.photos}
                                                pagination={{
                                                    defaultPageSize: 10, 
                                                    hideOnSinglePage: true,
                                                }}
                                                renderItem={item => (
                                                <List.Item actions={[
                                                    <Popconfirm placement="topRight" onConfirm={() => clickDelSpecialistPhotos({
                                                        id:item.id
                                                    })} 
                                                        title={
                                                            <Space direction="vertical">
                                                                <Text>Вы действительно хотите удалить данное изображение?</Text>
                                                            </Space>
                                                        } 
                                                    okText="Да" cancelText="Нет">
                                                        <FiTrash2 style={{color:'#f5222d',cursor:'pointer'}} />
                                                    </Popconfirm>
                                                ]}>
                                                    <List.Item.Meta
                                                        title={
                                                            <span className="item-list-items">
                                                                <Image
                                                                    className="image-preview"
                                                                    height={50}
                                                                    width={50}
                                                                    src={`${UPLOADS}/${item?.file_preview}`}
                                                                    preview={{
                                                                        src: `${UPLOADS}/${item?.file}`
                                                                    }}
                                                                /> 
                                                                Добавлено {moment.unix(item?.date).fromNow()}
                                                            </span>
                                                        }
                                                    />
                                                </List.Item>
                                                )}
                                            />
                                            <div className="fcenter">
                                                <Dragger
                                                    className="no-list"
                                                    {...upload_config_multi_image}
                                                    {...upload_specialist_image}
                                                    data={{'method':'upload_specialist_photos','token':profile?.token,'specialist_id':specialist.specialist_id?.id}}
                                                >
                                                    <div className='upload_content'>
                                                        <div className='left'>
                                                            <svg width="42" height="36" viewBox="0 0 42 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                <path d="M36.2727 15.1181C35.7664 15.1181 35.2808 15.3172 34.9228 15.6716C34.5648 16.026 34.3636 16.5067 34.3636 17.0079V30.2362C34.3636 30.7374 34.1625 31.2181 33.8045 31.5725C33.4465 31.9269 32.9609 32.126 32.4545 32.126H5.72727C5.22095 32.126 4.73537 31.9269 4.37734 31.5725C4.01932 31.2181 3.81818 30.7374 3.81818 30.2362V15.1181C3.81818 14.6169 4.01932 14.1362 4.37734 13.7818C4.73537 13.4274 5.22095 13.2283 5.72727 13.2283H9.54545C9.96176 13.2499 10.3737 13.1359 10.7185 12.9038C11.0632 12.6718 11.3217 12.3345 11.4545 11.9433L12.4855 8.84409C12.6135 8.46875 12.8575 8.14274 13.183 7.91214C13.5084 7.68155 13.8989 7.55803 14.2991 7.55906H24.8182C25.3245 7.55906 25.8101 7.35996 26.1681 7.00556C26.5261 6.65116 26.7273 6.17049 26.7273 5.66929C26.7273 5.1681 26.5261 4.68743 26.1681 4.33303C25.8101 3.97863 25.3245 3.77953 24.8182 3.77953H14.2036C13.0039 3.78175 11.8351 4.15688 10.8623 4.85198C9.88947 5.54708 9.16174 6.52703 8.78182 7.65354L8.17091 9.54331H5.72727C4.20831 9.54331 2.75155 10.1406 1.67748 11.2038C0.603407 12.267 0 13.709 0 15.2126V30.3307C0 31.8343 0.603407 33.2763 1.67748 34.3395C2.75155 35.4027 4.20831 36 5.72727 36H32.4545C33.9735 36 35.4303 35.4027 36.5043 34.3395C37.5784 33.2763 38.1818 31.8343 38.1818 30.3307V17.1024C38.1948 16.8464 38.155 16.5904 38.0648 16.3501C37.9747 16.1098 37.8361 15.8902 37.6576 15.7046C37.479 15.519 37.2641 15.3713 37.026 15.2704C36.788 15.1696 36.5317 15.1178 36.2727 15.1181ZM19.0909 13.2283C17.5806 13.2283 16.1042 13.6717 14.8484 14.5023C13.5926 15.3329 12.6138 16.5134 12.0358 17.8947C11.4579 19.2759 11.3066 20.7958 11.6013 22.2621C11.8959 23.7284 12.6232 25.0753 13.6912 26.1325C14.7591 27.1896 16.1198 27.9095 17.6011 28.2012C19.0824 28.4929 20.6179 28.3432 22.0132 27.7711C23.4086 27.1989 24.6012 26.2301 25.4403 24.987C26.2794 23.7439 26.7273 22.2824 26.7273 20.7874C26.7273 18.7826 25.9227 16.8599 24.4906 15.4423C23.0585 14.0247 21.1162 13.2283 19.0909 13.2283ZM19.0909 24.5669C18.3357 24.5669 17.5975 24.3453 16.9696 23.93C16.3417 23.5147 15.8524 22.9244 15.5634 22.2338C15.2744 21.5431 15.1988 20.7832 15.3461 20.0501C15.4934 19.3169 15.8571 18.6434 16.391 18.1149C16.925 17.5863 17.6054 17.2263 18.346 17.0805C19.0867 16.9347 19.8544 17.0095 20.5521 17.2956C21.2497 17.5816 21.8461 18.0661 22.2656 18.6876C22.6852 19.3091 22.9091 20.0399 22.9091 20.7874C22.9091 21.7898 22.5068 22.7511 21.7908 23.4599C21.0747 24.1687 20.1036 24.5669 19.0909 24.5669ZM40.0909 3.77953H38.1818V1.88976C38.1818 1.38857 37.9807 0.907898 37.6227 0.553499C37.2646 0.199099 36.779 0 36.2727 0C35.7664 0 35.2808 0.199099 34.9228 0.553499C34.5648 0.907898 34.3636 1.38857 34.3636 1.88976V3.77953H32.4545C31.9482 3.77953 31.4626 3.97863 31.1046 4.33303C30.7466 4.68743 30.5455 5.1681 30.5455 5.66929C30.5455 6.17049 30.7466 6.65116 31.1046 7.00556C31.4626 7.35996 31.9482 7.55906 32.4545 7.55906H34.3636V9.44882C34.3636 9.95002 34.5648 10.4307 34.9228 10.7851C35.2808 11.1395 35.7664 11.3386 36.2727 11.3386C36.779 11.3386 37.2646 11.1395 37.6227 10.7851C37.9807 10.4307 38.1818 9.95002 38.1818 9.44882V7.55906H40.0909C40.5972 7.55906 41.0828 7.35996 41.4408 7.00556C41.7989 6.65116 42 6.17049 42 5.66929C42 5.1681 41.7989 4.68743 41.4408 4.33303C41.0828 3.97863 40.5972 3.77953 40.0909 3.77953Z" fill="#9B9B9B"/>
                                                            </svg>
                                                        </div>
                                                        <div className='right'>
                                                            Нажмите на кнопку или перетащите фото в эту область до 10 изображений в формате PNG, JPEG.
                                                        </div>
                                                    </div>
                                                </Dragger>
                                            </div>
                                        </div>
                                    },
                                    {
                                        key: 'certificates',
                                        label: `Сертификаты`,
                                        children: 
                                        <div>
                                            <List
                                                locale={{ emptyText: <Empty description="Список пуст" />}} 
                                                itemLayout="horizontal"
                                                dataSource={specialist.specialist_id.certificates}
                                                pagination={{
                                                    defaultPageSize: 10, 
                                                    hideOnSinglePage: true,
                                                }}
                                                renderItem={item => (
                                                <List.Item actions={[
                                                    <Popconfirm placement="topRight" onConfirm={() => clickDelSpecialistCertificates({
                                                        id:item.id
                                                    })} 
                                                        title={
                                                            <Space direction="vertical">
                                                                <Text>Вы действительно хотите удалить данное изображение?</Text>
                                                            </Space>
                                                        } 
                                                    okText="Да" cancelText="Нет">
                                                        <FiTrash2 style={{color:'#f5222d',cursor:'pointer'}} />
                                                    </Popconfirm>
                                                ]}>
                                                    <List.Item.Meta
                                                        title={
                                                            <span className="item-list-items">
                                                                <Image
                                                                    className="image-preview"
                                                                    height={50}
                                                                    width={50}
                                                                    src={`${UPLOADS}/${item?.file_preview}`}
                                                                    preview={{
                                                                        src: `${UPLOADS}/${item?.file}`
                                                                    }}
                                                                /> 
                                                                Добавлено {moment.unix(item?.date).fromNow()}
                                                            </span>
                                                        }
                                                    />
                                                </List.Item>
                                                )}
                                            />
                                            <div className="fcenter">
                                                <Dragger
                                                    className="no-list"
                                                    {...upload_config_multi_image}
                                                    {...upload_specialist_image}
                                                    data={{'method':'upload_specialist_certificates','token':profile?.token,'specialist_id':specialist.specialist_id?.id}}
                                                >
                                                    <div className='upload_content'>
                                                        <div className='left'>
                                                            <svg width="42" height="36" viewBox="0 0 42 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                <path d="M36.2727 15.1181C35.7664 15.1181 35.2808 15.3172 34.9228 15.6716C34.5648 16.026 34.3636 16.5067 34.3636 17.0079V30.2362C34.3636 30.7374 34.1625 31.2181 33.8045 31.5725C33.4465 31.9269 32.9609 32.126 32.4545 32.126H5.72727C5.22095 32.126 4.73537 31.9269 4.37734 31.5725C4.01932 31.2181 3.81818 30.7374 3.81818 30.2362V15.1181C3.81818 14.6169 4.01932 14.1362 4.37734 13.7818C4.73537 13.4274 5.22095 13.2283 5.72727 13.2283H9.54545C9.96176 13.2499 10.3737 13.1359 10.7185 12.9038C11.0632 12.6718 11.3217 12.3345 11.4545 11.9433L12.4855 8.84409C12.6135 8.46875 12.8575 8.14274 13.183 7.91214C13.5084 7.68155 13.8989 7.55803 14.2991 7.55906H24.8182C25.3245 7.55906 25.8101 7.35996 26.1681 7.00556C26.5261 6.65116 26.7273 6.17049 26.7273 5.66929C26.7273 5.1681 26.5261 4.68743 26.1681 4.33303C25.8101 3.97863 25.3245 3.77953 24.8182 3.77953H14.2036C13.0039 3.78175 11.8351 4.15688 10.8623 4.85198C9.88947 5.54708 9.16174 6.52703 8.78182 7.65354L8.17091 9.54331H5.72727C4.20831 9.54331 2.75155 10.1406 1.67748 11.2038C0.603407 12.267 0 13.709 0 15.2126V30.3307C0 31.8343 0.603407 33.2763 1.67748 34.3395C2.75155 35.4027 4.20831 36 5.72727 36H32.4545C33.9735 36 35.4303 35.4027 36.5043 34.3395C37.5784 33.2763 38.1818 31.8343 38.1818 30.3307V17.1024C38.1948 16.8464 38.155 16.5904 38.0648 16.3501C37.9747 16.1098 37.8361 15.8902 37.6576 15.7046C37.479 15.519 37.2641 15.3713 37.026 15.2704C36.788 15.1696 36.5317 15.1178 36.2727 15.1181ZM19.0909 13.2283C17.5806 13.2283 16.1042 13.6717 14.8484 14.5023C13.5926 15.3329 12.6138 16.5134 12.0358 17.8947C11.4579 19.2759 11.3066 20.7958 11.6013 22.2621C11.8959 23.7284 12.6232 25.0753 13.6912 26.1325C14.7591 27.1896 16.1198 27.9095 17.6011 28.2012C19.0824 28.4929 20.6179 28.3432 22.0132 27.7711C23.4086 27.1989 24.6012 26.2301 25.4403 24.987C26.2794 23.7439 26.7273 22.2824 26.7273 20.7874C26.7273 18.7826 25.9227 16.8599 24.4906 15.4423C23.0585 14.0247 21.1162 13.2283 19.0909 13.2283ZM19.0909 24.5669C18.3357 24.5669 17.5975 24.3453 16.9696 23.93C16.3417 23.5147 15.8524 22.9244 15.5634 22.2338C15.2744 21.5431 15.1988 20.7832 15.3461 20.0501C15.4934 19.3169 15.8571 18.6434 16.391 18.1149C16.925 17.5863 17.6054 17.2263 18.346 17.0805C19.0867 16.9347 19.8544 17.0095 20.5521 17.2956C21.2497 17.5816 21.8461 18.0661 22.2656 18.6876C22.6852 19.3091 22.9091 20.0399 22.9091 20.7874C22.9091 21.7898 22.5068 22.7511 21.7908 23.4599C21.0747 24.1687 20.1036 24.5669 19.0909 24.5669ZM40.0909 3.77953H38.1818V1.88976C38.1818 1.38857 37.9807 0.907898 37.6227 0.553499C37.2646 0.199099 36.779 0 36.2727 0C35.7664 0 35.2808 0.199099 34.9228 0.553499C34.5648 0.907898 34.3636 1.38857 34.3636 1.88976V3.77953H32.4545C31.9482 3.77953 31.4626 3.97863 31.1046 4.33303C30.7466 4.68743 30.5455 5.1681 30.5455 5.66929C30.5455 6.17049 30.7466 6.65116 31.1046 7.00556C31.4626 7.35996 31.9482 7.55906 32.4545 7.55906H34.3636V9.44882C34.3636 9.95002 34.5648 10.4307 34.9228 10.7851C35.2808 11.1395 35.7664 11.3386 36.2727 11.3386C36.779 11.3386 37.2646 11.1395 37.6227 10.7851C37.9807 10.4307 38.1818 9.95002 38.1818 9.44882V7.55906H40.0909C40.5972 7.55906 41.0828 7.35996 41.4408 7.00556C41.7989 6.65116 42 6.17049 42 5.66929C42 5.1681 41.7989 4.68743 41.4408 4.33303C41.0828 3.97863 40.5972 3.77953 40.0909 3.77953Z" fill="#9B9B9B"/>
                                                            </svg>
                                                        </div>
                                                        <div className='right'>
                                                            Нажмите на кнопку или перетащите фото в эту область до 10 изображений в формате PNG, JPEG.
                                                        </div>
                                                    </div>
                                                </Dragger>
                                            </div>
                                        </div>
                                    }
                                ]} 
                            />
                        </div>
                    }
                </div>
            </Modal>
            <Modal mask={false} className="modal_custom" key="m24" width={900} title="Изменение пункта" open={app_global.panel.indexOf( 'specialistEducation' ) != -1 } onOk={() => hide_tab_specialist_education()} onCancel={() => hide_tab_specialist_education()} footer={null}>
                <div className='header'>
                    <h2>Изменение пункта образования</h2>
                    <h3>
                        {Object(specialist.specialist_id).length > 0 ?
                            <Skeleton.Button style={{marginBottom: '5px', maxWidth: '300px'}} size="small" shape="round" active block />
                        :
                            specialist.specialist_id?.name
                        }
                    </h3>
                </div>
                <div className='custom_divider' />
                <div className='body'>
                    <Form name="form" className="default_form" form={form_specialist_education} onFinish={form_finish_specialist_education} >
                        <Form.Item hidden name={`id`} rules={[{ required: true, message: `Пожалуйста, введите ID пункта!` }]}>
                            <Input disabled={specialist.loading} className='custom_input' />
                        </Form.Item>
                        <label style={{marginTop:20}}>Дата</label>
                        <Form.Item name={`date`} rules={[{ required: true, message: `Пожалуйста, введите дату!` }]}>
                            <Input disabled={specialist.loading} className='custom_input' placeholder="27.06.2000" />
                        </Form.Item>
                        <label style={{marginTop:20}}>Описание</label>
                        <Form.Item name={`text`}>
                            <Input disabled={specialist.loading} className='custom_input' placeholder="Диплом НГТУ" />
                        </Form.Item>
                        <div className='form_buttons'>
                            <Button className='submit' htmlType="submit" loading={specialist.loading}>Сохранить</Button>
                        </div>
                    </Form>
                </div>
            </Modal>
            <Modal mask={false} className="modal_custom" key="m25" width={900} title="Изменение пункта" open={app_global.panel.indexOf( 'specialistExperience' ) != -1 } onOk={() => hide_tab_specialist_experience()} onCancel={() => hide_tab_specialist_experience()} footer={null}>
                <div className='header'>
                    <h2>Изменение пункта опыта и достижения</h2>
                    <h3>
                        {Object(specialist.specialist_id).length > 0 ?
                            <Skeleton.Button style={{marginBottom: '5px', maxWidth: '300px'}} size="small" shape="round" active block />
                        :
                            specialist.specialist_id?.name
                        }
                    </h3>
                </div>
                <div className='custom_divider' />
                <div className='body'>
                    <Form name="form" className="default_form" form={form_specialist_experience} onFinish={form_finish_specialist_experience} >
                        <Form.Item hidden name={`id`} rules={[{ required: true, message: `Пожалуйста, введите ID пункта!` }]}>
                            <Input disabled={specialist.loading} className='custom_input' />
                        </Form.Item>
                        <label style={{marginTop:20}}>Описание</label>
                        <Form.Item name={`text`}>
                            <Input disabled={specialist.loading} className='custom_input' placeholder="Достижение..." />
                        </Form.Item>
                        <div className='form_buttons'>
                            <Button className='submit' htmlType="submit" loading={specialist.loading}>Сохранить</Button>
                        </div>
                    </Form>
                </div>
            </Modal>
            <Modal mask={false} className="modal_custom" key="m26" width={1600} title="Добавление категории записи" open={app_global.panel.indexOf( 'postsCategoryAdd' ) != -1 } onOk={() => hide_tab_posts_category_add()} onCancel={() => hide_tab_posts_category_add()} footer={null}>
                <div className='header'>
                    <h2>Добавление категории записи</h2>
                </div>
                <div className='custom_divider' />
                <div className='body'>
                    <div>
                        <Form name="form" className="default_form" form={form_posts_category_add} onFinish={form_finish_posts_category_add}>
                            <label>Название</label>
                            <Form.Item name={`title`} rules={[{ required: true, message: `Пожалуйста, введите название категории!` }]}>
                                <Input disabled={posts.loading} className='custom_input' placeholder={`Название категории`} />
                            </Form.Item>
                            <label>Родительская категория</label>
                            <Form.Item name={`category`}>
                                <Select
                                    disabled={organization.loading}
                                    className="custom_select"
                                    showSearch
                                    placeholder="Выбрать категорию"
                                    optionFilterProp="children"
                                    filterOption={(input, option) =>
                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                    options={obj.posts_category_category_list}
                                />
                            </Form.Item>
                            <label>Часть ссылки</label>
                            <Form.Item name={`slug`} rules={[{ required: true, message: `Пожалуйста, введите часть ссылки!` }]}>
                                <Input disabled={posts.loading} className='custom_input' placeholder={`Часть ссылки`} />
                            </Form.Item>
                            <div className='form_buttons'>
                                <Button className='submit' htmlType="submit" loading={organization.loading}>Сохранить</Button>
                            </div>
                        </Form>
                    </div>
                </div>
            </Modal>
            <Modal mask={false} className="modal_custom" key="m27" width={1600} title="Изменение категории записей" open={app_global.panel.indexOf( 'posts_category' ) != -1 } onOk={() => hide_tab_posts_category_item()} onCancel={() => hide_tab_posts_category_item()} footer={null}>
                <div className='header'>
                    <h2>Изменение информации</h2>
                    <h3>
                        {Object(posts.category_id).length === 0 ?
                            <Skeleton.Button style={{marginBottom: '5px', maxWidth: '300px'}} size="small" shape="round" active block />
                        :
                            <>{getOrgStatusTag(posts.category_id?.status)} {posts.category_id?.title}</>
                        }
                    </h3>
                </div>
                <div className='custom_divider' />
                <div className='body'>
                    {Object(posts.category_id).length === 0 ?
                        <Skeleton active />
                    :
                        <div className="custom-tabs">
                            <Tabs
                                tabPosition={Object.entries(screens)
                                    .filter((screen) => !!screen[1])
                                    .map((screen) => (
                                        screen[0]
                                )).indexOf( 'xl' ) != -1 ? 'left' : 'top'}
                                activeKey={activeTabPostsCategoryEdit}
                                defaultActiveKey="data"
                                onChange={tabPostsCategoryEditChange}
                                className="profile_tabs"
                                items={[
                                    {
                                        key: 'data',
                                        label: `Основная информация`,
                                        children: 
                                        <div>
                                            <Form name="form" className="default_form" form={form_posts_category_data} onFinish={form_finish_posts_category_data}>
                                                {["3","4"].indexOf( profile?.data?.type ) !== -1 ?
                                                    <label>Статус</label>
                                                :
                                                    null
                                                }
                                                <Form.Item name={`status`} hidden={["3","4"].indexOf( profile?.data?.type ) === -1} >
                                                    <Select
                                                        disabled={app_global.loading}
                                                        className="custom_select"
                                                        showSearch
                                                        placeholder="Выбрать статус"
                                                        optionFilterProp="children"
                                                        filterOption={(input, option) =>
                                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                        }
                                                        options={[
                                                            {value:"publish",label:"Опубликовано"},
                                                            {value:"moderation",label:"На проверке"},
                                                            {value:"draft",label:"Черновик"},
                                                            {value:"cancel",label:"Отклонено"}
                                                        ]}
                                                    />
                                                </Form.Item>
                                                <label>Название</label>
                                                <Form.Item name={`title`} rules={[{ required: true, message: `Пожалуйста, введите название категории!` }]}>
                                                    <Input disabled={posts.loading} className='custom_input' placeholder={`Название категории`} />
                                                </Form.Item>
                                                <label>Родительская категория</label>
                                                <Form.Item name={`category`} >
                                                    <Select
                                                        disabled={posts.loading}
                                                        className="custom_select"
                                                        showSearch
                                                        placeholder="Выбрать категорию"
                                                        optionFilterProp="children"
                                                        filterOption={(input, option) =>
                                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                        }
                                                        options={obj.posts_category_category_list}
                                                    />
                                                </Form.Item>
                                                <label>Описание</label>
                                                <Form.Item name={`excerpt`} >
                                                    <TextArea className='custom_input' placeholder="Описание категории" rows={4} showCount  maxLength={1000} />
                                                </Form.Item>
                                                <label>Дата публикации</label>
                                                <Form.Item name={`date_create`} >
                                                    <TimeInput
                                                        onChange={({ target: { value } }) => form_posts_category_data.setFieldsValue({
                                                            date_create:value  
                                                        })}>
                                                    </TimeInput>
                                                </Form.Item>
                                                <div className='form_buttons'>
                                                    <Button className='submit' htmlType="submit" loading={posts.loading}>Сохранить</Button>
                                                </div>
                                            </Form>
                                        </div>
                                    },
                                    {
                                        key: 'seo',
                                        label: `SEO`,
                                        children: 
                                        <div>
                                            <Form name="form" className="default_form" form={form_posts_category_seo} onFinish={form_finish_posts_category_seo}>
                                                <label>Заголовок</label>
                                                <Form.Item name={`seo_title`} rules={[{ required: true, message: `Пожалуйста, введите заголовок для поисковиков!` }]}>
                                                    <Input disabled={posts.loading} className='custom_input' placeholder={`Заголовок`} />
                                                </Form.Item>
                                                <label>Описание</label>
                                                <Form.Item name={`seo_description`} >
                                                    <Input disabled={posts.loading} className='custom_input' placeholder={`Описание`} />
                                                </Form.Item>
                                                <label>Ключевые слова</label>
                                                <Form.Item name={`seo_keywords`} >
                                                    <Input disabled={posts.loading} className='custom_input' placeholder={`Ключевые слова`} />
                                                </Form.Item>
                                                <div className='form_buttons'>
                                                    <Button className='submit' htmlType="submit" loading={posts.loading}>Сохранить</Button>
                                                </div>
                                            </Form>
                                        </div>
                                    },
                                    {
                                        key: 'content',
                                        label: `Контент`,
                                        children: 
                                        <div>
                                            <Form name="form" className="default_form" form={form_posts_category_content} onFinish={form_finish_posts_category_content} >
                                                <div className='form_buttons top'>
                                                    <Button className='submit' htmlType="submit" loading={posts.loading}>Сохранить</Button>
                                                </div>
                                                <ReactEditorJS 
                                                    token={profile.token}
                                                    API={API}
                                                    ref={editorCorePostsCategory}
                                                    onInitialize={handleInitializePostsCategory}
                                                    defaultValue={posts.category_id?.content !== undefined ? JSON.parse(posts.category_id?.content) : {}}
                                                />
                                                <Form.Item name={`content`} hidden >
                                                    <TextArea rows={4} disabled />
                                                </Form.Item>
                                                <div className='form_buttons'>
                                                    <Button className='submit' htmlType="submit" loading={posts.loading}>Сохранить</Button>
                                                </div>
                                            </Form>
                                        </div>
                                    },
                                    {
                                        key: 'images',
                                        label: `Изображения`,
                                        children: 
                                        <div>
                                            <Form className="default_form">
                                                <div className="fcenter images_column">
                                                    {posts.category_id?.photo_mini === null ?
                                                        null
                                                    :
                                                        <div className='image_preview'>
                                                            <Image
                                                                className="image-preview"
                                                                height={100}
                                                                width={100}
                                                                src={`${UPLOADS}/${posts.category_id?.photo_mini_preview}`}
                                                                preview={{
                                                                    src: `${UPLOADS}/${posts.category_id?.photo_mini}`
                                                                }}
                                                            /> 
                                                            <div className="float_remove">
                                                                <Popconfirm placement="topRight" onConfirm={() => posts_category_image_delete()} 
                                                                    title={
                                                                        <Space direction="vertical">
                                                                            <Text>Вы действительно хотите удалить данное изображение?</Text>
                                                                        </Space>
                                                                    } 
                                                                okText="Да" cancelText="Нет">
                                                                    <FiTrash2 style={{color:'#f5222d',cursor:'pointer'}} />
                                                                </Popconfirm>
                                                            </div>
                                                        </div>
                                                    }
                                                    <ImgCrop quality={1} aspect={1.495} modalTitle="Загрузка миниатюры" modalOk="Сохранить" modalCancel="Отмена" >
                                                        <Dragger
                                                            className="no-list"
                                                            {...upload_config_one_image}
                                                            {...upload_posts_category_image}
                                                            data={{'method':'upload_posts_category_photo','token':profile?.token,'category_id':posts.category_id?.id}}
                                                        >
                                                            <div className='upload_content'>
                                                                <div className='left'>
                                                                    <svg width="42" height="36" viewBox="0 0 42 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                        <path d="M36.2727 15.1181C35.7664 15.1181 35.2808 15.3172 34.9228 15.6716C34.5648 16.026 34.3636 16.5067 34.3636 17.0079V30.2362C34.3636 30.7374 34.1625 31.2181 33.8045 31.5725C33.4465 31.9269 32.9609 32.126 32.4545 32.126H5.72727C5.22095 32.126 4.73537 31.9269 4.37734 31.5725C4.01932 31.2181 3.81818 30.7374 3.81818 30.2362V15.1181C3.81818 14.6169 4.01932 14.1362 4.37734 13.7818C4.73537 13.4274 5.22095 13.2283 5.72727 13.2283H9.54545C9.96176 13.2499 10.3737 13.1359 10.7185 12.9038C11.0632 12.6718 11.3217 12.3345 11.4545 11.9433L12.4855 8.84409C12.6135 8.46875 12.8575 8.14274 13.183 7.91214C13.5084 7.68155 13.8989 7.55803 14.2991 7.55906H24.8182C25.3245 7.55906 25.8101 7.35996 26.1681 7.00556C26.5261 6.65116 26.7273 6.17049 26.7273 5.66929C26.7273 5.1681 26.5261 4.68743 26.1681 4.33303C25.8101 3.97863 25.3245 3.77953 24.8182 3.77953H14.2036C13.0039 3.78175 11.8351 4.15688 10.8623 4.85198C9.88947 5.54708 9.16174 6.52703 8.78182 7.65354L8.17091 9.54331H5.72727C4.20831 9.54331 2.75155 10.1406 1.67748 11.2038C0.603407 12.267 0 13.709 0 15.2126V30.3307C0 31.8343 0.603407 33.2763 1.67748 34.3395C2.75155 35.4027 4.20831 36 5.72727 36H32.4545C33.9735 36 35.4303 35.4027 36.5043 34.3395C37.5784 33.2763 38.1818 31.8343 38.1818 30.3307V17.1024C38.1948 16.8464 38.155 16.5904 38.0648 16.3501C37.9747 16.1098 37.8361 15.8902 37.6576 15.7046C37.479 15.519 37.2641 15.3713 37.026 15.2704C36.788 15.1696 36.5317 15.1178 36.2727 15.1181ZM19.0909 13.2283C17.5806 13.2283 16.1042 13.6717 14.8484 14.5023C13.5926 15.3329 12.6138 16.5134 12.0358 17.8947C11.4579 19.2759 11.3066 20.7958 11.6013 22.2621C11.8959 23.7284 12.6232 25.0753 13.6912 26.1325C14.7591 27.1896 16.1198 27.9095 17.6011 28.2012C19.0824 28.4929 20.6179 28.3432 22.0132 27.7711C23.4086 27.1989 24.6012 26.2301 25.4403 24.987C26.2794 23.7439 26.7273 22.2824 26.7273 20.7874C26.7273 18.7826 25.9227 16.8599 24.4906 15.4423C23.0585 14.0247 21.1162 13.2283 19.0909 13.2283ZM19.0909 24.5669C18.3357 24.5669 17.5975 24.3453 16.9696 23.93C16.3417 23.5147 15.8524 22.9244 15.5634 22.2338C15.2744 21.5431 15.1988 20.7832 15.3461 20.0501C15.4934 19.3169 15.8571 18.6434 16.391 18.1149C16.925 17.5863 17.6054 17.2263 18.346 17.0805C19.0867 16.9347 19.8544 17.0095 20.5521 17.2956C21.2497 17.5816 21.8461 18.0661 22.2656 18.6876C22.6852 19.3091 22.9091 20.0399 22.9091 20.7874C22.9091 21.7898 22.5068 22.7511 21.7908 23.4599C21.0747 24.1687 20.1036 24.5669 19.0909 24.5669ZM40.0909 3.77953H38.1818V1.88976C38.1818 1.38857 37.9807 0.907898 37.6227 0.553499C37.2646 0.199099 36.779 0 36.2727 0C35.7664 0 35.2808 0.199099 34.9228 0.553499C34.5648 0.907898 34.3636 1.38857 34.3636 1.88976V3.77953H32.4545C31.9482 3.77953 31.4626 3.97863 31.1046 4.33303C30.7466 4.68743 30.5455 5.1681 30.5455 5.66929C30.5455 6.17049 30.7466 6.65116 31.1046 7.00556C31.4626 7.35996 31.9482 7.55906 32.4545 7.55906H34.3636V9.44882C34.3636 9.95002 34.5648 10.4307 34.9228 10.7851C35.2808 11.1395 35.7664 11.3386 36.2727 11.3386C36.779 11.3386 37.2646 11.1395 37.6227 10.7851C37.9807 10.4307 38.1818 9.95002 38.1818 9.44882V7.55906H40.0909C40.5972 7.55906 41.0828 7.35996 41.4408 7.00556C41.7989 6.65116 42 6.17049 42 5.66929C42 5.1681 41.7989 4.68743 41.4408 4.33303C41.0828 3.97863 40.5972 3.77953 40.0909 3.77953Z" fill="#9B9B9B"/>
                                                                    </svg>
                                                                </div>
                                                                <div className='right'>
                                                                    [Миниатюра] Нажмите на кнопку или перетащите фото в эту область в формате PNG, JPEG.
                                                                </div>
                                                            </div>
                                                        </Dragger>
                                                    </ImgCrop>
                                                </div>
                                                <div className="fcenter images_column">
                                                    {posts.category_id?.photo_big === null ?
                                                        null
                                                    :
                                                        <div className='image_preview'>
                                                            <Image
                                                                className="image-preview"
                                                                height={100}
                                                                width={100}
                                                                src={`${UPLOADS}/${posts.category_id?.photo_big_preview}`}
                                                                preview={{
                                                                    src: `${UPLOADS}/${posts.category_id?.photo_big}`
                                                                }}
                                                            />
                                                            <div className="float_remove">
                                                                <Popconfirm placement="topRight" onConfirm={() => posts_category_image_big_delete()} 
                                                                    title={
                                                                        <Space direction="vertical">
                                                                            <Text>Вы действительно хотите удалить данное изображение?</Text>
                                                                        </Space>
                                                                    } 
                                                                okText="Да" cancelText="Нет">
                                                                    <FiTrash2 style={{color:'#f5222d',cursor:'pointer'}} />
                                                                </Popconfirm>
                                                            </div>
                                                        </div>
                                                    }
                                                    <ImgCrop quality={1} aspect={2.98} modalTitle="Загрузка большого изображения" modalOk="Сохранить" modalCancel="Отмена" >
                                                        <Dragger
                                                            className="no-list"
                                                            {...upload_config_one_image}
                                                            {...upload_posts_category_image}
                                                            data={{'method':'upload_posts_category_photo_big','token':profile?.token,'category_id':posts.category_id?.id}}
                                                        >
                                                            <div className='upload_content'>
                                                                <div className='left'>
                                                                    <svg width="42" height="36" viewBox="0 0 42 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                        <path d="M36.2727 15.1181C35.7664 15.1181 35.2808 15.3172 34.9228 15.6716C34.5648 16.026 34.3636 16.5067 34.3636 17.0079V30.2362C34.3636 30.7374 34.1625 31.2181 33.8045 31.5725C33.4465 31.9269 32.9609 32.126 32.4545 32.126H5.72727C5.22095 32.126 4.73537 31.9269 4.37734 31.5725C4.01932 31.2181 3.81818 30.7374 3.81818 30.2362V15.1181C3.81818 14.6169 4.01932 14.1362 4.37734 13.7818C4.73537 13.4274 5.22095 13.2283 5.72727 13.2283H9.54545C9.96176 13.2499 10.3737 13.1359 10.7185 12.9038C11.0632 12.6718 11.3217 12.3345 11.4545 11.9433L12.4855 8.84409C12.6135 8.46875 12.8575 8.14274 13.183 7.91214C13.5084 7.68155 13.8989 7.55803 14.2991 7.55906H24.8182C25.3245 7.55906 25.8101 7.35996 26.1681 7.00556C26.5261 6.65116 26.7273 6.17049 26.7273 5.66929C26.7273 5.1681 26.5261 4.68743 26.1681 4.33303C25.8101 3.97863 25.3245 3.77953 24.8182 3.77953H14.2036C13.0039 3.78175 11.8351 4.15688 10.8623 4.85198C9.88947 5.54708 9.16174 6.52703 8.78182 7.65354L8.17091 9.54331H5.72727C4.20831 9.54331 2.75155 10.1406 1.67748 11.2038C0.603407 12.267 0 13.709 0 15.2126V30.3307C0 31.8343 0.603407 33.2763 1.67748 34.3395C2.75155 35.4027 4.20831 36 5.72727 36H32.4545C33.9735 36 35.4303 35.4027 36.5043 34.3395C37.5784 33.2763 38.1818 31.8343 38.1818 30.3307V17.1024C38.1948 16.8464 38.155 16.5904 38.0648 16.3501C37.9747 16.1098 37.8361 15.8902 37.6576 15.7046C37.479 15.519 37.2641 15.3713 37.026 15.2704C36.788 15.1696 36.5317 15.1178 36.2727 15.1181ZM19.0909 13.2283C17.5806 13.2283 16.1042 13.6717 14.8484 14.5023C13.5926 15.3329 12.6138 16.5134 12.0358 17.8947C11.4579 19.2759 11.3066 20.7958 11.6013 22.2621C11.8959 23.7284 12.6232 25.0753 13.6912 26.1325C14.7591 27.1896 16.1198 27.9095 17.6011 28.2012C19.0824 28.4929 20.6179 28.3432 22.0132 27.7711C23.4086 27.1989 24.6012 26.2301 25.4403 24.987C26.2794 23.7439 26.7273 22.2824 26.7273 20.7874C26.7273 18.7826 25.9227 16.8599 24.4906 15.4423C23.0585 14.0247 21.1162 13.2283 19.0909 13.2283ZM19.0909 24.5669C18.3357 24.5669 17.5975 24.3453 16.9696 23.93C16.3417 23.5147 15.8524 22.9244 15.5634 22.2338C15.2744 21.5431 15.1988 20.7832 15.3461 20.0501C15.4934 19.3169 15.8571 18.6434 16.391 18.1149C16.925 17.5863 17.6054 17.2263 18.346 17.0805C19.0867 16.9347 19.8544 17.0095 20.5521 17.2956C21.2497 17.5816 21.8461 18.0661 22.2656 18.6876C22.6852 19.3091 22.9091 20.0399 22.9091 20.7874C22.9091 21.7898 22.5068 22.7511 21.7908 23.4599C21.0747 24.1687 20.1036 24.5669 19.0909 24.5669ZM40.0909 3.77953H38.1818V1.88976C38.1818 1.38857 37.9807 0.907898 37.6227 0.553499C37.2646 0.199099 36.779 0 36.2727 0C35.7664 0 35.2808 0.199099 34.9228 0.553499C34.5648 0.907898 34.3636 1.38857 34.3636 1.88976V3.77953H32.4545C31.9482 3.77953 31.4626 3.97863 31.1046 4.33303C30.7466 4.68743 30.5455 5.1681 30.5455 5.66929C30.5455 6.17049 30.7466 6.65116 31.1046 7.00556C31.4626 7.35996 31.9482 7.55906 32.4545 7.55906H34.3636V9.44882C34.3636 9.95002 34.5648 10.4307 34.9228 10.7851C35.2808 11.1395 35.7664 11.3386 36.2727 11.3386C36.779 11.3386 37.2646 11.1395 37.6227 10.7851C37.9807 10.4307 38.1818 9.95002 38.1818 9.44882V7.55906H40.0909C40.5972 7.55906 41.0828 7.35996 41.4408 7.00556C41.7989 6.65116 42 6.17049 42 5.66929C42 5.1681 41.7989 4.68743 41.4408 4.33303C41.0828 3.97863 40.5972 3.77953 40.0909 3.77953Z" fill="#9B9B9B"/>
                                                                    </svg>
                                                                </div>
                                                                <div className='right'>
                                                                    [Большое изображение] Нажмите на кнопку или перетащите фото в эту область в формате PNG, JPEG.
                                                                </div>
                                                            </div>
                                                        </Dragger>
                                                    </ImgCrop>
                                                </div>
                                            </Form>
                                        </div>
                                    }
                                ]} 
                            />
                        </div>
                    }
                </div>
            </Modal>
            <Modal mask={false} className="modal_custom" key="m28" width={1600} title="Добавление отзыва" open={app_global.panel.indexOf( 'reviewAdd' ) != -1 } onOk={() => hide_tab_reviews_add()} onCancel={() => hide_tab_reviews_add()} footer={null}>
                <div className='header'>
                    <h2>Добавление отзыва</h2>
                </div>
                <div className='custom_divider' />
                <div className='body'>
                    <div>
                        <Form name="form" className="default_form" form={form_review_add} onFinish={form_finish_review_add}>
                            <label style={{marginTop:20}}>Имя</label>
                            <Form.Item name={`firstname`} rules={[{ required: true, message: `Пожалуйста, введите Ваше имя!` }]}>
                                <Input disabled={profile.loading} className='custom_input' placeholder={randomUser['firstname']} />
                            </Form.Item>
                            <label>Пол</label>
                            <Form.Item name={`gender`} rules={[{ required: true, message: `Пожалуйста, укажите пол!` }]} >
                                <Select
                                    disabled={profile.loading}
                                    className="custom_select"
                                    showSearch
                                    placeholder="Выбрать пол"
                                    optionFilterProp="children"
                                    filterOption={(input, option) =>
                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                    options={[
                                        {value:"1",label:"Мужской"},
                                        {value:"2",label:"Женский"}
                                    ]}
                                />
                            </Form.Item>
                            <label>Организация</label>
                            <Form.Item name={`organization_id`} rules={[{ required: true, message: `Пожалуйста, выберите организацию!` }]}>
                                <Select
                                    disabled={specialist.loading}
                                    className="custom_select"
                                    showSearch
                                    placeholder="Выбрать организацию"
                                    optionFilterProp="children"
                                    filterOption={(input, option) =>
                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                    options={specialist.specialist_organization}
                                />
                            </Form.Item>
                            <label>Оцените учреждение</label>
                            <Form.Item name={`rate`} rules={[{ required: true, message: `Пожалуйста, укажите оценку!` }]}>
                                <Rate className="custom_star" allowHalf style={{color:'#487CAB'}} />
                            </Form.Item>
                            <label>Поделитесь впечатлениями об учреждении</label>
                            <Form.Item name={`text`} >
                                <TextArea className="custom_input" placeholder="Текст вашего отзыва" showCount  maxLength={1000} />
                            </Form.Item>
                            <label>Дата публикации</label>
                            <Form.Item name={`date_create`} >
                                <TimeInput
                                    onChange={({ target: { value } }) => form_review_add.setFieldsValue({
                                        date_create:value  
                                    })}>
                                </TimeInput>
                            </Form.Item>
                            <label>Время публикации</label>
                            <Form.Item name={`time_create`} >
                                <TimeTimeInput
                                    onChange={({ target: { value } }) => form_review_add.setFieldsValue({
                                        time_create:value  
                                    })}>
                                </TimeTimeInput>
                            </Form.Item>
                            <div className='form_buttons'>
                                <Button className='submit' htmlType="submit" loading={organization.loading}>Сохранить</Button>
                            </div>
                        </Form>
                    </div>
                </div>
            </Modal>
            <Modal mask={false} className="modal_custom" key="m29" width={900} title="Изменение номера телефона" open={app_global.panel.indexOf( 'specialistPhone' ) != -1 } onOk={() => hide_tab_specialist_phone()} onCancel={() => hide_tab_specialist_phone()} footer={null}>
                <div className='header'>
                    <h2>Изменение номера телефона</h2>
                    <h3>
                        {Object(specialist.specialist_id).length > 0 ?
                            <Skeleton.Button style={{marginBottom: '5px', maxWidth: '300px'}} size="small" shape="round" active block />
                        :
                            specialist.specialist_id?.name
                        }
                    </h3>
                </div>
                <div className='custom_divider' />
                <div className='body'>
                    <Form name="form" className="default_form" form={form_specialist_phone} onFinish={form_finish_specialist_phone} >
                        <Form.Item hidden name={`id`} rules={[{ required: true, message: `Пожалуйста, введите ID номера!` }]}>
                            <Input disabled={specialist.loading} className='custom_input' />
                        </Form.Item>
                        <label style={{marginTop:20}}>Телефон</label>
                        <Form.Item name={`phone`} rules={[{ required: true, message: `Пожалуйста, введите номер телефона!` }]}>
                            <MaskedInput
                                disabled={specialist.loading}
                                mask={SETTINGS_APP.mask[SETTINGS_APP.project_format]['default']}
                                placeholder={SETTINGS_APP.mask[SETTINGS_APP.project_format]['placeholder']}
                                render={(ref, props) => (
                                    <input className="ant-input custom_input" type="text" aria-required="true" ref={ref} {...props} />
                                )}
                            />
                        </Form.Item>
                        <label style={{marginTop:20}}>Добавочный номер (если есть)</label>
                        <Form.Item name={`more`} >
                            <Input disabled={specialist.loading} className='custom_input' />
                        </Form.Item>
                        <div className='form_buttons'>
                            <Button className='submit' htmlType="submit" loading={specialist.loading}>Сохранить</Button>
                        </div>
                    </Form>
                </div>
            </Modal>
            <Modal mask={false} className="modal_custom" key="m30" width={900} title="Изменение информации" open={app_global.panel.indexOf( 'users_item' ) != -1 } onOk={() => hide_tab_users_item()} onCancel={() => hide_tab_users_item()} footer={null}>
                <div className='header'>
                    <h2>Изменение пользователя</h2>
                </div>
                <div className='custom_divider' />
                <div className='body'>
                    <Form name="form" className="default_form" form={form_users_data} onFinish={form_finish_users_data}>
                        <Form.Item hidden name={`id`} rules={[{ required: true, message: `Пожалуйста, введите ID пользователя!` }]}>
                            <Input disabled={profile.loading} className='custom_input' />
                        </Form.Item>
                        <label style={{marginTop:20}}>Имя</label>
                        <Form.Item name={`firstname`} rules={[{ required: true, message: `Пожалуйста, введите имя!` }]}>
                            <Input disabled={profile.loading} className='custom_input' placeholder={randomUser['firstname']} />
                        </Form.Item>
                        <label>Фамилия</label>
                        <Form.Item name={`lastname`} >
                            <Input disabled={profile.loading} className='custom_input' placeholder={randomUser['lastname']} />
                        </Form.Item>
                        <label>Отчество</label>
                        <Form.Item name={`patronymic`} >
                            <Input disabled={profile.loading} className='custom_input' placeholder={randomUser['patronymic']} />
                        </Form.Item>
                        <label>Пол</label>
                        <Form.Item name={`gender`} >
                            <Select
                                disabled={profile.loading}
                                className="custom_select"
                                showSearch
                                placeholder="Выбрать пол"
                                optionFilterProp="children"
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                options={[
                                    {value:"1",label:"Мужской"},
                                    {value:"2",label:"Женский"},
                                    {value:"3",label:"Не указано"}
                                ]}
                            />
                        </Form.Item>
                        <label>Дата рождения</label>
                        <Form.Item name={`birthdate`} >
                            <TimeInput
                                onChange={({ target: { value } }) => form_profile.setFieldsValue({
                                    birthdate:value  
                                })}>
                            </TimeInput>
                        </Form.Item>
                        <label>Город</label>
                        <Form.Item name={`location`} rules={[{ required: true, message: `Пожалуйста, укажите город` }]}>
                            <Select
                                disabled={profile.loading}
                                className="custom_select"
                                showSearch
                                placeholder="Выбрать город"
                                optionFilterProp="children"
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                options={locations.locations.map((item, indexedDB) => ({value:item.id,label:item.city}))}
                            />
                        </Form.Item>
                        <label>Роль</label>
                        <Form.Item name={`type`} rules={[{ required: true, message: `Пожалуйста, укажите роль` }]} >
                            <Select
                                disabled={profile.loading}
                                className="custom_select"
                                showSearch
                                placeholder="Выбрать роль"
                                optionFilterProp="children"
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                options={[
                                    {value:"1",label:"Обычный пользователь"},
                                    {value:"2",label:"Владелец организации"},
                                    {value:"5",label:"Доктор"},
                                    {value:"3",label:"Администратор"}
                                ]}
                            />
                        </Form.Item>
                        <div className='form_buttons'>
                            <Button className='submit' htmlType="submit" loading={profile.loading}>Сохранить</Button>
                        </div>
                    </Form>
                </div>
            </Modal>
            <Modal mask={false} className="modal_custom" key="m31" width={900} title="Добавление специалиста" open={app_global.panel.indexOf( 'advertisementAdd' ) != -1 } onOk={() => hide_tab_advertisement_my_add()} onCancel={() => hide_tab_advertisement_my_add()} footer={null}>
                <div className='header'>
                    <h2>Добавление вакансии</h2>
                </div>
                <div className='custom_divider' />
                <div className='body'>
                    <div>
                        <Form name="form" className="default_form" form={form_advertisement_add} onFinish={form_finish_advertisement_add}>
                            <label>Название</label>
                            <Form.Item name={`title`} rules={[{ required: true, message: `Пожалуйста, введите название!` }]}>
                                <Input disabled={advertisement.loading} className='custom_input' placeholder={`Стоматолог ...`} />
                            </Form.Item>
                            {["5"].indexOf( profile?.data?.type ) !== -1 ?
                                <>
                                    <label>Специалист</label>
                                    <Form.Item name={`connect_id`} rules={[{ required: true, message: `Пожалуйста, выберите специалиста!` }]}>
                                        <Select
                                            disabled={advertisement.loading}
                                            className="custom_select"
                                            showSearch
                                            placeholder="Выбрать специалиста"
                                            optionFilterProp="children"
                                            filterOption={(input, option) =>
                                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                            }
                                            options={
                                                ["3","4"].indexOf( profile?.data?.type ) !== -1 ?
                                                    obj.specialist_list.filter((item) => item.status === 'publish').map((item) => ({label:item.name, value:item.id}))
                                                :
                                                    obj.specialist_my.filter((item) => item.status === 'publish').map((item) => ({label:item.name, value:item.id}))
                                            }
                                        />
                                    </Form.Item>
                                </>
                            :
                                null
                            }
                            {["2"].indexOf( profile?.data?.type ) !== -1 ?
                                <>
                                    <label>Организация</label>
                                    <Form.Item name={`connect_id`} rules={[{ required: true, message: `Пожалуйста, выберите организацию!` }]}>
                                        <Select
                                            disabled={advertisement.loading}
                                            className="custom_select"
                                            showSearch
                                            placeholder="Выбрать организацию"
                                            optionFilterProp="children"
                                            filterOption={(input, option) =>
                                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                            }
                                            options={
                                                ["3","4"].indexOf( profile?.data?.type ) !== -1 ?
                                                    obj.organization_list.filter((item) => item.status === 'publish').map((item) => ({label:item.orgname, value:item.id}))
                                                :
                                                    obj.organization_my.filter((item) => item.status === 'publish').map((item) => ({label:item.orgname, value:item.id}))
                                            }
                                        />
                                    </Form.Item>
                                </>
                            :
                                null
                            }
                            {["3","4"].indexOf( profile?.data?.type ) !== -1 ?
                                <>
                                    <label>Тип</label>
                                    <Form.Item name={`type`} rules={[{ required: true, message: `Пожалуйста, укажите тип объявления` }]}>
                                        <Select
                                            disabled={advertisement.loading}
                                            className="custom_select"
                                            placeholder="Ищу специалиста"
                                            options={[
                                                {value:'search_doctor',label:'Ищу специалиста'},
                                                {value:'search_organization',label:'Ищу место работы'}
                                            ]}
                                        />
                                    </Form.Item>
                                    <Form.Item
                                        noStyle
                                        shouldUpdate={(prevValues, currentValues) => prevValues[`type`] !== currentValues[`type`]}
                                    >
                                        {({ getFieldValue }) =>
                                            <>
                                                {
                                                    (getFieldValue(`type`) === 'search_organization') ? (
                                                        <>
                                                            <label>Специалист</label>
                                                            <Form.Item name={`connect_id`} rules={[{ required: true, message: `Пожалуйста, выберите специалиста!` }]}>
                                                                <Select
                                                                    disabled={advertisement.loading}
                                                                    className="custom_select"
                                                                    showSearch
                                                                    placeholder="Выбрать специалиста"
                                                                    optionFilterProp="children"
                                                                    filterOption={(input, option) =>
                                                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                                    }
                                                                    options={
                                                                        ["3","4"].indexOf( profile?.data?.type ) !== -1 ?
                                                                            obj.specialist_list.filter((item) => item.status === 'publish').map((item) => ({label:item.name, value:item.id}))
                                                                        :
                                                                            obj.specialist_my.filter((item) => item.status === 'publish').map((item) => ({label:item.name, value:item.id}))
                                                                    }
                                                                />
                                                            </Form.Item>
                                                        </>
                                                    ) : null
                                                }
                                                {
                                                    (getFieldValue(`type`) === 'search_doctor') ? (
                                                        <>
                                                            <label>Организация</label>
                                                            <Form.Item name={`connect_id`} rules={[{ required: true, message: `Пожалуйста, выберите организацию!` }]}>
                                                                <Select
                                                                    disabled={advertisement.loading}
                                                                    className="custom_select"
                                                                    showSearch
                                                                    placeholder="Выбрать организацию"
                                                                    optionFilterProp="children"
                                                                    filterOption={(input, option) =>
                                                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                                    }
                                                                    options={
                                                                        ["3","4"].indexOf( profile?.data?.type ) !== -1 ?
                                                                            obj.organization_list.filter((item) => item.status === 'publish').map((item) => ({label:item.orgname, value:item.id}))
                                                                        :
                                                                            obj.organization_my.filter((item) => item.status === 'publish').map((item) => ({label:item.orgname, value:item.id}))
                                                                    }
                                                                />
                                                            </Form.Item>
                                                        </>
                                                    ) : null
                                                }
                                            </>
                                        }
                                    </Form.Item>
                                    <label>Город поиска</label>
                                    <Form.Item name={`location`} rules={[{ required: true, message: `Пожалуйста, укажите город` }]}>
                                        <Select
                                            disabled={organization.loading}
                                            className="custom_select"
                                            showSearch
                                            placeholder="Выбрать город"
                                            optionFilterProp="children"
                                            filterOption={(input, option) =>
                                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                            }
                                            options={locations.locations.map((item, indexedDB) => ({value:item.id,label:item.city}))}
                                        />
                                    </Form.Item>
                                </>
                            :
                                null
                            }
                            
                            <div className='form_buttons'>
                                <Button className='submit' htmlType="submit" loading={advertisement.loading}>Сохранить</Button>
                            </div>
                        </Form>
                    </div>
                </div>
            </Modal>
            <Modal mask={false} className="modal_custom" key="m32" width={1600} title="Изменение информации" open={app_global.panel.indexOf( 'advertisement' ) != -1 } onOk={() => hide_tab_advertisement_item()} onCancel={() => hide_tab_advertisement_item()} footer={null}>
                <div className='header'>
                    <h2>Изменение информации</h2>
                    <h3>
                        {Object(advertisement.advertisement_id).length === 0 ?
                            <Skeleton.Button style={{marginBottom: '5px', maxWidth: '300px'}} size="small" shape="round" active block />
                        :
                            <>{getOrgStatusTag(advertisement.advertisement_id?.status)} {advertisement.advertisement_id?.name}</>
                        }
                    </h3>
                </div>
                <div className='custom_divider' />
                <div className='body'>
                    {Object(advertisement.advertisement_id).length === 0 ?
                        <Skeleton active />
                    :
                        <div className="custom-tabs">
                            <Tabs
                                tabPosition={Object.entries(screens)
                                    .filter((screen) => !!screen[1])
                                    .map((screen) => (
                                        screen[0]
                                )).indexOf( 'xl' ) != -1 ? 'left' : 'top'}
                                activeKey={activeTabAdvertisementEdit}
                                defaultActiveKey="data"
                                onChange={tabAdvertisementEditChange}
                                className="profile_tabs"
                                items={[
                                    {
                                        key: 'data',
                                        label: `Основная информация`,
                                        children: 
                                        <div>
                                            <Form name="form" className="default_form" form={form_advertisement_data} onFinish={form_finish_advertisement_data}>
                                                {["3","4"].indexOf( profile?.data?.type ) !== -1 ?
                                                    <label>Статус</label>
                                                :
                                                    null
                                                }
                                                <Form.Item name={`status`} hidden={["3","4"].indexOf( profile?.data?.type ) === -1} >
                                                    <Select
                                                        disabled={advertisement.loading}
                                                        className="custom_select"
                                                        showSearch
                                                        placeholder="Выбрать статус"
                                                        optionFilterProp="children"
                                                        filterOption={(input, option) =>
                                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                        }
                                                        options={[
                                                            {value:"publish",label:"Опубликовано"},
                                                            {value:"moderation",label:"На проверке"},
                                                            {value:"draft",label:"Черновик"},
                                                            {value:"draft_temp",label:"Снято с публикации"},
                                                            {value:"cancel",label:"Отклонено"}
                                                        ]}
                                                    />
                                                </Form.Item>
                                                {["3","4"].indexOf( profile?.data?.type ) !== -1 ?
                                                    <label>Позиция</label>
                                                :
                                                    null
                                                }
                                                <Form.Item name={`fixed`} hidden={["3","4"].indexOf( profile?.data?.type ) === -1} >
                                                    <Select
                                                        disabled={advertisement.loading}
                                                        className="custom_select"
                                                        placeholder="Выбрать позицию"
                                                        options={[
                                                            {value:"0",label:"Не закреплена"},
                                                            ...Array.from({ length: 5 }, (_, index) => ({
                                                                value: (index + 1).toString(),
                                                                label: `${(index + 1).toString()} позиция`,
                                                                disabled: obj.advertisement_list.find(item => item.fixed === (index + 1).toString() && advertisement.advertisement_id.fixed !== (index + 1).toString())
                                                              }))
                                                        ]}
                                                    />
                                                </Form.Item>
                                                <label>Название</label>
                                                <Form.Item name={`title`} rules={[{ required: true, message: `Пожалуйста, введите название!` }]}>
                                                    <Input disabled={advertisement.loading} className='custom_input' placeholder={`Стоматолог в клинику ...`} />
                                                </Form.Item>
                                                <label style={{display:'none'}}>Тип</label>
                                                <Form.Item hidden name={`type`} rules={[{ required: true, message: `Пожалуйста, укажите тип объявления` }]}>
                                                    <Select
                                                        disabled={advertisement.loading}
                                                        className="custom_select"
                                                        placeholder="Ищу специалиста"
                                                        options={[
                                                            {value:'search_doctor',label:'Ищу специалиста'},
                                                            {value:'search_organization',label:'Ищу место работы'}
                                                        ]}
                                                    />
                                                </Form.Item>
                                                <Form.Item
                                                    noStyle
                                                    shouldUpdate={(prevValues, currentValues) => prevValues[`type`] !== currentValues[`type`]}
                                                >
                                                    {({ getFieldValue }) =>
                                                        <>
                                                            {
                                                                (getFieldValue(`type`) === 'search_organization') ? (
                                                                    <>
                                                                        <label>Специалист</label>
                                                                        <Form.Item name={`connect_id`} rules={[{ required: true, message: `Пожалуйста, выберите специалиста!` }]}>
                                                                            <Select
                                                                                disabled={advertisement.loading}
                                                                                className="custom_select"
                                                                                showSearch
                                                                                placeholder="Выбрать специалиста"
                                                                                optionFilterProp="children"
                                                                                filterOption={(input, option) =>
                                                                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                                                }
                                                                                options={
                                                                                    ["3","4"].indexOf( profile?.data?.type ) !== -1 ?
                                                                                        obj.specialist_list.filter((item) => item.status === 'publish').map((item) => ({label:item.name, value:item.id}))
                                                                                    :
                                                                                        obj.specialist_my.filter((item) => item.status === 'publish').map((item) => ({label:item.name, value:item.id}))
                                                                                }
                                                                            />
                                                                        </Form.Item>
                                                                    </>
                                                                ) : null
                                                            }
                                                            {
                                                                (getFieldValue(`type`) === 'search_doctor') ? (
                                                                    <>
                                                                        <label>Организация</label>
                                                                        <Form.Item name={`connect_id`} rules={[{ required: true, message: `Пожалуйста, выберите организацию!` }]}>
                                                                            <Select
                                                                                disabled={advertisement.loading}
                                                                                className="custom_select"
                                                                                showSearch
                                                                                placeholder="Выбрать организацию"
                                                                                optionFilterProp="children"
                                                                                filterOption={(input, option) =>
                                                                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                                                }
                                                                                options={
                                                                                    ["3","4"].indexOf( profile?.data?.type ) !== -1 ?
                                                                                        obj.organization_list.filter((item) => item.status === 'publish').map((item) => ({label:item.orgname, value:item.id}))
                                                                                    :
                                                                                        obj.organization_my.filter((item) => item.status === 'publish').map((item) => ({label:item.orgname, value:item.id}))
                                                                                }
                                                                            />
                                                                        </Form.Item>
                                                                    </>
                                                                ) : null
                                                            }
                                                        </>
                                                    }
                                                </Form.Item>
                                                <label>Специализации</label>
                                                <Form.Item name={`specializations`} >
                                                    <Select
                                                        mode="tags"
                                                        disabled={advertisement.loading}
                                                        className="custom_select"
                                                        placeholder="Выбрать специализацию"
                                                        options={specialist.specialist_specialization}
                                                    />
                                                </Form.Item>
                                                <label>Город поиска</label>
                                                <Form.Item name={`location`} rules={[{ required: true, message: `Пожалуйста, укажите город` }]}>
                                                    <Select
                                                        disabled={advertisement.loading}
                                                        className="custom_select"
                                                        showSearch
                                                        placeholder="Выбрать город"
                                                        optionFilterProp="children"
                                                        filterOption={(input, option) =>
                                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                        }
                                                        options={locations.locations.map((item, indexedDB) => ({value:item.id,label:item.city}))}
                                                    />
                                                </Form.Item>
                                                <label>Телефон для связи</label>
                                                <Form.Item name={`phone`} >
                                                    <MaskedInput
                                                        disabled={organization.loading}
                                                        mask={SETTINGS_APP.mask[SETTINGS_APP.project_format]['default']}
                                                        placeholder={SETTINGS_APP.mask[SETTINGS_APP.project_format]['placeholder']}
                                                        render={(ref, props) => (
                                                            <input className="ant-input custom_input" type="text" aria-required="true" ref={ref} {...props} />
                                                        )}
                                                    />
                                                </Form.Item>
                                                <label>E-mail для связи</label>
                                                <Form.Item name={`email`} >
                                                    <MaskedInput
                                                        disabled={profile.loading}
                                                        mask={emailMask}
                                                        placeholder="mail@infomedic.uz"
                                                        render={(ref, props) => (
                                                            <input className="ant-input custom_input" type="text" aria-required="true" ref={ref} {...props} />
                                                        )}
                                                    />
                                                </Form.Item>
                                                <div className='form_buttons'>
                                                    {advertisement.advertisement_id?.status === 'publish' ?
                                                        <Button className='button_left' onClick={() => clickUpdAdvertisementUp()}>Поднять объявление выше</Button>
                                                    :
                                                        null
                                                    }
                                                    {(advertisement.advertisement_id?.status === 'draft' && ["2","5"].indexOf( profile?.data?.type ) !== -1) ?
                                                        <Button className='button_left' onClick={() => clickUpdAdvertisementModeration()}>Отправить на проверку</Button>
                                                    :
                                                        null
                                                    }
                                                    {advertisement.advertisement_id?.status === 'publish' ?
                                                        <Button className='button_left' onClick={() => clickUpdAdvertisementTempDraft()}>Снять с публикации</Button>
                                                    :
                                                        null
                                                    }
                                                    {advertisement.advertisement_id?.status === 'draft_temp' ?
                                                        <Button className='button_left' onClick={() => clickUpdAdvertisementPublish()}>Вернуть к публикации</Button>
                                                    :
                                                        null
                                                    }
                                                    <Button className='submit' htmlType="submit" loading={advertisement.loading}>Сохранить</Button>
                                                </div>
                                            </Form>
                                        </div>
                                    },
                                    {
                                        key: 'content',
                                        label: `О вакансии`,
                                        children: 
                                        <div>
                                            <Form name="form" className="default_form" form={form_advertisement_content} onFinish={form_finish_advertisement_content} >
                                                <div className='form_buttons top'>
                                                    <Button className='submit' htmlType="submit" loading={advertisement.loading}>Сохранить</Button>
                                                </div>
                                                <ReactEditorJS 
                                                    token={profile.token}
                                                    API={API}
                                                    ref={editorCoreAdvertisement}
                                                    onInitialize={handleInitializeAdvertisement}
                                                    defaultValue={advertisement.advertisement_id?.content !== undefined ? JSON.parse(advertisement.advertisement_id?.content) : {}}
                                                />
                                                <Form.Item name={`content`} hidden >
                                                    <TextArea rows={4} disabled />
                                                </Form.Item>
                                                <div className='form_buttons'>
                                                    <Button className='submit' htmlType="submit" loading={advertisement.loading}>Сохранить</Button>
                                                </div>
                                            </Form>
                                        </div>
                                    },
                                    {
                                        key: 'location',
                                        label: `Геолокация`,
                                        disabled: advertisement.advertisement_id?.type === 'search_',
                                        children: 
                                        <div>
                                            {locations.loading_search ?
                                                <Skeleton active />
                                            :
                                                <>
                                                    <Form name="form" className="default_form" form={form_advertisement_location} >
                                                        <label>Текущий адрес</label>
                                                        <Form.Item name={`address`} >
                                                            <Input disabled className='custom_input' placeholder={`Кремль, Нижний Новгород`} />
                                                        </Form.Item>
                                                        <label>Широта</label>
                                                        <Form.Item name={`latitude`} >
                                                            <Input disabled className='custom_input' placeholder={`69.232272`} />
                                                        </Form.Item>
                                                        <label>Долгота</label>
                                                        <Form.Item name={`longitude`} >
                                                            <Input disabled className='custom_input' placeholder={`41.320111`} />
                                                        </Form.Item>
                                                    </Form>
                                                    <div className='custom_divider' />
                                                    <Form name="form" className="default_form" form={form_locations_search_search} onFinish={form_finish_locations_search}>
                                                        <Form.Item key="search" name="search">
                                                            <Input className={`custom_input`} placeholder="Поиск по адресу" bordered={false} 
                                                                prefix={
                                                                    <SearchOutlined style={{ color: 'rgba(0,0,0,.45)', fontSize: 20 }} />
                                                                }
                                                                suffix={
                                                                    <Button className='submit_mini' htmlType="submit" loading={locations.loading_search}>Поиск</Button>
                                                                }
                                                            />
                                                        </Form.Item>
                                                    </Form>
                                                    <List
                                                        locale={{ emptyText: <Empty description="Список пуст" />}} 
                                                        className="organization_list"
                                                        dataSource={obj.locations_search}
                                                        pagination={{
                                                            defaultPageSize: 5, 
                                                            hideOnSinglePage: true,
                                                        }}
                                                        renderItem={(item) => (
                                                            <List.Item
                                                                key={item.id}
                                                                actions={[
                                                                    <Button className='submit_mini' loading={locations.loading_search} onClick={() => advertisement_location_select({
                                                                        address:item.address,
                                                                        latitude:item.latitude,
                                                                        longitude:item.longitude
                                                                    })} >Выбрать</Button>
                                                                ]}
                                                            >
                                                                <span className="orgname">{item?.address}</span>
                                                            </List.Item>
                                                        )}
                                                    />
                                                    <div className="map" style={{height:'fit-content'}}>
                                                        <YMaps style={{marginTop:20}}>
                                                            <Map defaultState={{ center: ["56.328259", "44.003193"], zoom: 9 }} height="400px" width="100%" >
                                                                {obj.locations_search.length > 0 ? 
                                                                    obj.locations_search.map((item, index) => 
                                                                        <Placemark key={index} geometry={[item.latitude, item.longitude]} />
                                                                    )
                                                                :
                                                                    <Placemark key="global" geometry={[advertisement.advertisement_id.latitude, advertisement.advertisement_id.longitude]} />
                                                                }
                                                            </Map>
                                                        </YMaps>
                                                    </div>
                                                </>
                                            }
                                        </div>
                                    },
                                ]} 
                            />
                        </div>
                    }
                </div>
            </Modal>
            <section className='container --profile marginTop'>
                <div className='content'>
                    {app_global.loading ?
                        <Skeleton active />
                    :
                        <>
                            {
                                profile.auth ?
                                <div className="profile_content">
                                    <div className="profile_right default_shadow">
                                        <div className="custom-tabs">
                                            <Tabs
                                                tabPosition={Object.entries(screens)
                                                    .filter((screen) => !!screen[1])
                                                    .map((screen) => (
                                                        screen[0]
                                                )).indexOf( 'xl' ) != -1 ? 'left' : 'top'}
                                                activeKey={activeTab}
                                                defaultActiveKey="profile"
                                                onChange={tabChange}
                                                className="profile_tabs"
                                                items={[
                                                    {
                                                        key: 'profile',
                                                        label: `Профиль`,
                                                        children: 
                                                        <div>
                                                            {profile.loading ? 
                                                                <Skeleton active />
                                                            :
                                                                <div>
                                                                    <Form name="form" className="default_form" form={form_profile} onFinish={form_finish_profile}>
                                                                        <label style={{marginTop:20}}>Имя</label>
                                                                        <Form.Item name={`firstname`} rules={[{ required: true, message: `Пожалуйста, введите Ваше имя!` }]}>
                                                                            <Input disabled={profile.loading} className='custom_input' placeholder={randomUser['firstname']} />
                                                                        </Form.Item>
                                                                        <label>Фамилия</label>
                                                                        <Form.Item name={`lastname`} >
                                                                            <Input disabled={profile.loading} className='custom_input' placeholder={randomUser['lastname']} />
                                                                        </Form.Item>
                                                                        <label>Отчество</label>
                                                                        <Form.Item name={`patronymic`} >
                                                                            <Input disabled={profile.loading} className='custom_input' placeholder={randomUser['patronymic']} />
                                                                        </Form.Item>
                                                                        <label>Пол</label>
                                                                        <Form.Item name={`gender`} >
                                                                            <Select
                                                                                disabled={profile.loading}
                                                                                className="custom_select"
                                                                                showSearch
                                                                                placeholder="Выбрать пол"
                                                                                optionFilterProp="children"
                                                                                filterOption={(input, option) =>
                                                                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                                                }
                                                                                options={[
                                                                                    {value:"1",label:"Мужской"},
                                                                                    {value:"2",label:"Женский"},
                                                                                    {value:"3",label:"Не указано"}
                                                                                ]}
                                                                            />
                                                                        </Form.Item>
                                                                        <label>Дата рождения</label>
                                                                        <Form.Item name={`birthdate`} >
                                                                            <TimeInput
                                                                                onChange={({ target: { value } }) => form_profile.setFieldsValue({
                                                                                    birthdate:value  
                                                                                })}>
                                                                            </TimeInput>
                                                                        </Form.Item>
                                                                        <label>Город</label>
                                                                        <Form.Item name={`location`} rules={[{ required: true, message: `Пожалуйста, укажите город` }]}>
                                                                            <Select
                                                                                disabled={profile.loading}
                                                                                className="custom_select"
                                                                                showSearch
                                                                                placeholder="Выбрать город"
                                                                                optionFilterProp="children"
                                                                                filterOption={(input, option) =>
                                                                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                                                }
                                                                                options={locations.locations.map((item, indexedDB) => ({value:item.id,label:item.city}))}
                                                                            />
                                                                        </Form.Item>
                                                                        <div className='form_buttons'>
                                                                            <Link to={`/help`} exact={ true } onClick={() => scroll.scrollToTop()} className='ant-btn ant-btn-default ant-btn-dangerous submit' danger style={{marginLeft:0,display:'flex',alignItems:'center',justifyContent:'center'}} loading={profile.loading}><span>Удалить</span></Link>
                                                                            <Button className='submit' htmlType="submit" loading={profile.loading}>Сохранить</Button>
                                                                        </div>
                                                                    </Form>
                                                                </div>
                                                            }
                                                        </div>
                                                    },
                                                    {
                                                        key: 'safety',
                                                        label: `Безопасность`,
                                                        children: 
                                                        <div>
                                                            {profile.loading ? 
                                                                <Skeleton active />
                                                            :
                                                                <div>
                                                                    <Form name="form" className="default_form" form={form_email} onFinish={form_finish_profile_email} >
                                                                        <label style={{marginTop:20}}>E-mail</label>
                                                                        <Form.Item name={`email`} rules={[{ required: true, message: `Пожалуйста, введите email!` }]}>
                                                                            <MaskedInput
                                                                                disabled={profile.loading}
                                                                                mask={emailMask}
                                                                                placeholder="mail@52c.ru"
                                                                                render={(ref, props) => (
                                                                                    <span class="ant-input-affix-wrapper custom_input ant-input-affix-wrapper-borderless">
                                                                                        <input className="ant-input custom_input" type="text" aria-required="true" ref={ref} {...props} />
                                                                                        {profile.data?.email_confirm === "0" ?
                                                                                            <span style={{display:'none'}}class="ant-input-suffix">
                                                                                                <button type="button" class="ant-btn ant-btn-default submit_mini" onClick={() => clickProfileConfirmEmail()}><span>Подтвердить</span></button>
                                                                                            </span>
                                                                                        :
                                                                                            null
                                                                                        }
                                                                                    </span>
                                                                                )}
                                                                            />
                                                                        </Form.Item>
                                                                        <div className='form_buttons'>
                                                                            <Button className='submit' htmlType="submit" loading={profile.loading}>Сохранить</Button>
                                                                        </div>
                                                                    </Form>

                                                                    <Form name="form" className="default_form" form={form_phone} onFinish={form_finish_profile_phone} >
                                                                        <label style={{marginTop:20}}>Телефон</label>
                                                                        <Form.Item name={`phone`} rules={[{ required: true, message: `Пожалуйста, введите номер телефона!` }]}>
                                                                            <MaskedInput
                                                                                disabled={profile.loading}
                                                                                mask={SETTINGS_APP.mask[SETTINGS_APP.project_format]['account']}
                                                                                placeholder={SETTINGS_APP.mask[SETTINGS_APP.project_format]['placeholder']}
                                                                                render={(ref, props) => (
                                                                                    <span style={{display:'none'}}class="ant-input-affix-wrapper custom_input ant-input-affix-wrapper-borderless">
                                                                                        <input className="ant-input custom_input" type="text" aria-required="true" ref={ref} {...props} />
                                                                                        {profile.data?.phone_confirm === "000" ?
                                                                                            <span class="ant-input-suffix">
                                                                                                <button type="button" class="ant-btn ant-btn-default submit_mini" onClick={() => clickProfileConfirmPhone()}><span>Подтвердить</span></button>
                                                                                            </span>
                                                                                        :
                                                                                            null
                                                                                        }
                                                                                    </span>
                                                                                )}
                                                                            />
                                                                        </Form.Item>
                                                                        <div className='form_buttons'>
                                                                            <Button className='submit' htmlType="submit" loading={profile.loading}>Сохранить</Button>
                                                                        </div>
                                                                    </Form>

                                                                    <Form name="form" className="default_form" form={form_password} onFinish={form_finish_profile_password} >
                                                                        <label style={{marginTop:20}}>Старый пароль</label>
                                                                        <Form.Item name={`password`} rules={[{ required: true, message: `Пожалуйста, введите старый пароль!` }]}>
                                                                            <Input.Password disabled={profile.loading} className='custom_input' placeholder={`•••••••••`} />
                                                                        </Form.Item>
                                                                        <label>Новый пароль</label>
                                                                        <Form.Item name={`password_new`} rules={[{ required: true, message: `Пожалуйста, введите новый пароль!` }]}>
                                                                            <Input.Password onChange={(e) => onChangePassword(e)} disabled={profile.loading} className='custom_input' placeholder={`•••••••••`} />
                                                                        </Form.Item>
                                                                        <Progress percent={password_complexity} />
                                                                        <div className='form_buttons'>
                                                                            <Button className='submit' htmlType="submit" loading={profile.loading}>Сохранить</Button>
                                                                        </div>
                                                                    </Form>
                                                                </div>
                                                            }
                                                        </div>
                                                    },
                                                    {
                                                        key: 'reviews_my',
                                                        label: `Мои отзывы`,
                                                        children: 
                                                        <div>
                                                            {reviews.loading ?
                                                                <Skeleton active />
                                                            :
                                                                <>
                                                                    <List
                                                                        locale={{ emptyText: <Empty description="Список пуст" />}} 
                                                                        className="organization_list"
                                                                        dataSource={obj.reviews_my}
                                                                        pagination={{
                                                                            defaultPageSize: 5, 
                                                                            hideOnSinglePage: true,
                                                                            onChange(current, pageSize) {
                                                                                setPageMyReviews(current);
                                                                            },
                                                                            defaultCurrent: pageMyReviews
                                                                        }}
                                                                        renderItem={(item) => (
                                                                            <List.Item
                                                                                key={item.id}
                                                                                actions={[
                                                                                    <Button className='submit_mini' loading={reviews.loading} onClick={() => show_tab_reviews_item(item.id)}>Открыть</Button>
                                                                                ]}
                                                                            >
                                                                                <span className="orgname">{getOrgStatus(item?.status)} <span style={{fontWeight:500}} className='date'>{moment.unix(item.date_create).format('DD MMMM YYYY в HH:mm')}</span> для {item?.orgname}</span>
                                                                            </List.Item>
                                                                        )}
                                                                    />
                                                                </>
                                                            }
                                                        </div>
                                                    },
                                                    {
                                                        key: 'reviews_list',
                                                        label: `Все отзывы`,
                                                        disabled: ["3","4"].indexOf( profile?.data?.type ) === -1,
                                                        children: 
                                                        <div>
                                                            {reviews.loading ?
                                                                <Skeleton active />
                                                            :
                                                                <>
                                                                    <Form name="form" className="default_form" form={form_reviews_list_search}>
                                                                        <Form.Item key="search" name="search">
                                                                            <Input className={`custom_input`} placeholder="Поиск по названию" onChange={ reviews_list_search_change } bordered={false} 
                                                                                prefix={
                                                                                    <SearchOutlined style={{ color: 'rgba(0,0,0,.45)', fontSize: 20 }} />
                                                                                }
                                                                                suffix={
                                                                                    ["3","4"].indexOf( profile?.data?.type ) !== -1 ?
                                                                                    <Button className='submit_mini' onClick={() => show_tab_reviews_add()}>Добавить</Button>
                                                                                    :
                                                                                    false
                                                                                }
                                                                            />
                                                                        </Form.Item>
                                                                        <label>Статус</label>
                                                                        <Form.Item name={`status`} >
                                                                            <Select
                                                                                onChange={ reviews_list_search_status_change }
                                                                                className="custom_select"
                                                                                showSearch
                                                                                defaultValue={``}
                                                                                placeholder="Выбрать тип"
                                                                                optionFilterProp="children"
                                                                                filterOption={(input, option) =>
                                                                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                                                }
                                                                                options={[
                                                                                    {value:"",label:"Не выбрано"},
                                                                                    {value:"publish",label:"Опубликовано"},
                                                                                    {value:"moderation",label:"На проверке"},
                                                                                    {value:"cancel",label:"Отклонено"},
                                                                                ]}
                                                                            />
                                                                        </Form.Item>
                                                                    </Form>
                                                                    <List
                                                                        locale={{ emptyText: <Empty description="Список пуст" />}} 
                                                                        className="organization_list"
                                                                        dataSource={obj.reviews_list}
                                                                        pagination={{
                                                                            defaultPageSize: 5, 
                                                                            hideOnSinglePage: true,
                                                                            onChange(current, pageSize) {
                                                                                setPageListReviews(current);
                                                                            },
                                                                            defaultCurrent: pageListReviews
                                                                        }}
                                                                        renderItem={(item) => (
                                                                            <List.Item
                                                                                key={item.id}
                                                                                actions={[
                                                                                    <Button className='submit_mini' loading={reviews.loading} onClick={() => show_tab_reviews_item(item.id)}>Открыть</Button>
                                                                                ]}
                                                                            >
                                                                                <span className="orgname">{getOrgStatus(item?.status)} <span style={{fontWeight:500}} className='date'>{moment.unix(item.date_create).format('DD MMMM YYYY в HH:mm')}</span> для {item?.orgname}</span>
                                                                            </List.Item>
                                                                        )}
                                                                    />
                                                                </>
                                                            }
                                                        </div>
                                                    },
                                                    {
                                                        key: 'categories',
                                                        label: `Категории организаций`,
                                                        disabled: ["3","4"].indexOf( profile?.data?.type ) === -1,
                                                        children: 
                                                        <div>
                                                            {posts.loading ?
                                                                <Skeleton active />
                                                            :
                                                                <>
                                                                    <Form name="form" className="default_form" form={form_categories_list_search}>
                                                                        <Form.Item key="search" name="search">
                                                                            <Input className={`custom_input`} placeholder="Поиск по названию" onChange={ categories_list_search_change } bordered={false} 
                                                                                prefix={
                                                                                    <SearchOutlined style={{ color: 'rgba(0,0,0,.45)', fontSize: 20 }} />
                                                                                }
                                                                                suffix={
                                                                                    ["2","3","4"].indexOf( profile?.data?.type ) !== -1 ?
                                                                                    <Button className='submit_mini' onClick={() => show_tab_categories_add()}>Добавить</Button>
                                                                                    :
                                                                                    false
                                                                                }
                                                                            />
                                                                        </Form.Item>
                                                                    </Form>
                                                                    <List
                                                                        locale={{ emptyText: <Empty description="Список пуст" />}} 
                                                                        className="organization_list"
                                                                        dataSource={obj.categories_list}
                                                                        pagination={{
                                                                            defaultPageSize: 10, 
                                                                            hideOnSinglePage: true,
                                                                            onChange(current, pageSize) {
                                                                                setPageCategories(current);
                                                                            },
                                                                            defaultCurrent: pageCategories
                                                                        }}
                                                                        renderItem={(item) => (
                                                                            <List.Item
                                                                                key={item.id}
                                                                                actions={[
                                                                                    <Button className='submit_mini' loading={posts.loading} onClick={() => show_tab_categories_item({
                                                                                        id:item.value,
                                                                                        title:item.label
                                                                                    })}>Изменить</Button>
                                                                                ]}
                                                                            >
                                                                                <span className="orgname">{item?.label}</span>
                                                                            </List.Item>
                                                                        )}
                                                                    />
                                                                </>
                                                            }
                                                        </div>
                                                    },
                                                    {
                                                        key: 'organization_my',
                                                        label: `Мои организации`,
                                                        disabled: ["2","3","4"].indexOf( profile?.data?.type ) === -1,
                                                        children: 
                                                        <div>
                                                            {organization.loading ?
                                                                <Skeleton active />
                                                            :
                                                                <>
                                                                    <Form name="form" className="default_form" form={form_organization_my_search}>
                                                                        <Form.Item key="search" name="search">
                                                                            <Input className={`custom_input`} placeholder="Поиск по названию" onChange={ organization_my_search_change } bordered={false} 
                                                                                prefix={
                                                                                    <SearchOutlined style={{ color: 'rgba(0,0,0,.45)', fontSize: 20 }} />
                                                                                }
                                                                                suffix={
                                                                                    ["2","3","4"].indexOf( profile?.data?.type ) !== -1 ?
                                                                                    <Button className='submit_mini' onClick={() => show_tab_organization_my_add()}>Добавить</Button>
                                                                                    :
                                                                                    false
                                                                                }
                                                                            />
                                                                        </Form.Item>
                                                                    </Form>
                                                                    <List
                                                                        locale={{ emptyText: <Empty description="Список пуст" />}} 
                                                                        className="organization_list"
                                                                        dataSource={obj.organization_my}
                                                                        pagination={{
                                                                            defaultPageSize: 5, 
                                                                            hideOnSinglePage: true,
                                                                            onChange(current, pageSize) {
                                                                                setPageMyOrganizations(current);
                                                                            },
                                                                            defaultCurrent: pageMyOrganizations
                                                                        }}
                                                                        renderItem={(item) => (
                                                                            <List.Item
                                                                                key={item.id}
                                                                                actions={[
                                                                                    <Button className='submit_mini' loading={organization.loading} onClick={() => show_tab_organization_my_item(item.id)}>Изменить</Button>
                                                                                ]}
                                                                            >
                                                                                <span className="orgname">{getOrgStatus(item?.status)}{item?.orgname}</span>
                                                                            </List.Item>
                                                                        )}
                                                                    />
                                                                </>
                                                            }
                                                        </div>
                                                    },
                                                    {
                                                        key: 'organization_list',
                                                        label: `Все организации`,
                                                        disabled: ["3","4"].indexOf( profile?.data?.type ) === -1,
                                                        children: 
                                                        <div>
                                                            {organization.loading ?
                                                                <Skeleton active />
                                                            :
                                                                <>
                                                                    <Form name="form" className="default_form" form={form_organization_list_search}>
                                                                        <Form.Item key="search" name="search">
                                                                            <Input className={`custom_input`} placeholder="Поиск по названию" onChange={ organization_list_search_change } bordered={false} 
                                                                                prefix={
                                                                                    <SearchOutlined style={{ color: 'rgba(0,0,0,.45)', fontSize: 20 }} />
                                                                                } 
                                                                            />
                                                                        </Form.Item>
                                                                        <label>Статус</label>
                                                                        <Form.Item name={`status`} >
                                                                            <Select
                                                                                onChange={ organization_list_search_status_change }
                                                                                className="custom_select"
                                                                                showSearch
                                                                                defaultValue={``}
                                                                                placeholder="Выбрать тип"
                                                                                optionFilterProp="children"
                                                                                filterOption={(input, option) =>
                                                                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                                                }
                                                                                options={[
                                                                                    {value:"",label:"Не выбрано"},
                                                                                    {value:"publish",label:"Опубликовано"},
                                                                                    {value:"draft",label:"Черновик"},
                                                                                    {value:"moderation",label:"На проверке"},
                                                                                    {value:"cancel",label:"Отклонено"},
                                                                                ]}
                                                                            />
                                                                        </Form.Item>
                                                                    </Form>
                                                                    <List
                                                                        locale={{ emptyText: <Empty description="Список пуст" />}} 
                                                                        className="organization_list"
                                                                        dataSource={obj.organization_list}
                                                                        pagination={{
                                                                            defaultPageSize: 5, 
                                                                            hideOnSinglePage: true,
                                                                            onChange(current, pageSize) {
                                                                                setPageListOrganizations(current);
                                                                            },
                                                                            defaultCurrent: pageListOrganizations
                                                                        }}
                                                                        renderItem={(item) => (
                                                                            <List.Item
                                                                                key={item.id}
                                                                                actions={[
                                                                                    <Button className='submit_mini' loading={organization.loading} onClick={() => show_tab_organization_list_item(item.id)}>Изменить</Button>
                                                                                ]}
                                                                            >
                                                                                <span className="orgname">{getOrgStatus(item?.status)}{item?.orgname}</span>
                                                                            </List.Item>
                                                                        )}
                                                                    />
                                                                </>
                                                            }
                                                        </div>
                                                    },
                                                    {
                                                        key: 'specialist_my',
                                                        label: `Мои анкеты специалистов`,
                                                        disabled: ["2","3","4","5"].indexOf( profile?.data?.type ) === -1,
                                                        children: 
                                                        <div>
                                                            {specialist.loading ?
                                                                <Skeleton active />
                                                            :
                                                                <>
                                                                    <Form name="form" className="default_form" form={form_specialist_my_search}>
                                                                        <Form.Item key="search" name="search">
                                                                            <Input className={`custom_input`} placeholder="Поиск по ФИО" onChange={ specialist_my_search_change } bordered={false} 
                                                                                prefix={
                                                                                    <SearchOutlined style={{ color: 'rgba(0,0,0,.45)', fontSize: 20 }} />
                                                                                }
                                                                                suffix={
                                                                                    ["2","3","4","5"].indexOf( profile?.data?.type ) !== -1 ?
                                                                                    <Button className='submit_mini' onClick={() => show_tab_specialist_my_add()}>Добавить</Button>
                                                                                    :
                                                                                    false
                                                                                }
                                                                            />
                                                                        </Form.Item>
                                                                    </Form>
                                                                    <List
                                                                        locale={{ emptyText: <Empty description="Список пуст" />}} 
                                                                        className="organization_list"
                                                                        dataSource={obj.specialist_my}
                                                                        pagination={{
                                                                            defaultPageSize: 5, 
                                                                            hideOnSinglePage: true,
                                                                            onChange(current, pageSize) {
                                                                                setPageMySpecialists(current);
                                                                            },
                                                                            defaultCurrent: pageMySpecialists
                                                                        }}
                                                                        renderItem={(item) => (
                                                                            <List.Item
                                                                                key={item.id}
                                                                                actions={[
                                                                                    <Button className='submit_mini' loading={specialist.loading} onClick={() => show_tab_specialist_my_item(item.id)}>Изменить</Button>
                                                                                ]}
                                                                            >
                                                                                <span className="orgname">{getOrgStatus(item?.status)}{item?.name}</span>
                                                                            </List.Item>
                                                                        )}
                                                                    />
                                                                </>
                                                            }
                                                        </div>
                                                    },

                                                    {
                                                        key: 'specialist_list',
                                                        label: `Все анкеты специалистов`,
                                                        disabled: ["3","4"].indexOf( profile?.data?.type ) === -1,
                                                        children: 
                                                        <div>
                                                            {specialist.loading ?
                                                                <Skeleton active />
                                                            :
                                                                <>
                                                                    <Form name="form" className="default_form" form={form_specialist_list_search}>
                                                                        <Form.Item key="search" name="search">
                                                                            <Input className={`custom_input`} placeholder="Поиск по ФИО" onChange={ specialist_list_search_change } bordered={false} 
                                                                                prefix={
                                                                                    <SearchOutlined style={{ color: 'rgba(0,0,0,.45)', fontSize: 20 }} />
                                                                                } 
                                                                            />
                                                                        </Form.Item>
                                                                        <label>Статус</label>
                                                                        <Form.Item name={`status`} >
                                                                            <Select
                                                                                onChange={ specialist_list_search_status_change }
                                                                                className="custom_select"
                                                                                showSearch
                                                                                defaultValue={``}
                                                                                placeholder="Выбрать тип"
                                                                                optionFilterProp="children"
                                                                                filterOption={(input, option) =>
                                                                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                                                }
                                                                                options={[
                                                                                    {value:"",label:"Не выбрано"},
                                                                                    {value:"publish",label:"Опубликовано"},
                                                                                    {value:"draft",label:"Черновик"},
                                                                                    {value:"moderation",label:"На проверке"},
                                                                                    {value:"cancel",label:"Отклонено"},
                                                                                ]}
                                                                            />
                                                                        </Form.Item>
                                                                    </Form>
                                                                    <List
                                                                        locale={{ emptyText: <Empty description="Список пуст" />}} 
                                                                        className="organization_list"
                                                                        dataSource={obj.specialist_list}
                                                                        pagination={{
                                                                            defaultPageSize: 5, 
                                                                            hideOnSinglePage: true,
                                                                            onChange(current, pageSize) {
                                                                                setPageListSpecialists(current);
                                                                            },
                                                                            defaultCurrent: pageListSpecialists
                                                                        }}
                                                                        renderItem={(item) => (
                                                                            <List.Item
                                                                                key={item.id}
                                                                                actions={[
                                                                                    <Button className='submit_mini' loading={specialist.loading} onClick={() => show_tab_specialist_list_item(item.id)}>Изменить</Button>
                                                                                ]}
                                                                            >
                                                                                <span className="orgname">{getOrgStatus(item?.status)}{item?.name}</span>
                                                                            </List.Item>
                                                                        )}
                                                                    />
                                                                </>
                                                            }
                                                        </div>
                                                    },
                                                    {
                                                        key: 'publications_category',
                                                        label: `Категории публикаций`,
                                                        disabled: ["4"].indexOf( profile?.data?.type ) === -1,
                                                        children: 
                                                        <div>
                                                            {posts.loading ?
                                                                <Skeleton active />
                                                            :
                                                                <>
                                                                    <Form name="form" className="default_form" form={form_posts_category_list_search}>
                                                                        <Form.Item key="search" name="search">
                                                                            <Input className={`custom_input`} placeholder="Поиск по названию" onChange={ posts_category_list_search_change } bordered={false} 
                                                                                prefix={
                                                                                    <SearchOutlined style={{ color: 'rgba(0,0,0,.45)', fontSize: 20 }} />
                                                                                }
                                                                                suffix={
                                                                                    ["4"].indexOf( profile?.data?.type ) !== -1 ?
                                                                                    <Button className='submit_mini' onClick={() => show_tab_posts_category_add()}>Добавить</Button>
                                                                                    :
                                                                                    false
                                                                                }
                                                                            />
                                                                        </Form.Item>
                                                                        <label>Статус</label>
                                                                        <Form.Item name={`status`} >
                                                                            <Select
                                                                                onChange={ posts_category_list_search_status_change }
                                                                                className="custom_select"
                                                                                showSearch
                                                                                defaultValue={``}
                                                                                placeholder="Выбрать тип"
                                                                                optionFilterProp="children"
                                                                                filterOption={(input, option) =>
                                                                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                                                }
                                                                                options={[
                                                                                    {value:"",label:"Не выбрано"},
                                                                                    {value:"publish",label:"Опубликовано"},
                                                                                    {value:"draft",label:"Черновик"},
                                                                                    {value:"moderation",label:"На проверке"},
                                                                                    {value:"cancel",label:"Отклонено"},
                                                                                ]}
                                                                            />
                                                                        </Form.Item>
                                                                        <label>Категория</label>
                                                                        <Form.Item name={`category`} >
                                                                            <Select
                                                                                onChange={ posts_category_list_search_category_change }
                                                                                className="custom_select"
                                                                                showSearch
                                                                                defaultValue={``}
                                                                                placeholder="Выбрать категорию"
                                                                                optionFilterProp="children"
                                                                                filterOption={(input, option) =>
                                                                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                                                }
                                                                                options={obj.posts_category_category_list}
                                                                            />
                                                                        </Form.Item>
                                                                    </Form>
                                                                    <List
                                                                        locale={{ emptyText: <Empty description="Список пуст" />}} 
                                                                        className="organization_list"
                                                                        dataSource={obj.posts_category_list}
                                                                        pagination={{
                                                                            defaultPageSize: 10, 
                                                                            hideOnSinglePage: true,
                                                                            onChange(current, pageSize) {
                                                                                setPagePostsCategories(current);
                                                                            },
                                                                            defaultCurrent: pagePostsCategories
                                                                        }}
                                                                        renderItem={(item) => (
                                                                            <List.Item
                                                                                key={item.id}
                                                                                actions={[
                                                                                    <Button className='submit_mini' loading={posts.loading} onClick={() => show_tab_posts_category_item(item.id)}>Изменить</Button>
                                                                                ]}
                                                                            >
                                                                                <span className="orgname">{getOrgStatus(item?.status)}{item?.title}</span>
                                                                            </List.Item>
                                                                        )}
                                                                    />
                                                                </>
                                                            }
                                                        </div>
                                                    },
                                                    {
                                                        key: 'publications',
                                                        label: `Публикации`,
                                                        disabled: ["3","4"].indexOf( profile?.data?.type ) === -1,
                                                        children: 
                                                        <div>
                                                            {posts.loading ?
                                                                <Skeleton active />
                                                            :
                                                                <>
                                                                    <Form name="form" className="default_form" form={form_posts_list_search}>
                                                                        <Form.Item key="search" name="search">
                                                                            <Input className={`custom_input`} placeholder="Поиск по названию" onChange={ posts_list_search_change } bordered={false} 
                                                                                prefix={
                                                                                    <SearchOutlined style={{ color: 'rgba(0,0,0,.45)', fontSize: 20 }} />
                                                                                }
                                                                                suffix={
                                                                                    ["3","4"].indexOf( profile?.data?.type ) !== -1 ?
                                                                                    <Button className='submit_mini' onClick={() => show_tab_posts_add()}>Добавить</Button>
                                                                                    :
                                                                                    false
                                                                                }
                                                                            />
                                                                        </Form.Item>
                                                                        <label>Статус</label>
                                                                        <Form.Item name={`status`} >
                                                                            <Select
                                                                                onChange={ posts_list_search_status_change }
                                                                                className="custom_select"
                                                                                showSearch
                                                                                defaultValue={``}
                                                                                placeholder="Выбрать тип"
                                                                                optionFilterProp="children"
                                                                                filterOption={(input, option) =>
                                                                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                                                }
                                                                                options={[
                                                                                    {value:"",label:"Не выбрано"},
                                                                                    {value:"publish",label:"Опубликовано"},
                                                                                    {value:"draft",label:"Черновик"},
                                                                                    {value:"moderation",label:"На проверке"},
                                                                                    {value:"cancel",label:"Отклонено"},
                                                                                ]}
                                                                            />
                                                                        </Form.Item>
                                                                        <label>Категория</label>
                                                                        <Form.Item name={`category`} >
                                                                            <Select
                                                                                onChange={ posts_list_search_category_change }
                                                                                className="custom_select"
                                                                                showSearch
                                                                                defaultValue={``}
                                                                                placeholder="Выбрать категорию"
                                                                                optionFilterProp="children"
                                                                                filterOption={(input, option) =>
                                                                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                                                }
                                                                                options={[
                                                                                    {value:"",label:"Не выбрано"},
                                                                                    {value:"1",label:"Новости портала"},
                                                                                    {value:"6",label:"Новости минздрава"},
                                                                                    {value:"3",label:"Статьи о здоровье"},
                                                                                    {value:"9",label:"Полезная информация"},
                                                                                    {value:"4",label:"Памятки для пациентов"},
                                                                                    {value:"5",label:"Направления медицины"},
                                                                                    {value:"7",label:"Симптоматика"},
                                                                                    {value:"8",label:"Продукты питания"},
                                                                                    {value:"11",label:"Советы по правильному питанию"},
                                                                                    {value:"12",label:"Физическая активность"},
                                                                                    {value:"13",label:"Здоровый сон"},
                                                                                    {value:"14",label:"Статьи"},
                                                                                ]}
                                                                            />
                                                                        </Form.Item>
                                                                    </Form>
                                                                    <List
                                                                        locale={{ emptyText: <Empty description="Список пуст" />}} 
                                                                        className="organization_list"
                                                                        dataSource={obj.posts_list}
                                                                        pagination={{
                                                                            defaultPageSize: 10, 
                                                                            hideOnSinglePage: true,
                                                                            onChange(current, pageSize) {
                                                                                setPagePosts(current);
                                                                            },
                                                                            defaultCurrent: pagePosts
                                                                        }}
                                                                        renderItem={(item) => (
                                                                            <List.Item
                                                                                key={item.id}
                                                                                actions={[
                                                                                    <Button className='submit_mini' loading={posts.loading} onClick={() => show_tab_posts_item(item.id)}>Изменить</Button>
                                                                                ]}
                                                                            >
                                                                                <span className="orgname">{getOrgStatus(item?.status)}{item?.title}</span>
                                                                            </List.Item>
                                                                        )}
                                                                    />
                                                                </>
                                                            }
                                                        </div>
                                                    },
                                                    {
                                                        key: 'surveys',
                                                        label: `Опросы`,
                                                        disabled: ["3","4"].indexOf( profile?.data?.type ) === -1,
                                                        children: 
                                                        <div>
                                                            {surveys.loading ?
                                                                <Skeleton active />
                                                            :
                                                                <>
                                                                    <Form name="form" className="default_form" form={form_surveys_list_search}>
                                                                        <Form.Item key="search" name="search">
                                                                            <Input className={`custom_input`} placeholder="Поиск по названию" onChange={ surveys_list_search_change } bordered={false} 
                                                                                prefix={
                                                                                    <SearchOutlined style={{ color: 'rgba(0,0,0,.45)', fontSize: 20 }} />
                                                                                }
                                                                                suffix={
                                                                                    ["2","3","4"].indexOf( profile?.data?.type ) !== -1 ?
                                                                                    <Button className='submit_mini' onClick={() => show_tab_surveys_add()}>Добавить</Button>
                                                                                    :
                                                                                    false
                                                                                }
                                                                            />
                                                                        </Form.Item>
                                                                        <label>Статус</label>
                                                                        <Form.Item name={`status`} >
                                                                            <Select
                                                                                onChange={ surveys_list_search_status_change }
                                                                                className="custom_select"
                                                                                showSearch
                                                                                defaultValue={``}
                                                                                placeholder="Выбрать тип"
                                                                                optionFilterProp="children"
                                                                                filterOption={(input, option) =>
                                                                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                                                }
                                                                                options={[
                                                                                    {value:"",label:"Не выбрано"},
                                                                                    {value:"publish",label:"Опубликовано"},
                                                                                    {value:"draft",label:"Черновик"},
                                                                                    {value:"moderation",label:"На проверке"},
                                                                                    {value:"cancel",label:"Отклонено"},
                                                                                ]}
                                                                            />
                                                                        </Form.Item>
                                                                    </Form>
                                                                    <List
                                                                        locale={{ emptyText: <Empty description="Список пуст" />}} 
                                                                        className="organization_list"
                                                                        dataSource={obj.surveys_list}
                                                                        pagination={{
                                                                            defaultPageSize: 10, 
                                                                            hideOnSinglePage: true,
                                                                            onChange(current, pageSize) {
                                                                                setPageSurveys(current);
                                                                            },
                                                                            defaultCurrent: pageSurveys
                                                                        }}
                                                                        renderItem={(item) => (
                                                                            <List.Item
                                                                                key={item.id}
                                                                                actions={[
                                                                                    <Button className='submit_mini' loading={posts.loading} onClick={() => show_tab_surveys_item(item.id)}>Изменить</Button>
                                                                                ]}
                                                                            >
                                                                                <span className="orgname">{getOrgStatus(item?.status)}{item?.title}</span>
                                                                            </List.Item>
                                                                        )}
                                                                    />
                                                                </>
                                                            }
                                                        </div>
                                                    },
                                                    {
                                                        key: 'users',
                                                        label: `Пользователи`,
                                                        disabled: ["3","4"].indexOf( profile?.data?.type ) === -1,
                                                        children: 
                                                        <div>
                                                            {app_global.loading ?
                                                                <Skeleton active />
                                                            :
                                                                <>
                                                                    <Form name="form" className="default_form" form={form_users_list_search}>
                                                                        <Form.Item key="search" name="search">
                                                                            <Input className={`custom_input`} placeholder="Поиск" onChange={ users_list_search_change } bordered={false} 
                                                                                prefix={
                                                                                    <SearchOutlined style={{ color: 'rgba(0,0,0,.45)', fontSize: 20 }} />
                                                                                }
                                                                            />
                                                                        </Form.Item>
                                                                    </Form>
                                                                    <List
                                                                        locale={{ emptyText: <Empty description="Список пуст" />}} 
                                                                        className="organization_list"
                                                                        dataSource={obj.users}
                                                                        pagination={{
                                                                            defaultPageSize: 10, 
                                                                            hideOnSinglePage: true,
                                                                            onChange(current, pageSize) {
                                                                                setPageUsers(current);
                                                                            },
                                                                            defaultCurrent: pageUsers
                                                                        }}
                                                                        renderItem={(item) => (
                                                                            <List.Item
                                                                                key={item.id}
                                                                                actions={[
                                                                                    <Button className='submit_mini' loading={app_global.loading} onClick={() => show_tab_users_item(item)}>Изменить</Button>
                                                                                ]}
                                                                            >
                                                                                <Tooltip placement="top" title={
                                                                                    <div>
                                                                                        <p><strong>Email: </strong>{item?.email}</p>
                                                                                        <p><strong>Телефон: </strong>{customPhoneMask(item?.phone)}</p>
                                                                                    </div>
                                                                                }><span className="orgname">{[item?.lastname,item?.firstname].join(' ').replace(/ +/g, ' ').trim()}</span></Tooltip>
                                                                            </List.Item>
                                                                        )}
                                                                    />
                                                                </>
                                                            }
                                                        </div>
                                                    },
                                                    {
                                                        key: 'advertisement_my',
                                                        label: `Мои вакансии`,
                                                        disabled: ["2","5","3","4"].indexOf( profile?.data?.type ) === -1,
                                                        children: 
                                                        <div>
                                                            {advertisement.loading ?
                                                                <Skeleton active />
                                                            :
                                                                <>
                                                                    <Form name="form" className="default_form" form={form_advertisement_my_search}>
                                                                        <Form.Item key="search" name="search">
                                                                            <Input className={`custom_input`} placeholder="Поиск по названию" onChange={ advertisement_my_search_change } bordered={false} 
                                                                                prefix={
                                                                                    <SearchOutlined style={{ color: 'rgba(0,0,0,.45)', fontSize: 20 }} />
                                                                                }
                                                                                suffix={
                                                                                    ["2","3","4","5"].indexOf( profile?.data?.type ) !== -1 ?
                                                                                    <Button className='submit_mini' onClick={() => show_tab_advertisement_my_add()}>Добавить</Button>
                                                                                    :
                                                                                    false
                                                                                }
                                                                            />
                                                                        </Form.Item>
                                                                    </Form>
                                                                    <List
                                                                        locale={{ emptyText: <Empty description="Список пуст" />}} 
                                                                        className="organization_list"
                                                                        dataSource={obj.advertisement_my}
                                                                        pagination={{
                                                                            defaultPageSize: 5, 
                                                                            hideOnSinglePage: true,
                                                                            onChange(current, pageSize) {
                                                                                setPageMyAdvertisements(current);
                                                                            },
                                                                            defaultCurrent: pageMyAdvertisements
                                                                        }}
                                                                        renderItem={(item) => (
                                                                            <List.Item
                                                                                key={item.id}
                                                                                actions={[
                                                                                    <Button className='submit_mini' loading={advertisement.loading} onClick={() => show_tab_advertisement_my_item(item.id)}>Изменить</Button>
                                                                                ]}
                                                                            >
                                                                                <span className="orgname">{getOrgStatus(item?.status)}{item?.title}</span>
                                                                            </List.Item>
                                                                        )}
                                                                    />
                                                                </>
                                                            }
                                                        </div>
                                                    },
                                                    {
                                                        key: 'advertisement_list',
                                                        label: `Все вакансии`,
                                                        disabled: ["3","4"].indexOf( profile?.data?.type ) === -1,
                                                        children: 
                                                        <div>
                                                            {advertisement.loading ?
                                                                <Skeleton active />
                                                            :
                                                                <>
                                                                    <Form name="form" className="default_form" form={form_advertisement_list_search}>
                                                                        <Form.Item key="search" name="search">
                                                                            <Input className={`custom_input`} placeholder="Поиск по названию" onChange={ advertisement_list_search_change } bordered={false} 
                                                                                prefix={
                                                                                    <SearchOutlined style={{ color: 'rgba(0,0,0,.45)', fontSize: 20 }} />
                                                                                } 
                                                                            />
                                                                        </Form.Item>
                                                                        <label>Статус</label>
                                                                        <Form.Item name={`status`} >
                                                                            <Select
                                                                                onChange={ advertisement_list_search_status_change }
                                                                                className="custom_select"
                                                                                showSearch
                                                                                defaultValue={``}
                                                                                placeholder="Выбрать тип"
                                                                                optionFilterProp="children"
                                                                                filterOption={(input, option) =>
                                                                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                                                }
                                                                                options={[
                                                                                    {value:"",label:"Не выбрано"},
                                                                                    {value:"publish",label:"Опубликовано"},
                                                                                    {value:"draft",label:"Черновик"},
                                                                                    {value:"moderation",label:"На проверке"},
                                                                                    {value:"cancel",label:"Отклонено"},
                                                                                ]}
                                                                            />
                                                                        </Form.Item>
                                                                    </Form>
                                                                    <List
                                                                        locale={{ emptyText: <Empty description="Список пуст" />}} 
                                                                        className="organization_list"
                                                                        dataSource={obj.advertisement_list}
                                                                        pagination={{
                                                                            defaultPageSize: 5, 
                                                                            hideOnSinglePage: true,
                                                                            onChange(current, pageSize) {
                                                                                setPageListAdvertisements(current);
                                                                            },
                                                                            defaultCurrent: pageListAdvertisements
                                                                        }}
                                                                        renderItem={(item) => (
                                                                            <List.Item
                                                                                key={item.id}
                                                                                actions={[
                                                                                    <Button className='submit_mini' loading={advertisement.loading} onClick={() => show_tab_advertisement_list_item(item.id)}>Изменить</Button>
                                                                                ]}
                                                                            >
                                                                                <span className="orgname">{getOrgStatus(item?.status)}{+item?.fixed > 0 ? `#${item.fixed} ` : ''}{item?.title}</span>
                                                                            </List.Item>
                                                                        )}
                                                                    />
                                                                </>
                                                            }
                                                        </div>
                                                    },
                                                ]}
                                            />
                                        </div>
                                    </div>
                                    <div className="profile_left">
                                        <div className="information">
                                            {["2","3","4","5"].indexOf( profile?.data?.type ) !== -1 ?
                                                <Link style={{fontSize:14, backgroundColor:'#48ab48'}} className='url_info ant-btn ant-btn-primary default' to='/specialist-info' exact={ true } onClick={() => scroll.scrollToTop()}><span>Информация для специалистов</span></Link>
                                            :
                                                null
                                            }
                                        </div>
                                        <div className="profile_card">
                                            <div className="profile_avatar">
                                                {profile.loading ?
                                                    <Skeleton.Avatar style={{width:100, height:100}} shape="circle" active bloak />
                                                :
                                                    <img src={`${UPLOADS}/temp/avatar_${profile.data.gender}.jpg`} />
                                                }
                                            </div>
                                            <div className="card_content">
                                                <div className="card_content_body">
                                                    {profile.loading ?
                                                        <Skeleton.Button style={{marginBottom: '20px'}} shape="round" active block={false} />
                                                    :
                                                        <>
                                                            <h1 style={{margin:0}}>{profile.data.firstname}</h1>
                                                            {profile.data.lastname ? <h1>{profile.data.lastname}</h1> : null}
                                                        </>
                                                    }
                                                    {profile.loading ?
                                                            <>
                                                                <Skeleton.Button style={{marginBottom: '5px'}} size="small" shape="round" active block />
                                                                <Skeleton.Button style={{marginBottom: '5px'}} size="small" shape="round" active block />
                                                                <Skeleton.Button style={{marginBottom: '5px'}} size="small" shape="round" active block />
                                                            </>
                                                        :
                                                        <>
                                                            <p><b>Email</b>: {profile.data.email}</p>
                                                            <p><b>Телефон</b>: {customPhoneMask(profile.data.phone)}</p>
                                                            <p><b>Город</b>: {profile.data.city}</p>
                                                        </>
                                                    }
                                                </div>
                                                <div className="card_content_footer">
                                                    { activeTab !== 'profile' ? 
                                                        <Button className="default" loading={app_global.loading} onClick={() => setActiveTab('profile')} >Редактировать</Button>
                                                    :
                                                        null    
                                                    }
                                                    <Button className="exit" loading={app_global.loading || profile.loading} onClick={() => logout()} >Выход</Button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                :
                                <div className="on_auth">
                                    <h1>Вы не авторизованы</h1>
                                    <Link className='ant-btn ant-btn-primary default' to='/' exact={ true } onClick={() => scroll.scrollToTop()}><span>На главную</span></Link>
                                </div>
                            }
                        </>
                    }
                </div>
            </section>
        </>
    );
};